// This file is generated by the
// Microsoft.Mashup.Web.Tools.TypeScriptContractGenerator
// using the Product\UI\Clients\Shared\JsonContracts.config.json file.
// The purpose is to create matching TypeScript definitions for the objects
// that will be transfered between the client and the controllers using JSON.

import { type BuiltInIconId } from "ClientShared/Scripts/Icons/BuiltInIconId";
import { type ByExampleCell } from "ClientShared/Scripts/ByExample/ByExampleCell";
import { type ByExampleColumnName } from "ClientShared/Scripts/ByExample/ByExampleColumnName";
import { type HostCommandId } from "ClientShared/Scripts/Commands/HostCommands/HostCommandId";
import { type InvocationPattern } from "Document/Patterns/InvocationPattern";
import { type JsonEvaluationParams } from "Clients/Shared/JsonEvaluationParams";
import { type JsonPathEvaluationParams } from "Clients/Shared/JsonPathEvaluationParams";
import { type LinkKind } from "ClientShared/Scripts/Navigator/LinkKind";
import { type LocalizedString } from "Utils/LocalizedString";
import { type MashupDiscoveryOptions } from "Clients/Shared/MashupDiscoveryOptions";
import { type MashupScriptPattern } from "Document/Patterns/MashupScriptPattern";
import { type MashupTypeName } from "ClientShared/Scripts/Models/MashupTypeName";
import { type NavigationInfo } from "Document/NavigationInfo";
import { type Query } from "Document/Query";
import { type DefaultDestinationSettings, type DocumentMemberDependentGraph, type IncrementalRefreshSettings, type NameDisambiguatorType, type ParameterSetSettings, type SourceError, type StagingDefinition, type UpdateMethod, type DataDestinations } from "Document/Generated/JsonContracts.generated";
import { type Step } from "Document/Step";
import { StepPointer } from "Document/StepPointer";
import { type TypeName } from "Document/TypeName";
import { type Guid } from "Utils/Guid";
import { type ConnectionIdentity } from "ClientShared/Scripts/ConnectionIdentity";
import { type RecentItemNodeId } from "ClientShared/Scripts//Components/DataDiscovery/RecentItemsV2/RecentItemNode";

/* eslint-disable max-len */

/** @public */
export interface AdditionalDatahubSettings {
    /**
     * Gets or sets Datahub settings in JSON format.
     * These settings are directly passed to the DatahubCompactViewConfig in the datahub-client.
     * Type Definition: https://learn.microsoft.com/en-us/javascript/api/%40ms-fabric/workload-client/datahubcompactviewconfig
     * To generate the JSON string to use here: https://dailyplayground.powerbi.com/en-us/datahub-playground?refreshAccessToken=true
     * The default value is null.
     * For the time being the DatahubCompactViewConfigJson will override Deprecated settings until they are removed.
     */
    readonly datahubCompactViewConfigJson?: string;
    /**
     * Gets or sets whether to use the pq default pivot list.
     * The default value is true.
     */
    readonly usePqDefaultPivots?: boolean;
}

/** @internal */
export interface AddParameterQueryRequest {
    readonly queryName: string;
    readonly description?: string;
    readonly jsonPattern: string;
}

/** @internal */
export interface AddQueriesRequest {
    readonly requests: ReadonlyArray<AddQueryRequest>;
}

/** @internal */
export interface AddQueryExecutionInfo {
    readonly toolName: string;
    readonly editVersion: number;
    readonly queryState: Query;
    readonly queryAndStepDependentGraph: DocumentMemberDependentGraph;
    readonly queryMetadata: QueryMetadata;
}

/** @internal */
export interface AddQueryRequest {
    readonly queryName: string;
    readonly queryScript: string;
    readonly queryGroupId?: string;
    readonly loadEnabled?: boolean;
    readonly isHidden?: boolean;
    readonly queryNameGenerationSettings?: QueryNameGenerationSettings;
}

/** @internal */
export interface AgenticContextSeedRequest {
    readonly previousMessages: ReadonlyArray<PreviousAgenticSessionMessage>;
}

/** @internal */
export interface AgenticContextSeedResult {
    readonly seedApplied: boolean;
    readonly seededMessageCount: number;
}

/** @internal */
export interface AgenticFrontendTurnRequest {
    readonly userMessage: string;
    readonly workspaceObjectId: string;
    readonly artifactObjectId: string;
    readonly artifactType: string;
    readonly chatCompletionModel: string;
    readonly workspaceVariables?: ReadonlyArray<WorkspaceVariable>;
    readonly confirmedToolCallToken?: string;
    readonly frontendTools?: ReadonlyArray<FrontendToolDefinition>;
    readonly frontendToolResults?: ReadonlyArray<FrontendToolResult>;
    readonly autoApprovedDestructiveTools?: ReadonlyArray<string>;
    readonly allowModernEvaluationEngine: boolean;
    readonly agentId?: string;
}

/** @internal */
export interface AgenticSseAssistantMessageEvent {
    readonly type: AgenticSseEventType.AssistantMessage;
    readonly content: string;
}

/** @internal */
export interface AgenticSseChallengeEvent {
    readonly type: AgenticSseEventType.Challenge;
    readonly content: string;
    readonly args: { [key: string]: string };
}

/** @internal */
export interface AgenticSseConfirmationRequiredEvent {
    readonly type: AgenticSseEventType.ConfirmationRequired;
    /**
     * Base64Url-encoded JSON of the LLM assistant message. Pass this back
     * as ConfirmationToken in the next turn request to approve execution.
     */
    readonly token: string;
    /**
     * Structured confirmations for destructive tool calls, enabling i18n-friendly UI rendering.
     * When present and all items have known toolType, clients can use this for localized messages.
     */
    readonly toolConfirmations?: ReadonlyArray<ToolConfirmation>;
}

/** @internal */
export interface AgenticSseDoneEvent {
    readonly type: AgenticSseEventType.Done;
    readonly message: string;
}

/** @internal */
export interface AgenticSseErrorEvent {
    readonly type: AgenticSseEventType.Error;
    readonly message: string;
}

/** @internal */
export type AgenticSseEvent =
    | AgenticSseAssistantMessageEvent
    | AgenticSseChallengeEvent
    | AgenticSseConfirmationRequiredEvent
    | AgenticSseDoneEvent
    | AgenticSseErrorEvent
    | AgenticSseFrontendToolCallRequiredEvent
    | AgenticSseReasoningEvent
    | AgenticSseStatusEvent
    | AgenticSseSubAgentCompletedEvent
    | AgenticSseSubAgentMessageEvent
    | AgenticSseSubAgentProgressEvent
    | AgenticSseSubAgentStartedEvent
    | AgenticSseSubAgentToolStartEvent
    | AgenticSseToolResultEvent
    | AgenticSseToolStartEvent
    | AgenticSseTurnStartEvent;

/** @internal */
export const enum AgenticSseEventType {
    AssistantMessage = "assistant_message",
    Challenge = "challenge",
    ConfirmationRequired = "confirmation_required",
    Done = "done",
    Error = "error",
    FrontendToolCallRequired = "frontend_tool_call_required",
    Reasoning = "reasoning",
    Status = "status",
    SubAgentCompleted = "sub_agent_completed",
    SubAgentMessage = "sub_agent_message",
    SubAgentProgress = "sub_agent_progress",
    SubAgentStarted = "sub_agent_started",
    SubAgentToolStart = "sub_agent_tool_start",
    ToolResult = "tool_result",
    ToolStart = "tool_start",
    TurnStart = "turn_start",
}

/** @internal */
export interface AgenticSseFrontendToolCallRequiredEvent {
    readonly type: AgenticSseEventType.FrontendToolCallRequired;
    /**
     * Base64Url-encoded JSON of the LLM assistant message (same encoding as ConfirmationRequired).
     * Pass this back as ConfirmedToolCallToken in the resume turn.
     */
    readonly token: string;
    /**
     * Tool calls the frontend must execute. Includes id, name, and arguments
     * so the frontend knows what to do without decoding the token.
     */
    readonly toolCalls: ReadonlyArray<FrontendToolCallInfo>;
}

/** @internal */
export interface AgenticSseReasoningEvent {
    readonly type: AgenticSseEventType.Reasoning;
    readonly content: string;
}

/** @internal */
export interface AgenticSseStatusEvent {
    readonly type: AgenticSseEventType.Status;
    readonly message: string;
}

/** @internal */
export interface AgenticSseSubAgentCompletedEvent {
    readonly type: AgenticSseEventType.SubAgentCompleted;
    readonly agentId: string;
    readonly purpose: string;
    readonly success: boolean;
    readonly resultSummary?: string;
}

/** @internal */
export interface AgenticSseSubAgentMessageEvent {
    readonly type: AgenticSseEventType.SubAgentMessage;
    readonly agentId: string;
    readonly content: string;
}

/** @internal */
export interface AgenticSseSubAgentProgressEvent {
    readonly type: AgenticSseEventType.SubAgentProgress;
    readonly agentId: string;
    readonly status: string;
    readonly toolName?: string;
    readonly detail?: string;
    readonly iteration: number;
    readonly maxIterations: number;
}

/** @internal */
export interface AgenticSseSubAgentStartedEvent {
    readonly type: AgenticSseEventType.SubAgentStarted;
    readonly agentId: string;
    readonly purpose: string;
}

/** @internal */
export interface AgenticSseSubAgentToolStartEvent {
    readonly type: AgenticSseEventType.SubAgentToolStart;
    readonly agentId: string;
    readonly toolName: string;
    readonly args: { [key: string]: string };
}

/** @internal */
export interface AgenticSseToolResultContent {
    readonly summary: string;
    readonly payload?: ToolResultPayload;
}

/** @internal */
export interface AgenticSseToolResultEvent {
    readonly type: AgenticSseEventType.ToolResult;
    readonly toolCallId: string;
    readonly toolName: string;
    readonly success: boolean;
    readonly content: AgenticSseToolResultContent;
    readonly executionInfo?: ToolExecutionInfo;
}

/** @internal */
export interface AgenticSseToolStartEvent {
    readonly type: AgenticSseEventType.ToolStart;
    readonly toolCallId: string;
    readonly toolName: string;
    readonly args: { [key: string]: string };
}

/** @internal */
export interface AgenticSseTurnStartEvent {
    readonly type: AgenticSseEventType.TurnStart;
    readonly requestId: string;
}

/** @internal */
export const enum AmPmDistributionValue {
    AM = "AM",
    PM = "PM",
}

/** @internal */
export interface AnalyzeDataSourceReferenceFailureResult {
    readonly success: false;
}

/** @internal */
export interface AnalyzeDataSourceReferenceRequest {
    readonly jsonDataSourceReference: string;
    readonly sourceStepName: string;
    readonly navigationStepName: string;
    readonly jsonOptions?: string;
}

/** @internal */
export type AnalyzeDataSourceReferenceResult =
    | AnalyzeDataSourceReferenceFailureResult
    | AnalyzeDataSourceReferenceSuccessResult;

/** @internal */
export interface AnalyzeDataSourceReferenceSuccessResult {
    readonly success: true;
    readonly dataSource: MashupDataSource;
    readonly mScript: string;
}

/** @internal */
export const enum ArtifactConnectionInfoErrorCode {
    NotFound = "NotFound",
    Unauthorized = "Unauthorized",
    NetworkError = "NetworkError",
    InvalidResponse = "InvalidResponse",
    Unknown = "Unknown",
    NotConfigured = "NotConfigured",
}

/** @internal */
export type ArtifactConnectionInfoResult =
    | FailedArtifactConnectionInfoResult
    | SuccessfulArtifactConnectionInfoResult;

/** @internal */
export interface AssistedMappingAttributeMatch extends BaseAssistedMappingResult {
    readonly attributeName: string;
    readonly attributeLogicalName: string;
    readonly attributeDataType: string;
}

/** @internal */
export interface AssistedMappingBaseQuery {
    readonly top: number;
}

/** @internal */
export interface AssistedMappingByAttributeMatchQuery extends AssistedMappingBaseQuery {
    readonly entityLogicalName: string;
    readonly sourceAttributes: ReadonlyArray<AssistedMappingSourceAttribute>;
}

/** @internal */
export interface AssistedMappingByAttributeMatchRequest extends BaseAssistedMappingRequest<AssistedMappingByAttributeMatchQuery> {
}

/** @internal */
export interface AssistedMappingByAttributeMatchResponse extends BaseAssistedMappingOkResponse<AssistedMappingByAttributeMatchResult> {
}

/** @internal */
export interface AssistedMappingByAttributeMatchResult {
    readonly sourceAttributeName: string;
    readonly matches: ReadonlyArray<AssistedMappingAttributeMatch>;
}

/** @internal */
export interface AssistedMappingByAttributeQuery extends AssistedMappingBaseQuery {
    readonly entityLogicalNames?: ReadonlyArray<string>;
    readonly attributeName: string;
    readonly attributeDataType?: string;
}

/** @internal */
export interface AssistedMappingByAttributeRequest extends BaseAssistedMappingRequest<AssistedMappingByAttributeQuery> {
}

/** @internal */
export interface AssistedMappingByAttributeResponse extends BaseAssistedMappingOkResponse<AssistedMappingByAttributeResult> {
}

/** @internal */
export interface AssistedMappingByAttributeResult extends BaseAssistedMappingResult {
    readonly entityLogicalName: string;
    readonly attributeName: string;
    readonly attributeDataType: string;
}

/** @internal */
export interface AssistedMappingByEntityQuery extends AssistedMappingBaseQuery {
    readonly entity: AssistedMappingEntityQuery;
}

/** @internal */
export interface AssistedMappingByEntityRequest extends BaseAssistedMappingRequest<AssistedMappingByEntityQuery> {
}

/** @internal */
export interface AssistedMappingByEntityResponse extends BaseAssistedMappingOkResponse<AssistedMappingByEntityResult> {
}

/** @internal */
export interface AssistedMappingByEntityResult extends BaseAssistedMappingResult {
    readonly entityName: string;
    readonly entityLogicalName: string;
    readonly attributeMappings?: ReadonlyArray<AssistedMappingByAttributeMatchResult>;
    readonly isRecommendedByAPIEntity?: boolean;
}

/** @internal */
export interface AssistedMappingEntityQuery {
    readonly attributes?: ReadonlyArray<AssistedMappingQueryAttribute>;
}

/** @internal */
export interface AssistedMappingErrorResponse {
    readonly errorMessage: string;
}

/** @internal */
export interface AssistedMappingQueryAttribute {
    readonly attributeName: string;
    readonly dataType?: string;
}

/** @internal */
export const enum AssistedMappingQueryType {
    Entity = "Entity",
    Attribute = "Attribute",
    AttributeMatch = "AttributeMatch",
}

/** @internal */
export interface AssistedMappingSourceAttribute {
    readonly attributeName: string;
    readonly dataType?: string;
}

/** @internal */
export type AuthCredentialValue = (
    | SourceValue
)
& {
    readonly value?: string;
}

/** @internal */
export const enum AuthenticationKind {
    Anonymous = "Anonymous",
    Basic = "Basic",
    Key = "Key",
    KeyPair = "KeyPair",
    OAuth2 = "OAuth2",
    SAS = "SAS",
    ServicePrincipal = "ServicePrincipal",
    Windows = "Windows",
    WorkspaceIdentity = "WorkspaceIdentity",
}

/** @internal */
export const enum AuthKind {
    anonymous = 1,
    basic = 2,
    oAuth2 = 3,
    windows = 4,
    key = 5,
    sas = 6,
    servicePrincipal = 7,
    workspaceIdentity = 8,
    keyPair = 9,
}

/** @internal */
export type AuthKindCredential =
    | BasicCredential
    | KeyCredential
    | SasCredential
    | ServicePrincipalCredential
    | KeyPairCredential;

/** @internal */
export const enum AutoBindErrorCode {
    NoSuitableAutoBindScenarioFound = "NoSuitableAutoBindScenarioFound",
    NoUnambiguousAutoBindScenarioFound = "NoUnambiguousAutoBindScenarioFound",
    UnknownError = "UnknownError",
}

/** @internal */
export interface AutomaticColumnMappings {
    readonly kind: ColumnMappingsKind.Automatic;
}

/** @public */
export interface AutoNumberSettings {
    readonly autoNumberFormat: string;
    readonly displayName: string;
    readonly seedValue: number;
}

/** @internal */
export const enum AutoSelectableDataDiscoveryModuleId {
    Datahub = "Datahub",
    NewSource = "NewSource",
}

/** @internal */
export interface AutoSelectDataDiscoveryDatahubModule {
    readonly moduleId: AutoSelectableDataDiscoveryModuleId.Datahub;
}

/** @internal */
export type AutoSelectDataDiscoveryModule =
    | AutoSelectDataDiscoveryDatahubModule
    | AutoSelectDataDiscoveryNewSourceModule;

/** @internal */
export interface AutoSelectDataDiscoveryNewSourceModule {
    readonly moduleId: AutoSelectableDataDiscoveryModuleId.NewSource;
    readonly dataSourceOptionIdentifier?: DataSourceOptionIdentifier;
}

/** @internal */
export interface BaseAssistedMappingOkResponse<T> {
    readonly results?: ReadonlyArray<T>;
}

/** @internal */
export interface BaseAssistedMappingRequest<T> {
    readonly queryType: AssistedMappingQueryType;
    readonly query: T;
    readonly scenarioName: string;
}

/** @internal */
export interface BaseAssistedMappingResponse {
}

/** @internal */
export interface BaseAssistedMappingResult {
    readonly score: number;
}

/** @internal */
export interface BasicCredential {
    readonly authKind: AuthenticationKind.Basic;
    readonly password?: AuthCredentialValue;
}

/** @internal */
export interface BinarySourceBuilderSettings {
    readonly disableFilePathTextField: boolean;
    readonly enableOneDriveFileSelection: boolean;
    readonly enableThisDeviceFileUpload: boolean;
    readonly enableThisDeviceFileSelection: boolean;
}

/** @internal */
export interface BindQueriesToDefaultOutputDestinationFailureResult {
    readonly success: false;
}

/** @internal */
export interface BindQueriesToDefaultOutputDestinationRequest {
    readonly queryNames: ReadonlyArray<string>;
}

/** @internal */
export type BindQueriesToDefaultOutputDestinationResult =
    | BindQueriesToDefaultOutputDestinationFailureResult
    | BindQueriesToDefaultOutputDestinationSuccessResult;

/** @internal */
export interface BindQueriesToDefaultOutputDestinationSuccessResult {
    readonly success: true;
    readonly updatedQueries?: ReadonlyArray<Query>;
    readonly updatedQueriesMetadata?: ReadonlyArray<QueryMetadata>;
}

/** @internal */
export interface BindQueryToDefaultOutputDestinationFailureResult {
    readonly success: false;
}

/** @internal */
export interface BindQueryToDefaultOutputDestinationRequest {
    readonly queryName: string;
    readonly useStaging?: boolean;
}

/** @internal */
export type BindQueryToDefaultOutputDestinationResult =
    | BindQueryToDefaultOutputDestinationFailureResult
    | BindQueryToDefaultOutputDestinationSuccessResult;

/** @internal */
export interface BindQueryToDefaultOutputDestinationSuccessResult {
    readonly success: true;
    readonly updatedQuery?: Query;
    readonly updatedQueryMetadata?: QueryMetadata;
}

/** @public */
export enum BuiltInDataSourceOptionId {
    Access = "access",
    ActiveDirectory = "activeDirectory",
    AdobeAnalytics = "adobeAnalytics",
    AnalysisServices = "analysisServices",
    AzureAnalysisServices = "azureAnalysisServices",
    AzureBlobs = "azureBlobs",
    AzureDataLake = "azureDataLake",
    AzureHdInsightHdfs = "azureHdInsightHdfs",
    AzureSql = "azureSql",
    AzureSqlDw = "azureSqlDW",
    AzureTables = "azureTables",
    Blank = "blank",
    Db2 = "db2",
    Dynamics365 = "dynamics365",
    EnterData = "enterData",
    Excel = "excel",
    ExchangeOnline = "exchangeOnline",
    FolderContents = "folderContents",
    FolderFiles = "folderFiles",
    GoogleAnalytics = "googleAnalytics",
    HadoopFile = "hadoopFile",
    Json = "json",
    MySql = "mySql",
    Odata = "odata",
    Odbc = "odbc",
    Oracle = "oracle",
    Parquet = "parquet",
    Pdf = "pdf",
    PostgreSql = "postgreSql",
    SharepointContents = "sharepointContents",
    SharepointFolder = "sharepointFolder",
    SharepointList = "sharepointList",
    SharepointOnlineList = "sharepointOnlineList",
    SalesforceObjects = "salesforceObjects",
    SalesforceReports = "salesforceReports",
    SapBusinessWarehouseAppServer = "SapBusinessWarehouseAppServer",
    SapBusinessWarehouseMessageServer = "SapBusinessWarehouseMessageServer",
    SapHanaDatabase = "SapHanaDatabase",
    Sql = "sql",
    Sybase = "sybase",
    Teradata = "teradata",
    TextCsv = "textCsv",
    Web = "web",
    WebApi = "webApi",
    Xml = "xml",
}

/** @public */
export interface BuiltInDataSourceOptionIdentifier {
    readonly kind: DataSourceOptionKind.BuiltIn;
    readonly id: BuiltInDataSourceOptionId;
}

/** @internal */
export interface ByExampleSchema {
    readonly columns: ReadonlyArray<ByExampleTypedColumnName>;
}

/** @internal */
export interface ByExampleTable {
    readonly schema: ByExampleSchema;
    readonly values: ReadonlyArray<ReadonlyArray<ByExampleValue>>;
}

/** @internal */
export interface ByExampleTypedColumnName {
    readonly name: string;
    readonly type: ByExampleValueType;
    readonly inferredType: ByExampleValueType;
}

/** @internal */
export interface ByExampleValue {
    readonly type: ByExampleValueType;
    readonly jsonValue: string;
}

/** @internal */
export interface ByExampleValueType {
    readonly name: string;
    readonly nullable: boolean;
}

/** @internal */
export const enum CancellationReasonCodes {
    ClientRequested = "ClientRequested",
    TimedOut = "TimedOut",
}

/** @alpha */
export interface ChangeQueryConnectivityModes {
    readonly targetModeId: string;
    readonly buttonLabel: string;
    readonly kind: QueryConnectivityModeValidationErrorFixKind.ChangeModes;
}

/** @public */
export const enum CloseButtonActionOnClick {
    Cancel = "Cancel",
    Commit = "Commit",
}

/** @public */
export const enum CloseButtonActionOnConfirm {
    AlwaysCancel = "AlwaysCancel",
    AlwaysCommit = "AlwaysCommit",
    Ask = "Ask",
}

/** @public */
export type CloseButtonSettings = (
    | CloseButtonSettingsWithConfirmation
    | CloseButtonSettingsWithoutConfirmation
)
& {
    readonly showConfirmationPrompt: boolean;
}

/** @public */
export interface CloseButtonSettingsWithConfirmation {
    readonly showConfirmationPrompt: true;
    readonly actionOnConfirm: CloseButtonActionOnConfirm;
    readonly promptOnlyIfDocumentChanged?: boolean;
}

/** @public */
export interface CloseButtonSettingsWithoutConfirmation {
    readonly showConfirmationPrompt: false;
    readonly actionOnClick: CloseButtonActionOnClick;
}

/** @internal */
export interface ColumnByExampleFindTransformRequest {
    readonly examples: ReadonlyArray<string | undefined>;
    readonly inputTable: ByExampleTable;
    readonly outputColumnName: ByExampleColumnName;
    readonly uiLocale: string;
}

/** @internal */
export interface ColumnByExampleFindTransformResult {
    readonly needsMoreExamples: boolean;
    readonly outputCells: ReadonlyArray<ByExampleCell>;
    readonly outputColumnName: ByExampleColumnName;
    readonly transformMashupScript: string;
    readonly transformOutputType: TypeName;
    readonly suggestedStepName: string;
}

/** @internal */
export interface ColumnByExampleGetSuggestionsRequest {
    readonly inputTable: ByExampleTable;
    readonly outputColumnName: ByExampleColumnName;
    readonly rowIndex: number;
    readonly uiLocale: string;
}

/** @internal */
export interface ColumnByExampleGetSuggestionsResult {
    readonly suggestions: ReadonlyArray<ColumnByExampleSuggestion>;
}

/** @internal */
export interface ColumnByExampleSuggestion {
    readonly description: string;
    readonly displayString: string;
    readonly key: string;
    readonly transformKind: ColumnByExampleTransformKind;
    readonly transformMashupScript: string;
    readonly transformOutputType: TypeName;
    readonly outputColumnName: string;
    readonly suggestedStepName: string;
}

/** @internal */
export const enum ColumnByExampleTransformKind {
    Composition = "Composition",
    Conditional = "Conditional",
    CountDistinctValues = "CountDistinctValues",
    CountValues = "CountValues",
    Date_Age = "Date_Age",
    Date_CombineWithTime = "Date_CombineWithTime",
    Date_Day = "Date_Day",
    Date_DayOfWeek = "Date_DayOfWeek",
    Date_DayOfWeekName = "Date_DayOfWeekName",
    Date_DayOfYear = "Date_DayOfYear",
    Date_DaysInMonth = "Date_DaysInMonth",
    Date_Earliest = "Date_Earliest",
    Date_EndOfDay = "Date_EndOfDay",
    Date_EndOfMonth = "Date_EndOfMonth",
    Date_EndOfQuarter = "Date_EndOfQuarter",
    Date_EndOfWeek = "Date_EndOfWeek",
    Date_EndOfYear = "Date_EndOfYear",
    Date_From = "Date_From",
    Date_Latest = "Date_Latest",
    Date_Month = "Date_Month",
    Date_MonthName = "Date_MonthName",
    Date_QuarterOfYear = "Date_QuarterOfYear",
    Date_StartOfDay = "Date_StartOfDay",
    Date_StartOfMonth = "Date_StartOfMonth",
    Date_StartOfQuarter = "Date_StartOfQuarter",
    Date_StartOfWeek = "Date_StartOfWeek",
    Date_StartOfYear = "Date_StartOfYear",
    Date_SubtractDays = "Date_SubtractDays",
    Date_WeekOfMonth = "Date_WeekOfMonth",
    Date_WeekOfYear = "Date_WeekOfYear",
    Date_Year = "Date_Year",
    DateTime_Earliest = "DateTime_Earliest",
    DateTime_From = "DateTime_From",
    DateTime_Latest = "DateTime_Latest",
    DateTime_Subtract = "DateTime_Subtract",
    DateTime_SubtractDays = "DateTime_SubtractDays",
    DateTimeZone_Earliest = "DateTimeZone_Earliest",
    DateTimeZone_From = "DateTimeZone_From",
    DateTimeZone_Latest = "DateTimeZone_Latest",
    DateTimeZone_Subtract = "DateTimeZone_Subtract",
    DateTimeZone_SubtractDays = "DateTimeZone_SubtractDays",
    DateTimeZone_ToLocal = "DateTimeZone_ToLocal",
    Number_Abs = "Number_Abs",
    Number_Acos = "Number_Acos",
    Number_Asin = "Number_Asin",
    Number_Atan = "Number_Atan",
    Number_Average = "Number_Average",
    Number_Cos = "Number_Cos",
    Number_Cube = "Number_Cube",
    Number_Divide = "Number_Divide",
    Number_Exp = "Number_Exp",
    Number_Factorial = "Number_Factorial",
    Number_From = "Number_From",
    Number_IntegerDivide = "Number_IntegerDivide",
    Number_IsEven = "Number_IsEven",
    Number_IsOdd = "Number_IsOdd",
    Number_Ln = "Number_Ln",
    Number_Log10 = "Number_Log10",
    Number_Maximum = "Number_Maximum",
    Number_Median = "Number_Median",
    Number_Minimum = "Number_Minimum",
    Number_Modulo = "Number_Modulo",
    Number_MultiplyColumnAndConstant = "Number_MultiplyColumnAndConstant",
    Number_MultiplyColumns = "Number_MultiplyColumns",
    Number_PercentOf = "Number_PercentOf",
    Number_Power = "Number_Power",
    Number_Range = "Number_Range",
    Number_Round = "Number_Round",
    Number_RoundDown = "Number_RoundDown",
    Number_RoundUp = "Number_RoundUp",
    Number_Sign = "Number_Sign",
    Number_Sin = "Number_Sin",
    Number_Sqrt = "Number_Sqrt",
    Number_Square = "Number_Square",
    Number_StandardDeviation = "Number_StandardDeviation",
    Number_Subtract = "Number_Subtract",
    Number_SumColumnAndConstant = "Number_SumColumnAndConstant",
    Number_SumColumns = "Number_SumColumns",
    Number_Tan = "Number_Tan",
    Reference = "Reference",
    Text_AfterDelimiter = "Text_AfterDelimiter",
    Text_BeforeDelimiter = "Text_BeforeDelimiter",
    Text_BetweenDelimiters = "Text_BetweenDelimiters",
    Text_Combine = "Text_Combine",
    Text_End = "Text_End",
    Text_Length = "Text_Length",
    Text_Literal = "Text_Literal",
    Text_Middle = "Text_Middle",
    Text_Reference = "Text_Reference",
    Text_Remove = "Text_Remove",
    Text_Replace = "Text_Replace",
    Text_Select = "Text_Select",
    Text_Start = "Text_Start",
    Time_Earliest = "Time_Earliest",
    Time_EndOfHour = "Time_EndOfHour",
    Time_From = "Time_From",
    Time_Hour = "Time_Hour",
    Time_Latest = "Time_Latest",
    Time_Minute = "Time_Minute",
    Time_Second = "Time_Second",
    Time_StartOfHour = "Time_StartOfHour",
    Time_Subtract = "Time_Subtract",
}

/** @internal */
export interface ColumnMappingPair {
    readonly sourceColumnName: string;
    readonly destinationColumnName: string;
}

/** @internal */
export type ColumnMappings =
    | AutomaticColumnMappings
    | ExplicitColumnMappings
    | FixedColumnMappings;

/** @internal */
export enum ColumnMappingsKind {
    Automatic = "Automatic",
    Explicit = "Explicit",
    Fixed = "Fixed",
}

/** @internal */
export const enum ColumnProfileAttributeName {
    Count = "Count",
    ErrorCount = "ErrorCount",
    NullCount = "NullCount",
    DistinctCount = "DistinctCount",
    UniqueCount = "UniqueCount",
    Min = "Min",
    Max = "Max",
    ValueDistribution = "ValueDistribution",
    Average = "Average",
    EmptyStringCount = "EmptyStringCount",
    TextLengthDistribution = "TextLengthDistribution",
    NaNCount = "NaNCount",
    ZeroCount = "ZeroCount",
    EvenCount = "EvenCount",
    OddCount = "OddCount",
    StandardDeviation = "StandardDeviation",
    SignDistribution = "SignDistribution",
    ParityDistribution = "ParityDistribution",
    TrueCount = "TrueCount",
    FalseCount = "FalseCount",
    YearDistribution = "YearDistribution",
    MonthDistribution = "MonthDistribution",
    DayDistribution = "DayDistribution",
    WeekOfYearDistribution = "WeekOfYearDistribution",
    DayOfWeekDistribution = "DayOfWeekDistribution",
    AmPmDistribution = "AmPmDistribution",
    HourDistribution = "HourDistribution",
}

/** @public */
export enum CommonResourceKind {
    ActiveDirectory = "ActiveDirectory",
    AdobeAnalytics = "AdobeAnalytics",
    AmazonRedshift = "AmazonRedshift",
    AnalysisServices = "AnalysisServices",
    AzureAnalysisServices = "AzureAnalysisServices",
    AzureBlobs = "AzureBlobs",
    AzureDataExplorer = "AzureDataExplorer",
    AzureDataFactoryDataFlow = "AzureDataFactoryDataFlow",
    AzureDataLakeStorage = "AzureDataLakeStorage",
    AzureTables = "AzureTables",
    AIFunctions = "AIFunctions",
    AIInsights = "AIInsights",
    AzureMLFunctions = "AzureMLFunctions",
    AIInsightsInProc = "AIInsightsInProc",
    Cds = "Cds",
    CommonDataService = "CommonDataService",
    CurrentWorkbook = "CurrentWorkbook",
    DataLake = "DataLake",
    Dataverse = "Dataverse",
    DB2 = "DB2",
    ElasticSearch = "ElasticSearch",
    Exchange = "Exchange",
    FabricAI = "FabricAI",
    FabricSql = "FabricSql",
    Fhir = "Fhir",
    File = "File",
    Folder = "Folder",
    GoogleAnalytics = "GoogleAnalytics",
    GoogleBigQuery = "GoogleBigQuery",
    HDInsight = "HDInsight",
    Impala = "Impala",
    Kusto = "Kusto",
    Lakehouse = "Lakehouse",
    MySql = "MySql",
    OData = "OData",
    Odbc = "Odbc",
    Oracle = "Oracle",
    OracleERP = "OracleERP",
    PostgreSQL = "PostgreSQL",
    PowerBI = "PowerBI",
    PowerBIDatamarts = "PowerBIDatamarts",
    PowerPlatformDataflows = "PowerPlatformDataflows",
    PQGenericInvokeTestExtension = "PQGenericInvokeTestExtension",
    Salesforce = "Salesforce",
    SapBusinessWarehouse = "SapBusinessWarehouse",
    SAPERP = "SAPERP",
    SapHana = "SapHana",
    SharePoint = "SharePoint",
    Snowflake = "Snowflake",
    Spark = "Spark",
    SQL = "SQL",
    Sybase = "Sybase",
    Teradata = "Teradata",
    Vertica = "Vertica",
    Warehouse = "Warehouse",
    Web = "Web",
}

/** @public */
export interface ComputeEngineSettings {
    readonly allowFastCopy: boolean;
    readonly allowModernEvaluationEngine: boolean;
    readonly allowModernRefreshService: boolean;
    readonly allowPartitionedCompute: boolean;
    readonly parametric: boolean;
    readonly maxConcurrency: number | null | undefined;
    readonly enableVorder: boolean;
    readonly enableOptimizedCopyToLakehouse: boolean;
}

/** @internal */
export interface Connection {
    /**
     * Gets or sets a value indicating whether the connection can be used through a gateway mashup.
     * This is only supported for the DMTS connection provider. All other providers default to true.
     */
    readonly allowUsageThroughGateway: boolean;
    readonly authKind?: AuthKind | null | undefined;
    readonly credentialSource?: AuthKindCredential;
    readonly encryptConnection: EncryptConnection;
    readonly id: string | null | undefined;
    readonly key: string | null | undefined;
    readonly sasToken: string | null | undefined;
    readonly servicePrincipalClientId: string | null | undefined;
    readonly servicePrincipalSecret: string | null | undefined;
    readonly servicePrincipalTenantId: string | null | undefined;
    readonly kind: string | null | undefined;
    readonly displayName: string | null | undefined;
    readonly emailAddress?: string;
    readonly ewsSupportedSchema?: string;
    readonly ewsUrl?: string;
    readonly identitySource?: string;
    /**
     * Gets or sets a value indicating whether the connection can be used through another connection to fetch secrets for credentials.
     * This is only supported for the DMTS connection provider. All other providers default to null.
     */
    readonly isReferenceConnection?: boolean;
    readonly oAuthCredentials?: ConnectionOAuthCredentials;
    readonly passphrase: string | null | undefined;
    readonly password: string | null | undefined;
    readonly privateKey: string | null | undefined;
    readonly path: string | null | undefined;
    readonly normalizedPath: string | null | undefined;
    readonly username: string | null | undefined;
    readonly gatewayObjectId: string | null | undefined;
    readonly gatewayKind: string | null | undefined;
    readonly gatewayPublicKey: string | null | undefined;
    readonly gatewayEncryptedCredential: string | null | undefined;
    readonly privacyGroup: string | null | undefined;
    readonly isConsentedConnection?: boolean;
    readonly connectionProviderProperties: { [key: string]: string } | null | undefined;
}

/** @internal */
export interface ConnectionDataSource {
    readonly kind: string;
    readonly path: string;
    readonly gatewayObjectId: string | null | undefined;
    readonly connectionId: string | null | undefined;
    readonly name: string | null | undefined;
}

/** @internal */
export interface ConnectionDataSourcePair {
    readonly connection: Connection;
    readonly dataSource: MashupDataSource;
}

/** @internal */
export interface ConnectionOAuthCredentials {
    readonly callbackUri: string;
    readonly serializedProviderOAuthContext: string;
    readonly useConfidentialClient?: boolean;
}

/** @internal */
export const enum ConnectionsInScopeErrorCode {
    Aborted = "Aborted",
    UnknownError = "UnknownError",
}

/** @internal */
export interface ConnectionsInScopeResponse {
    readonly connectionsInScope: ReadonlyArray<Connection>;
    readonly normalizedPath?: string;
    readonly resourceKind: string;
}

/** @internal */
export enum ConnectionUpsertErrorCode {
    AccessPremiumConnectionNotAllowed = "AccessPremiumConnectionNotAllowed",
    AdfConnectionEvaluationError = "AdfConnectionEvaluationError",
    AllowDatasourceThroughGatewayAndCredentialTypeMismatchError = "AllowDatasourceThroughGatewayAndCredentialTypeMismatchError",
    BlockedByAuthenticationKindPolicies = "BlockedByAuthenticationKindPolicies",
    BlockedByDataLossPreventionConfiguration = "BlockedByDataLossPreventionConfiguration",
    BlockedByInterTenantDataLossPreventionConfiguration = "BlockedByInterTenantDataLossPreventionConfiguration",
    BlockedByWorkspaceGatewayPolicies = "BlockedByWorkspaceGatewayPolicies",
    BlockedByWorkspaceOutboundAccessProtectionPolicies = "BlockedByWorkspaceOutboundAccessProtectionPolicies",
    CannotCreatePersonalOnPremConnection = "CannotCreatePersonalOnPremConnection",
    CannotUpdateCredentialType = "CannotUpdateCredentialType",
    CdsaFeatureNotAvailableError = "CdsaFeatureNotAvailableError",
    CloudConnectionsBlocked = "CloudConnectionsBlocked",
    ConnectionApiPolicyViolation = "ConnectionApiPolicyViolation",
    ConnectionCouldNotBeCreated = "ConnectionCouldNotBeCreated",
    ConnectionCouldNotBeUpdated = "ConnectionCouldNotBeUpdated",
    ConnectionKindNotSupported = "ConnectionKindNotSupported",
    ConnectionNotFound = "ConnectionNotFound",
    ConnectionShareNotAllowedInEnvironment = "ConnectionShareNotAllowedInEnvironment",
    ConsentedConnectionsFeatureNotEnabled = "ConsentedConnectionsFeatureNotEnabled",
    CredentialsNotFoundInAzureKeyVaultError = "CredentialsNotFoundInAzureKeyVaultError",
    DataAccessError = "DataAccessError",
    DataflowDatasourceIsReadOnly = "DataflowDatasourceIsReadOnly",
    DataflowInternalError = "DataflowInternalError",
    DataSourceUnreachableOrGatewayRequired = "DataSourceUnreachableOrGatewayRequired",
    DuplicateDataSourceNameError = "DuplicateDataSourceNameError",
    EncryptionNotSupportedForKindAndGateway = "EncryptionNotSupportedForKindAndGateway",
    ExceededLimit = "ExceededLimit",
    GatewayCryptographicException = "GatewayCryptographicException",
    GatewayDetailsNotFound = "GatewayDetailsNotFound",
    GatewayExceededLimit = "GatewayExceededLimit",
    GatewayNotAuthorized = "GatewayNotAuthorized",
    GatewayOAuthPublicClientAppBlockedException = "GatewayOAuthPublicClientAppBlockedException",
    GatewayTimeout = "GatewayTimeout",
    GatewayUnreachable = "GatewayUnreachable",
    GatewayUnsupportedByLicense = "GatewayUnsupportedByLicense",
    GatewayUpgradeRequired = "GatewayUpgradeRequired",
    InvalidConnectionId = "InvalidConnectionId",
    InvalidCredentials = "InvalidCredentials",
    InvalidDataPoolId = "InvalidDataPoolId",
    InvalidPath = "InvalidPath",
    InvalidServicePrincipalId = "InvalidServicePrincipalId",
    InvalidServicePrincipalSecret = "InvalidServicePrincipalSecret",
    InvalidServicePrincipalTenantId = "InvalidServicePrincipalTenantId",
    ModelGatewayOutOfDate = "ModelGatewayOutOfDate",
    OAuthCustomConnectorBlocked = "OAuthCustomConnectorBlocked",
    OAuthLoginFailed = "OAuthLoginFailed",
    OAuthNotSupported = "OAuthNotSupported",
    OAuthNotSupportedForDSRKind = "OAuthNotSupportedForDSRKind",
    OAuthResourceTimeout = "OAuthResourceTimeout",
    OAuthTokenLoginError = "OAuthTokenLoginError",
    OAuthTokenRefreshFailed = "OAuthTokenRefreshFailed",
    OboSignInError = "OboSignInError",
    OboTokenAcquisitionFailed = "OboTokenAcquisitionFailed",
    PowerAppsConnectorApilookupFailed = "PowerAppsConnectorApilookupFailed",
    PowerBiExternalServiceError = "PowerBiExternalServiceError",
    ProviderVersionMismatch = "ProviderVersionMismatch",
    ServicePrincipalTokenGenerationFailed = "ServicePrincipalTokenGenerationFailed",
    SharepointSpnTokenAcquisitionFailed = "SharepointSpnTokenAcquisitionFailed",
    TooManyConnections = "TooManyConnections",
    TooManyRequests = "TooManyRequests",
    UnableToApplyOutboundAccessProtectionPolicies = "UnableToApplyOutboundAccessProtectionPolicies",
    UnknownDmtsError = "UnknownDmtsError",
    UnknownError = "UnknownError",
    UnknownGatewayError = "UnknownGatewayError",
    Unsupported = "Unsupported",
    UnsupportedConnectionString = "UnsupportedConnectionString",
    UnsupportedConsentedConnectionCredentialType = "UnsupportedConsentedConnectionCredentialType",
    UserIsNotAllowedToAccessAzureKeyVaultConnectionError = "UserIsNotAllowedToAccessAzureKeyVaultConnectionError",
    AccessToKeyVaultForbiddenError = "AccessToKeyVaultForbiddenError",
    KeyVaultInternalError = "KeyVaultInternalError",
    KeyVaultNotFoundError = "KeyVaultNotFoundError",
    UnauthorizedAccessToKeyVaultError = "UnauthorizedAccessToKeyVaultError",
    WorkspaceIdentityUnsupportedForEndpoint = "WorkspaceIdentityUnsupportedForEndpoint",
}

/** @internal */
export interface ConnectorMetadataConfig {
    readonly builtInResourceKindsSupportingAad: ReadonlyArray<string>;
    readonly builtInResourceKindsSupportingEncryption: ReadonlyArray<string>;
    readonly dataSourceExtensions: ReadonlyArray<DataSourceExtension>;
}

/** @internal */
export interface ConnectorMetadataRequest {
    readonly allowedModules?: ReadonlyArray<string>;
    readonly hostContext: HostContext;
    readonly locale: string;
    readonly gatewayObjectId?: string;
}

/** @internal */
export enum ConsentedConnectionState {
    NotEnabled = "NotEnabled",
    Enabled = "Enabled",
}

/** @internal */
export type CopilotAction =
    | CopilotAddStepAction
    | CopilotCreateQueryAction
    | CopilotExplainQueryAction
    | CopilotExplainStepAction
    | CopilotGetDataAction;

/** @internal */
export const enum CopilotActionKind {
    AddStep = "AddStep",
    CreateQuery = "CreateQuery",
    ExplainQuery = "ExplainQuery",
    ExplainStep = "ExplainStep",
    GetData = "GetData",
}

/** @internal */
export interface CopilotActionResponse {
    readonly kind: CopilotResponseKind.Action;
    readonly action: CopilotAction;
}

/** @internal */
export interface CopilotAddStepAction {
    readonly kind: CopilotActionKind.AddStep;
    readonly queryName: string;
    readonly currentStepName: string | null | undefined;
    readonly stepName: string;
    readonly stepPattern: MashupScriptPattern;
    readonly stepScript: string;
    readonly unrolledSteps: ReadonlyArray<Step> | null | undefined;
}

/** @internal */
export interface CopilotCapacityExceededError {
    readonly kind: CopilotErrorKind.CapacityExceeded;
}

/** @internal */
export interface CopilotCouldNotExplainQueryError {
    readonly kind: CopilotErrorKind.CouldNotExplainQuery;
    readonly message: string;
    readonly queryName: string | null | undefined;
}

/** @internal */
export interface CopilotCouldNotGenerateQueryError {
    readonly kind: CopilotErrorKind.CouldNotGenerateQuery;
    readonly message: string;
}

/** @internal */
export interface CopilotCouldNotGenerateStepError {
    readonly kind: CopilotErrorKind.CouldNotGenerateStep;
    readonly message: string;
}

/** @internal */
export interface CopilotCouldNotGetDataError {
    readonly kind: CopilotErrorKind.CouldNotGetData;
    readonly message: string;
}

/** @internal */
export interface CopilotCreateQueryAction {
    readonly kind: CopilotActionKind.CreateQuery;
    readonly queryName: string;
    readonly queryScript: string;
}

/** @internal */
export interface CopilotCrossGeoError {
    readonly kind: CopilotErrorKind.CrossGeoDisallowed;
}

/** @public */
export interface CopilotDisabledStatus {
    readonly enabled: false;
    readonly reasonCode: CopilotModelStatusReasonCode;
}

/** @public */
export interface CopilotEnabledStatus {
    readonly enabled: true;
}

/** @internal */
export interface CopilotEndpointExceptionError {
    readonly kind: CopilotErrorKind.EndpointException;
    readonly message: string;
}

/** @internal */
export interface CopilotEndpointThrottlingError {
    readonly kind: CopilotErrorKind.EndpointThrottling;
}

/** @internal */
export type CopilotError =
    | CopilotCapacityExceededError
    | CopilotCouldNotExplainQueryError
    | CopilotCouldNotGenerateQueryError
    | CopilotCouldNotGenerateStepError
    | CopilotCouldNotGetDataError
    | CopilotCrossGeoError
    | CopilotEndpointExceptionError
    | CopilotEndpointThrottlingError
    | CopilotInputTooLongError
    | CopilotInsufficientLicenseError
    | CopilotLanguageNotSupportedError
    | CopilotMalformedModelResponseError
    | CopilotOperationTimeoutError
    | CopilotOutOfScopeError
    | CopilotQueriesEmptyOrNullError
    | CopilotQueryNotFoundError
    | CopilotResponseOverflowError
    | CopilotSkillFailedError
    | CopilotUnknownError;

/** @internal */
export const enum CopilotErrorKind {
    CapacityExceeded = "CapacityExceeded",
    CrossGeoDisallowed = "CrossGeoDisallowed",
    CouldNotExplainQuery = "CouldNotExplainQuery",
    CouldNotGenerateQuery = "CouldNotGenerateQuery",
    CouldNotGenerateStep = "CouldNotGenerateStep",
    CouldNotGetData = "CouldNotGetData",
    EndpointException = "EndpointException",
    EndpointThrottling = "EndpointThrottling",
    InputTooLong = "InputTooLong",
    InsufficientLicense = "InsufficientLicense",
    LanguageNotSupported = "LanguageNotSupported",
    MalformedModelResponse = "MalformedModelResponse",
    OperationTimeout = "OperationTimeout",
    OutOfScope = "OutOfScope",
    QueriesEmptyOrNull = "QueriesEmptyOrNull",
    QueryNotFound = "QueryNotFound",
    ResponseOverflow = "ResponseOverflow",
    SkillFailed = "SkillFailed",
    Unknown = "Unknown",
}

/** @internal */
export interface CopilotErrorResponse {
    readonly kind: CopilotResponseKind.Error;
    readonly copilotError: CopilotError;
}

/** @internal */
export interface CopilotExplainQueryAction {
    readonly kind: CopilotActionKind.ExplainQuery;
    readonly queryName: string;
    readonly explanation: CopilotQueryExplanation;
}

/** @internal */
export interface CopilotExplainStepAction {
    readonly kind: CopilotActionKind.ExplainStep;
    readonly queryName: string;
    readonly stepName: string;
    readonly explanation: string;
}

/** @internal */
export interface CopilotGetDataAction {
    readonly kind: CopilotActionKind.GetData;
    readonly connectionName: string;
    readonly connectionType: string | null | undefined;
    readonly connectionTables: ReadonlyArray<string> | null | undefined;
}

/** @internal */
export interface CopilotInputTooLongError {
    readonly kind: CopilotErrorKind.InputTooLong;
}

/** @internal */
export interface CopilotInsufficientLicenseError {
    readonly kind: CopilotErrorKind.InsufficientLicense;
}

/** @internal */
export interface CopilotLanguageNotSupportedError {
    readonly kind: CopilotErrorKind.LanguageNotSupported;
    readonly language: string;
}

/** @internal */
export interface CopilotMalformedModelResponseError {
    readonly kind: CopilotErrorKind.MalformedModelResponse;
    readonly message?: string | null | undefined;
}

/** @public */
export const enum CopilotModelStatusReasonCode {
    CapacityLimitExceeded = "CapacityLimitExceeded",
    CapacityNotActive = "CapacityNotActive",
    DisabledForCrossGeo = "DisabledForCrossGeo",
    DisabledForTenant = "DisabledForTenant",
    InsufficientResources = "InsufficientResources",
    InvalidCapacity = "InvalidCapacity",
    ModelNotFound = "ModelNotFound",
    Unknown = "Unknown",
}

/** @internal */
export interface CopilotOperationTimeoutError {
    readonly kind: CopilotErrorKind.OperationTimeout;
}

/** @internal */
export interface CopilotOutOfScopeError {
    readonly kind: CopilotErrorKind.OutOfScope;
    readonly reason: string;
}

/** @internal */
export interface CopilotQueriesEmptyOrNullError {
    readonly kind: CopilotErrorKind.QueriesEmptyOrNull;
}

/** @internal */
export interface CopilotQueryExplanation {
    readonly summary: string;
    readonly stepExplanations: ReadonlyArray<CopilotStepExplanation>;
}

/** @internal */
export interface CopilotQueryNotFoundError {
    readonly kind: CopilotErrorKind.QueryNotFound;
    readonly queryName: string;
}

/** @internal */
export type CopilotResponse =
    | CopilotActionResponse
    | CopilotErrorResponse;

/** @internal */
export const enum CopilotResponseKind {
    Action = "Action",
    Error = "Error",
}

/** @internal */
export interface CopilotResponseOverflowError {
    readonly kind: CopilotErrorKind.ResponseOverflow;
    readonly message?: string | null | undefined;
}

/** @internal */
export interface CopilotSerializedQuery {
    readonly id: string;
    readonly name: string;
    readonly steps: ReadonlyArray<CopilotSerializedStep>;
    readonly table?: SuggestionTableResult;
}

/** @internal */
export interface CopilotSerializedStep {
    readonly name: string;
    readonly script: string;
    readonly table?: SuggestionTableResult;
}

/** @internal */
export interface CopilotSkillFailedError {
    readonly kind: CopilotErrorKind.SkillFailed;
    readonly skillName: string | null | undefined;
}

/** @public */
export type CopilotStatus =
    | CopilotDisabledStatus
    | CopilotEnabledStatus;

/** @internal */
export interface CopilotStepExplanation {
    readonly stepName: string;
    readonly explanation: string;
}

/** @internal */
export interface CopilotUnknownError {
    readonly kind: CopilotErrorKind.Unknown;
    readonly error: string;
}

/** @internal */
export enum CopyIntent {
    UserSelectedQuery = "UserSelectedQuery",
    ReferenceQuery = "ReferenceQuery",
    DestinationQuery = "DestinationQuery",
    DestinationReferenceQuery = "DestinationReferenceQuery",
}

/** @internal */
export interface CopyQueriesResult {
    readonly queryDetails: ReadonlyArray<QueryDetails>;
    readonly extendedQueryDetails?: ReadonlyArray<ExtendedQueryDetails>;
}

/** @internal */
export interface CreateBaseQueryFailureResult {
    readonly success: false;
    readonly errorReason: string;
}

/** @internal */
export interface CreateBaseQueryRequest {
    readonly hostContext: HostContext;
    readonly documentLocale: string;
    readonly dataSourceScript: string;
    readonly sampleTableColumnNames: ReadonlyArray<string>;
    readonly sampleTableCells: ReadonlyArray<ReadonlyArray<string>>;
    readonly sampleTableColumnTypes?: ReadonlyArray<string>;
    readonly dataSourceStepName?: string;
    readonly changeTypeStepName?: string;
    readonly userTier?: string;
}

/** @internal */
export type CreateBaseQueryResult =
    | CreateBaseQuerySuccessResult
    | CreateBaseQueryFailureResult;

/** @internal */
export interface CreateBaseQuerySuccessResult {
    readonly success: true;
    readonly mashupDocument: string;
    readonly queryName: string;
    readonly queryScript: string;
}

/** @internal */
export interface CreateDataflowRequest {
    readonly dataflowDisplayName: string;
    readonly workspaceObjectId: Guid;
    readonly mashupId: Guid;
}

/** @internal */
export interface CreateDataflowResult {
    readonly success: boolean;
    readonly dataflowObjectId?: Guid;
    readonly errorMessage?: string;
    readonly errorDetails?: ReadonlyArray<CreateErrorResponseDetail>;
}

/** @internal */
export interface CreateErrorResponseDetail {
    readonly errorCode: string;
    readonly errorMessage?: string;
}

/** @internal */
export interface CreateStepFromNaturalLanguageFailureResult {
    readonly success: false;
    readonly errorReason: string;
}

/** @internal */
export interface CreateStepFromNaturalLanguageRequest {
    readonly hostContext: HostContext;
    readonly documentLocale: string;
    readonly queryScript: string;
    readonly naturalLanguageInput: string;
    readonly sampleTableColumnNames: ReadonlyArray<string>;
    readonly sampleTableCells: ReadonlyArray<ReadonlyArray<string>>;
    readonly llmProviderKind: string;
    readonly explainStep: boolean;
    readonly userTier?: string;
}

/** @internal */
export type CreateStepFromNaturalLanguageResult =
    | CreateStepFromNaturalLanguageSuccessResult
    | CreateStepFromNaturalLanguageFailureResult;

/** @internal */
export interface CreateStepFromNaturalLanguageSuccessResult {
    readonly success: true;
    readonly transformationStepScript: string;
    readonly transformationStepExplanation?: string;
}

/** @internal */
export interface CreateTypeChangeStepFailureResult {
    readonly success: false;
    readonly errorReason: string;
}

/** @internal */
export interface CreateTypeChangeStepRequest {
    readonly hostContext: HostContext;
    readonly documentLocale: string;
    readonly dataSourceStepName: string;
    readonly sampleTableColumnNames: ReadonlyArray<string>;
    readonly sampleTableCells: ReadonlyArray<ReadonlyArray<string>>;
    readonly sampleTableColumnTypes?: ReadonlyArray<string>;
    readonly userTier?: string;
}

/** @internal */
export type CreateTypeChangeStepResult =
    | CreateTypeChangeStepSuccessResult
    | CreateTypeChangeStepFailureResult;

/** @internal */
export interface CreateTypeChangeStepSuccessResult {
    readonly success: true;
    readonly typeChangeStepScript: string;
}

/** @internal */
export interface CubeParameterValue {
    readonly parameterId: string;
    readonly functionParameterName: string;
    readonly jsonInvocationParameterValue?: string | null | undefined;
}

/** @internal */
export interface CustomTransform {
    readonly id: SuggestedTransformId.Custom;
    readonly step: Step;
    readonly suggestionScore: number;
}

/** @public */
export const enum DataAccessMode {
    Read = "Read",
    Write = "Write",
}

/** @internal */
export interface DatabaseTableAppendSettings {
    readonly primaryKeyColumnNames?: ReadonlyArray<string>;
    readonly updateMethod: TableUpdateMethodKind.AppendRows;
}

/** @internal */
export interface DatabaseTableReplaceSettings {
    readonly primaryKeyColumnNames?: ReadonlyArray<string>;
    readonly updateMethod: TableUpdateMethodKind.ReplaceRows;
}

/** @internal */
export type DatabaseTableUpdateSettings =
    | DatabaseTableAppendSettings
    | DatabaseTableReplaceSettings;

/** @public */
export type DataDiscoveryCommitResult =
    | DataDiscoveryNewSourceCommitResult
    | DataDiscoveryDatahubCommitResult
    | DataDiscoveryRecentItemCommitResult;

/** @internal */
export const enum DataDiscoveryCommitResultKind {
    NewSource = "NewSource",
    DatahubArtifact = "DatahubArtifact",
    RecentItem = "RecentItem",
}

/** @internal */
export interface DataDiscoveryDatahubCommitResult {
    /**
     * Gets or sets the JSON string representing the datahub artifact.
     * This property is used to facilitate type conversion without type redefinitions,
     * as the corresponding type exists in TypeScript but not in C#.
     * Type definition: <see href="https://learn.microsoft.com/en-us/javascript/api/@ms-fabric/workload-client/basedatahubartifactui?view=fabric-extensibility-js-latest"/>
     */
    readonly datahubArtifactJsonString: string;
    /**
     * Gets or sets the ID of the connectivity mode selected for the datahub artifact.
     *
     * NOTE: This is only available when datahub connectivity modes are configured,
     *  via <see cref = "DatahubConnectivityModeSettings"/>.
     */
    readonly connectivityModeId?: string;
    /**
     * Gets or sets the connection information for the committed artifact.
     *
     * NOTE: Currently only populated for Power BI semantic model artifacts; null for all other artifact kinds.
     */
    readonly artifactConnectionInfo?: ArtifactConnectionInfoResult;
    readonly kind: DataDiscoveryCommitResultKind.DatahubArtifact;
}

/** @internal */
export interface DataDiscoveryDatahubModuleLoadedResult {
    readonly moduleId: DataDiscoveryModuleLoadedResultKind.Datahub;
}

/** @public */
export type DataDiscoveryModuleLoadedResult = (
    | DataDiscoveryNewSourceModuleLoadedResult
    | DataDiscoveryDatahubModuleLoadedResult
    | DataDiscoveryRecentModuleLoadedResult
)
& {
    /**
     * Gets or sets the timestamp in milliseconds when the module has loaded, used for performance measurements.
     *
     * @remarks
     * - Represents the exact time the module finished loading, not when the event/callback was fired.
     */
    readonly perfTimestamp: number;
    /**
     * Gets or sets the timestamp in milliseconds when user interaction started, used for performance measurements.
     */
    readonly userInteractionStartPerfTimestamp: number;
    /**
     * Gets or sets the source of the user interaction that triggered the module load.
     */
    readonly moduleLoadedTriggerInteraction: DataDiscoveryModuleLoadTriggerInteraction;
}

/** @internal */
export const enum DataDiscoveryModuleLoadedResultKind {
    NewSource = "NewSource",
    Datahub = "Datahub",
    Recent = "Recent",
}

/** @public */
export const enum DataDiscoveryModuleLoadTriggerInteraction {
    InitialHomeLoad = "InitialHomeLoad",
    ModuleTabSwitch = "ModuleTabSwitch",
    HomeTabSwitch = "HomeTabSwitch",
    InitialModuleLoad = "InitialModuleLoad",
}

/** @public */
export interface DataDiscoveryModuleSettings<TModuleSettings> {
    /**
     * Whether the module is allowed to be shown in Data Discovery.
     * defaultValue true.
     */
    readonly allowed: boolean;
    /**
     * Whether the module is allowed to be shown in Data Discovery Home tab.
     * defaultValue true.
     */
    readonly showInHomeTabAllowed?: boolean;
    /**
     * Settings specific to the module.
     * These settings are work in progress and will be added as needed.
     * defaultValue null.
     */
    readonly settings?: TModuleSettings;
}

/** @internal */
export interface DataDiscoveryNewSourceCommitResult {
    readonly dataSourceOptionIdentifier: DataSourceOptionIdentifier;
    readonly kind: DataDiscoveryCommitResultKind.NewSource;
}

/** @internal */
export interface DataDiscoveryNewSourceModuleLoadedResult {
    readonly moduleId: DataDiscoveryModuleLoadedResultKind.NewSource;
}

/** @internal */
export interface DataDiscoveryRecentItemCommitResult {
    readonly recentItemNode: RecentItemNode;
    readonly kind: DataDiscoveryCommitResultKind.RecentItem;
}

/** @internal */
export interface DataDiscoveryRecentModuleLoadedResult {
    readonly moduleId: DataDiscoveryModuleLoadedResultKind.Recent;
}

/** @internal */
export interface DataDiscoveryStageCommitHandlerResult extends PreventableDefaultBehaviourHandlerResult {
}

/** @public */
export interface DataDiscoveryTabListSettings {
    /**
     * Gets or sets whether a tab list is shown in the Data Discovery Home tab.
     * defaultValue true.
     */
    readonly showTabList?: boolean;
    /**
     * Gets or sets whether the tab list is horizontal or vertical.
     * defaultValue false.
     */
    readonly isHorizontal?: boolean;
}

/** @beta */
export interface DataflowEditingSession {
    readonly computeEngineSettings?: ComputeEngineSettings;
    readonly editingSessionMashup: EditingSessionMashup;
}

/** @public */
export interface DataflowInformation {
    readonly dataflowId: string;
    readonly name: string;
    readonly dataflowKind?: string;
}

/** @public */
export enum DatahubArtifactType {
    CopyJob = "CopyJob",
    Dataflow = "Dataflow",
    Datamart = "Datamart",
    DataWarehouse = "DataWarehouse",
    LakeWarehouse = "LakeWarehouse",
    MountedWarehouse = "MountedWarehouse",
    KustoDatabase = "KustoDatabase",
    Lakehouse = "Lakehouse",
    Warehouse = "Warehouse",
    SQLDbNative = "SQLDbNative",
    MountedRelationalDatabase = "MountedRelationalDatabase",
    Dataset = "Dataset",
}

/** @public */
export interface DatahubConnectivityMode {
    readonly id: string;
    readonly label: string;
}

/** @public */
export interface DatahubConnectivityModeArtifactSupport {
    /**
     * Gets or sets the artifact type, for which connectivity modes are configured.
     *
     * This is the `DatahubArtifactUI.tridentArtifactType`
     * Source: https://powerbi.visualstudio.com/DefaultCollection/PowerBIClients/_git/PowerBIClients?path=/trident/libs/extension/client-common/src/lib/contracts/datahub-common/compact-view-data.config.ts&version=GBmaster&line=88&lineEnd=89&lineStartColumn=1&lineEndColumn=1&lineStyle=plain&_a=contents .
     */
    readonly artifactType: string;
    /**
     * Gets or sets supported connectivity mode IDs for the artifact.
     *
     *  WARN: Ids must be defined in <see cref = "DatahubConnectivityModeSettings.AllowedModes"/>.
     */
    readonly supportedModeIds: ReadonlyArray<string>;
    /**
     * Gets or sets default connectivity mode ID for the artifact.
     *
     *  WARN: Id must be defined in <see cref = "DatahubConnectivityModeSettings.AllowedModes"/>.
     */
    readonly defaultModeId?: string;
}

/** @public */
export interface DatahubConnectivityModeSettings {
    readonly allowedModes: ReadonlyArray<DatahubConnectivityMode>;
    readonly artifactSupportList: ReadonlyArray<DatahubConnectivityModeArtifactSupport>;
}

/** @internal */
export interface DatahubEmbeddedUIConfiguration {
    readonly embedUrl: string;
    readonly authResourceUri: string;
    readonly allowTelemetryLogging: boolean;
    readonly locale: string;
    readonly formattingLocale: string;
    readonly featureFlagsOverride?: { [key: string]: boolean };
}

/** @internal */
export const enum DatahubInstanceId {
    GetDataWizardHomeTab = "GetDataWizardHomeTab",
    GetDataWizardDatahubTab = "GetDataWizardDatahubTab",
    DefaultOutputDestinationDatahubTab = "DefaultOutputDestinationDatahubTab",
    DefaultOutputDestinationHomeTab = "DefaultOutputDestinationHomeTab",
    QueryOutputDestinationDatahubTab = "QueryOutputDestinationDatahubTab",
    QueryOutputDestinationHomeTab = "QueryOutputDestinationHomeTab",
    CopilotImportDataModule = "CopilotImportDataModule",
}

/** @public */
export interface DatahubSettings {
    /**
     * Gets or sets a value indicating whether Datahub will show the workspace navigation.
     * defaultValue true.
     */
    readonly workspaceNavigationEnabled?: boolean;
    /**
     * Gets or sets the supported artifact types to show in all Datahub instances.
     * defaultValue is null. If null, the artifacts supported will be inferred from the supported data sources.
     */
    readonly supportedArtifactTypes?: ReadonlyArray<DatahubArtifactType>;
    /**
     * Gets or sets the connectivity mode settings for Datahub.
     *
     * When it is set, user will be prompted to chose a connectivity mode when connecting to an artifact
     *  on Datahub, then the selected mode is propogated via <see cref = "DataDiscoveryDatahubCommitResult.ConnectivityModeId"/>.
     */
    readonly connectivityModeSettings?: DatahubConnectivityModeSettings;
    /**
     * Gets or sets Datahub settings in JSON format.
     * These settings are directly passed to the DatahubCompactViewConfig in the datahub-client.
     * Type Definition: https://learn.microsoft.com/en-us/javascript/api/%40ms-fabric/workload-client/datahubcompactviewconfig
     * To generate the JSON string to use here: https://dailyplayground.powerbi.com/en-us/datahub-playground?refreshAccessToken=true
     * The default value is null.
     * For the time being the DatahubCompactViewConfigJson will override Deprecated settings until they are removed.
     */
    readonly additionalDatahubSettings?: AdditionalDatahubSettings;
    /**
     * Indicates whether the Datahub tab should be preloaded  when not selected.
     * When enabled, the module is rendered invisibly until selected,
     * @defaultValue false.
     */
    readonly renderTabAsInvisibleWhenNotSelected?: boolean;
}

/** @internal */
export interface DataSourceConnectionDetails {
    readonly connectionId: string;
}

/** @internal */
export interface DataSourceDiscoveryForEachQueryScriptRequest {
    readonly queryScripts: ReadonlyArray<string>;
    readonly kind: DataSourceDiscoveryRequestKind.ForEachQueryScript;
}

/** @internal */
export interface DataSourceDiscoveryForEachStepRequest {
    readonly steps: ReadonlyArray<Step>;
    readonly kind: DataSourceDiscoveryRequestKind.ForEachStep;
}

/** @internal */
export type DataSourceDiscoveryRequest = (
    | DataSourceDiscoveryForEachStepRequest
    | DataSourceDiscoveryForEachQueryScriptRequest
)
& {
    /**
     * Gets or sets a value indicating whether to include connection details in the response.
     *
     * When true, if there is a problem finding the connection details,
     * then one of the <see cref = "DataSourceDiscoveryResultItemErrorCode"/> might be returned.
     *
     * @defaultValue false.
     */
    readonly includeConnectionDetails?: boolean;
    readonly evaluationConnections?: ReadonlyArray<EvaluationConnection>;
}

/** @internal */
export const enum DataSourceDiscoveryRequestKind {
    ForEachStep = "ForEachStep",
    ForEachQueryScript = "ForEachQueryScript",
}

/** @internal */
export type DataSourceDiscoveryResponse =
    | SuccessfulDataSourceDiscoveryResponse
    | FailedDataSourceDiscoveryResponse;

/** @internal */
export const enum DataSourceDiscoveryResponseErrorCode {
    CouldNotGetHostedMashup = "CouldNotGetHostedMashup",
}

/** @internal */
export interface DataSourceDiscoveryResult {
    readonly items: ReadonlyArray<DataSourceDiscoveryResultItem>;
}

/** @internal */
export type DataSourceDiscoveryResultItem =
    | SuccessfulDataSourceDiscoveryResultItem
    | FailedDataSourceDiscoveryResultItem;

/** @internal */
export const enum DataSourceDiscoveryResultItemErrorCode {
    CouldNotGetConnectionInScope = "CouldNotGetConnectionInScope",
    NoConnectionInScopeFound = "NoConnectionInScopeFound",
    NoMatchingOverriddenConnectionFound = "NoMatchingOverriddenConnectionFound",
}

/** @internal */
export interface DataSourceExtension {
    readonly builderLabel: string;
    readonly category: string;
    readonly function: string;
    readonly infoLinkUrl: string;
    readonly isBeta: boolean;
    readonly resourceKind: string;
    readonly resourceLabel: string;
    readonly sourceIcon16: string | null | undefined;
    readonly isExternal: boolean;
    readonly sourceIcon32: string | null | undefined;
    readonly resourceType: string;
    readonly description: string;
    readonly moduleName: string;
    readonly supportsEncryption: boolean;
    readonly supportsNativeQuery: boolean;
    readonly jsonFunctionValue: string;
    readonly jsonAdvancedOptionsParameterNames: string;
    readonly jsonAuthKinds: string;
    readonly enableFunctionNameTelemetry: boolean;
    readonly isGatewayRequired: boolean;
    readonly jsonNativeQueryProperties: string;
    readonly version: string;
    readonly isStubConnector: boolean;
    readonly status: string;
    readonly extensionId: string;
    readonly jsonAuthLabels: string;
    readonly isKnownEndpointSupported: boolean;
    readonly outputDestinationProperties?: OutputDestinationProperties;
}

/** @internal */
export const enum DataSourceExtensionResourceType {
    Singleton = "Singleton",
    Database = "Database",
    Uri = "Uri",
    Custom = "Custom",
}

/** @alpha */
export type DataSourceOptionIdentifier =
    | BuiltInDataSourceOptionIdentifier
    | ExtensionDataSourceOptionIdentifier;

/** @public */
export const enum DataSourceOptionKind {
    BuiltIn = "BuiltIn",
    Extension = "Extension",
}

/** @internal */
export type DefaultOutputDestinationDefinition =
    | JsonDsrDefaultOutputDestinationDefinition
    | MScriptDefaultOutputDestinationDefinition
    | QueryModelDefaultOutputDestinationDefinition;

/** @internal */
export const enum DefaultOutputDestinationDefinitionKind {
    JsonDsr = "JsonDsr",
    MScript = "MScript",
    QueryModel = "QueryModel",
}

/** @alpha */
export type DefaultOutputDestinationSettings =
    | DisabledDefaultOutputDestinationSettings
    | EnabledDefaultOutputDestinationSettings;

/** @internal */
export enum DeleteConnectionErrorCode {
    InsufficientPermissions = "InsufficientPermissions",
    InvalidConnectionId = "InvalidConnectionId",
    MissingDatasourceObjectIdProviderKey = "MissingDatasourceObjectIdProviderKey",
    UnknownError = "UnknownError",
}

/** @internal */
export interface DeleteDefaultOutputDestinationFailureResult {
    readonly success: false;
}

/** @internal */
export type DeleteDefaultOutputDestinationResult =
    | DeleteDefaultOutputDestinationFailureResult
    | DeleteDefaultOutputDestinationSuccessResult;

/** @internal */
export interface DeleteDefaultOutputDestinationSuccessResult {
    readonly unboundQueryNames: ReadonlyArray<string>;
    readonly deletedQueryNames?: ReadonlyArray<string>;
    readonly success: true;
}

/** @internal */
export interface DeleteDestinationsFailureResult {
    readonly success: false;
}

/** @internal */
export interface DeleteDestinationsRequest {
    readonly queryNames: ReadonlyArray<string>;
}

/** @internal */
export type DeleteDestinationsResult =
    | DeleteDestinationsFailureResult
    | DeleteDestinationsSuccessResult;

/** @internal */
export interface DeleteDestinationsSuccessResult {
    readonly updatedQueries: ReadonlyArray<Query>;
    readonly deletedQueryNames: ReadonlyArray<string>;
    readonly success: true;
}

/** @internal */
export interface DeleteQueriesAndQueryGroupsResult {
    readonly deletedQueryGroupIds: ReadonlyArray<string>;
    readonly deletedQueryNames: ReadonlyArray<string>;
    readonly success: boolean;
}

/** @internal */
export interface DeleteQueryConfirmation {
    readonly toolType: ToolConfirmationType.DeleteQuery;
    readonly queryName: string;
}

/** @internal */
export interface DeleteQueryExecutionInfo {
    readonly toolName: string;
    readonly editVersion: number;
    readonly queryName: string;
}

/** @internal */
export interface DeleteQueryOutputFailureResult {
    readonly success: false;
}

/** @internal */
export type DeleteQueryOutputResult =
    | DeleteQueryOutputFailureResult
    | DeleteQueryOutputSuccessResult;

/** @internal */
export interface DeleteQueryOutputSuccessResult {
    readonly success: true;
    readonly updatedSourceQueryMetadata?: QueryMetadata;
    readonly updatedSourceQuery?: Query;
    readonly deletedQueryNames?: ReadonlyArray<string>;
}

/** @internal */
export interface DeleteRecentItemNodesRequest {
    readonly recentItemNodes: ReadonlyArray<RecentItemNode>;
}

/** @internal */
export interface DeleteStepConfirmation {
    readonly toolType: ToolConfirmationType.DeleteStep;
    readonly queryName: string;
    readonly stepIndex: number;
    readonly stepName: string;
}

/** @internal */
export interface DeleteStepExecutionInfo {
    readonly toolName: string;
    readonly editVersion: number;
    readonly queryName: string;
    readonly stepIndex: number;
    readonly queryState: Query;
    readonly queryAndStepDependentGraph: DocumentMemberDependentGraph;
}

/** @alpha */
export type DestinationDefinition =
    | ReferenceDestinationDefinition
    | ScriptDestinationDefinition;

/** @alpha */
export const enum DestinationDefinitionKind {
    Reference = "Reference",
    Script = "Script",
}

/** @internal */
export interface DestinationDetails {
    readonly fields: ReadonlyArray<DestinationField>;
}

/** @internal */
export const enum DestinationDetailsName {
    Cluster = "Cluster",
    Database = "Database",
    File = "File",
    Folder = "Folder",
    Lakehouse = "Lakehouse",
    Object = "Object",
    Path = "Path",
    Schema = "Schema",
    Server = "Server",
    Table = "Table",
    Url = "Url",
    Warehouse = "Warehouse",
    Workspace = "Workspace",
}

/** @internal */
export interface DestinationField {
    readonly displayName?: LocalizedString;
    readonly displayValue: LocalizedString;
    readonly linkKind: LinkKind;
    readonly name: DestinationDetailsName;
    readonly value: string;
}

/** @internal */
export interface DetailedMashupDataSourceDiscovery {
    readonly unmatchedDiscoveredDataSources: ReadonlyArray<MashupDataSource>;
    readonly matchedDiscoveredDataSources: ReadonlyArray<MashupDataSource>;
    readonly unknownDataSourcesExist: boolean;
}

/** @internal */
export interface DetectTableHeaderAndColumnTypesRequestAdditionalInfo {
    readonly headerDetectionPolicy?: HeaderDetectionPolicy;
    readonly columnsToDetect?: ReadonlyArray<string>;
    readonly forceInferTableType?: boolean;
}

/** @alpha */
export interface DisabledDefaultOutputDestinationSettings {
    readonly enableDefaultOutputDestination: false;
}

/** @alpha */
export interface DisabledQueryOutputDestinationSettings {
    readonly enableQueryOutputDestinations: false;
}

/** @internal */
export const enum DiscoverDataSourcesErrorCode {
    InvalidRequest = "InvalidRequest",
    MashupNotFound = "MashupNotFound",
}

/** @internal */
export interface DiscoverDataSourcesForMashupDocumentRequest {
    readonly kind: DiscoverDataSourcesRequestKind.MashupDocument;
    readonly mashupDocument: string;
}

/** @internal */
export interface DiscoverDataSourcesForMashupIdRequest {
    readonly kind: DiscoverDataSourcesRequestKind.MashupId;
    readonly mashupId: string;
}

/** @internal */
export interface DiscoverDataSourcesForQueryScriptsRequest {
    readonly queryScripts: ReadonlyArray<string>;
    readonly kind: DiscoverDataSourcesRequestKind.QueryScripts;
}

/** @internal */
export interface DiscoverDataSourcesForStepsRequest {
    readonly steps: ReadonlyArray<Step>;
    readonly kind: DiscoverDataSourcesRequestKind.Steps;
}

/** @internal */
export type DiscoverDataSourcesRequest =
    | DiscoverDataSourcesForMashupDocumentRequest
    | DiscoverDataSourcesForMashupIdRequest
    | DiscoverDataSourcesForQueryScriptsRequest
    | DiscoverDataSourcesForStepsRequest;

/** @internal */
export const enum DiscoverDataSourcesRequestKind {
    MashupId = "MashupId",
    MashupDocument = "MashupDocument",
    QueryScripts = "QueryScripts",
    Steps = "Steps",
}

/** @internal */
export interface DiscoverDataSourcesResponse {
    readonly discoveries: ReadonlyArray<MashupDataSourceDiscovery>;
}

/** @internal */
export interface DiscoverDetailedDataSourcesFailureResult {
    readonly success: false;
    readonly errorReason: string;
}

/** @internal */
export interface DiscoverDetailedDataSourcesRequest {
    readonly hostContext: HostContext;
    readonly mashupDocument: string;
    readonly connectionOverrides: ReadonlyArray<EvaluationConnection>;
}

/** @internal */
export type DiscoverDetailedDataSourcesResult =
    | DiscoverDetailedDataSourcesSuccessResult
    | DiscoverDetailedDataSourcesFailureResult;

/** @internal */
export interface DiscoverDetailedDataSourcesSuccessResult {
    readonly success: true;
    readonly detailedDiscovery: DetailedMashupDataSourceDiscovery;
}

/** @internal */
export interface DiscoveredConnectionDataSources {
    readonly matchedConnectionDataSourcePairs: ReadonlyArray<ConnectionDataSourcePair>;
    readonly unmatchedDataSources: ReadonlyArray<MashupDataSource>;
}

/** @internal */
export interface DiscoverRelatedTablesRequestAdditionalInfo {
    readonly jsonNavigationPath: string;
    readonly jsonSelectedNodes: string;
}

/** @internal */
export const enum DmtsFabricProviderContextDetailKey {
    ConsentedConnectionState = "ConsentedConnectionState",
    EnablePersonalAndSharedCloudConnections = "EnablePersonalAndSharedCloudConnections",
    CapacityObjectId = "CapacityObjectId",
    DataflowId = "DataflowId",
    WorkloadHostname = "workloadBaseURI_CapacityDNSSuffix",
    WorkspaceObjectId = "WorkspaceObjectId",
    SessionCallbackContext = "SessionCallbackContext",
}

/** @internal */
export const enum DmtsPowerBiProviderContextDetailKey {
    ConnectionsPartitionContext = "connectionsPartitionContext",
    EnableUniqueConnectionsMode = "EnableUniqueConnectionsMode",
}

/** @internal */
export interface DocumentInspectionRequest {
    readonly mashupDocument: string;
}

/** @internal */
export interface DocumentInspectionResponse {
    readonly queryInspections: ReadonlyArray<QueryInspection>;
}

/** @internal */
export interface DropBlankRowsTransform {
    readonly id: SuggestedTransformId.DropBlankRows;
    readonly step: Step;
    readonly suggestionScore: number;
}

/** @internal */
export const enum DropColumnReason {
    Constant = "Constant",
    Duplicate = "Duplicate",
    Empty = "Empty",
    Index = "Index",
}

/** @internal */
export interface DropColumnTransform {
    readonly columnName: string;
    readonly id: SuggestedTransformId.DropColumn;
    readonly reason: DropColumnReason;
    readonly step: Step;
    readonly suggestionScore: number;
}

/** @internal */
export interface DropDuplicatesTransform {
    readonly id: SuggestedTransformId.DropDuplicates;
    readonly step: Step;
    readonly suggestionScore: number;
}

/** @public */
export interface EditingSessionMashup extends HostContextAgnosticEditingSessionMashup {
    readonly hostContext: HostContext;
}

/** @public */
export interface EditingSessionQueryMetadata {
    readonly queryId: string;
    readonly queryName: string;
    readonly queryGroupId: string | null | undefined;
    readonly entityName?: string;
    readonly lastKnownIsCalculatedEntity?: boolean;
    readonly lastKnownIsLinkedEntity?: boolean;
    readonly lastKnownIsParameter?: boolean;
    readonly isHidden?: boolean;
    readonly lastKnownResultTypeName: TypeName | null | undefined;
    readonly loadEnabled: boolean;
    readonly fieldsMetadata?: { [key: string]: FieldMetadata };
    readonly deleteExistingDataOnLoad?: boolean;
    readonly hostProperties?: { [key: string]: string };
    readonly jsonOutputDestinations?: string;
    readonly bindToDefaultOutputDestination?: boolean;
    readonly jsonIncrementalRefreshSettings?: string;
    /**
     * Gets or sets the StagingDefinition.
     * @alpha
     */
    readonly stagingDefinition?: StagingDefinition;
}

/** @internal */
export interface EditStepExecutionInfo {
    readonly toolName: string;
    readonly editVersion: number;
    readonly queryName: string;
    readonly stepIndex: number;
    readonly queryState: Query;
    readonly queryAndStepDependentGraph: DocumentMemberDependentGraph;
}

/** @internal */
export interface EmitMdmMetricsRequest {
    readonly metrics: ReadonlyArray<MdmMetric>;
}

/** @alpha */
export interface EnabledDefaultOutputDestinationSettings {
    readonly enableDefaultOutputDestination: true;
    readonly destinationJsonDataSourceReference: string;
}

/** @alpha */
export interface EnabledQueryOutputDestinationSettings {
    readonly enableQueryOutputDestinations: true;
    readonly allowedQueryOutputDestinations: ReadonlyArray<QueryOutputDestination>;
}

/** @public */
export const enum EnabledStatePolicy {
    AlwaysDisabled = "AlwaysDisabled",
    Default = "Default",
}

/** @internal */
export const enum EncryptConnection {
    false = 0,
    true = 1,
    notApplicable = 2,
    existingConnection = 3,
}

/** @internal */
export enum EnsureConnectionUsableErrorCode {
    InvalidDataPoolId = "InvalidDataPoolId",
    UnknownError = "UnknownError",
}

/** @internal */
export interface ErrorTrace {
    readonly message: string;
    readonly stack: string | null | undefined;
    readonly details: string;
}

/** @internal */
export interface EvaluateSampleFailureResult {
    readonly success: false;
    readonly errorReason: string;
}

/** @internal */
export interface EvaluateSampleFailureResult2 {
    readonly success: false;
    readonly errorReason: string;
}

/** @internal */
export interface EvaluateSampleRequest {
    readonly hostContext: HostContext;
    readonly documentLocale: string;
    readonly queryScript: string;
    readonly sampleTableColumnNames: ReadonlyArray<string>;
    readonly sampleTableCells: ReadonlyArray<ReadonlyArray<string>>;
    readonly userTier?: string;
}

/** @internal */
export type EvaluateSampleResult =
    | EvaluateSampleSuccessResult
    | EvaluateSampleFailureResult;

/** @internal */
export type EvaluateSampleResult2 =
    | EvaluateSampleSuccessResult2
    | EvaluateSampleFailureResult2;

/** @internal */
export const enum EvaluateSampleResultKind {
    JsonResult = "JsonResult",
    EvaluationResult = "EvaluationResult",
}

/** @internal */
export interface EvaluateSampleSuccessResult {
    readonly success: true;
    readonly columnNames: ReadonlyArray<string>;
    readonly columnTypes: ReadonlyArray<string>;
    readonly rows: ReadonlyArray<ReadonlyArray<string | null | undefined>>;
}

/** @internal */
export interface EvaluateSampleSuccessResult2 {
    readonly success: true;
    readonly kind: EvaluateSampleResultKind;
    readonly json: string;
}

/** @public */
export interface EvaluationConnection {
    readonly path: string;
    readonly kind: string;
    readonly provider?: string;
    readonly authenticationKind: string;
    readonly environmentName?: string;
    readonly apiName?: string;
    readonly connectionName?: string;
    readonly credentialDetails: { [key: string]: string };
    readonly audience?: string;
    readonly connectionParameters?: { [key: string]: object };
    readonly adfTokenUrl?: string;
    readonly adfLinkedServiceName?: string;
    readonly privacyGroup?: string;
    readonly dataflowId?: string;
    readonly workspaceId?: string;
    readonly capacityId?: string;
}

/** @internal */
export interface EvaluationConnectionsDataSourcePair {
    readonly evaluationConnections: ReadonlyArray<EvaluationConnection>;
    readonly dataSource: MashupDataSource;
}

/** @public */
export const enum EvaluationErrorCode {
    ApiHubTokenExchangeFailed = "ApiHubTokenExchangeFailed",
    Cancelled = "Cancelled",
    ConnectionDisabled = "ConnectionDisabled",
    ConnectionIsNotAllowedThroughGateway = "ConnectionIsNotAllowedThroughGateway",
    ConnectionNonRoutable = "ConnectionNonRoutable",
    ConnectorDisabled = "ConnectorDisabled",
    MaxCredentialsExceeded = "MaxCredentialsExceeded",
    DmtsTooManyRequests = "DmtsTooManyRequests",
    DuplicateEvaluationId = "DuplicateEvaluationId",
    ErrorGettingPowerBIPrivateLinkConfig = "ErrorGettingPowerBIPrivateLinkConfig",
    EvaluationAgentNotFound = "EvaluationAgentNotFound",
    EvaluationQuotaReached = "EvaluationQuotaReached",
    ExternalServiceError = "ExternalServiceError",
    GatewayDetailsNotFound = "GatewayDetailsNotFound",
    GatewayNeeded = "GatewayNeeded",
    GatewayNotAuthorized = "GatewayNotAuthorized",
    GatewayNotFound = "GatewayNotFound",
    GatewayOAuthPublicClientAppBlockedException = "GatewayOAuthPublicClientAppBlockedException",
    GatewayUnknownError = "GatewayUnknownError",
    GatewayUnreachable = "GatewayUnreachable",
    GatewayUpgradeRequired = "GatewayUpgradeRequired",
    GatewayUserError = "GatewayUserError",
    InvalidGatewayObjectId = "InvalidGatewayObjectId",
    InvalidMashup = "InvalidMashup",
    InvalidOutputTransformArgument = "InvalidOutputTransformArgument",
    InvalidOutputTransformType = "InvalidOutputTransformType",
    MashupEntityNotFound = "MashupEntityNotFound",
    MaxConcurrentEvaluationsReached = "MaxConcurrentEvaluationsReached",
    MwcCapacityCannotBeCharged = "MwcCapacityCannotBeCharged",
    NetworkAccessDenied = "NetworkAccessDenied",
    NoPowerBiClusterForTenant = "NoPowerBiClusterForTenant",
    OAuthTokenLoginFailed = "OAuthTokenLoginFailed",
    OAuthTokenRefreshFailure = "OAuthTokenRefreshFailure",
    ParameterNotFound = "ParameterNotFound",
    PowerAppsEnvironmentNotFound = "PowerAppsEnvironmentNotFound",
    PrivacyUpdateFirewallChallenge = "PrivacyUpdateFirewallChallenge",
    QueryNameAndQueryScriptBothSpecified = "QueryNameAndQueryScriptBothSpecified",
    QueryNameAndQueryScriptNotSpecified = "QueryNameAndQueryScriptNotSpecified",
    QueryNotFound = "QueryNotFound",
    StepNotFound = "StepNotFound",
    StreamingBadRequest = "StreamingBadRequest",
    TimedOut = "TimedOut",
    UnableToContactBackend = "UnableToContactBackend",
    Unknown = "Unknown",
    UnsupportedAadTokenProviderWithGateway = "UnsupportedAadTokenProviderWithGateway",
    UnsupportedKind = "UnsupportedKind",
    ValueTooComplex = "ValueTooComplex",
}

/** @internal */
export interface EvaluationExecutionInfo {
    readonly toolName: string;
    /**
     * Time in milliseconds spent inside the Mashup Engine evaluating the query.
     */
    readonly engineTimeMs?: number;
    /**
     * Total wall-clock time in milliseconds for the evaluation round-trip
     * (includes engine time, network, serialization).
     */
    readonly evaluationTimeMs?: number;
}

/** @internal */
export interface EvaluationParamsBase {
    /**
     * Gets or sets a value indicating whether the .NET Core Evaluator can be used.
     * When the option is set to true, we route the evaluation to the .NET core Evaluator
     * if the evaluation uses supported connectors and belongs to specific host contexts.
     */
    readonly allowModernEvaluationEngine: boolean;
    readonly forceRefresh: boolean;
    readonly locale: string;
    readonly sessionId: string;
    readonly workspaceVariables: string;
    readonly slot?: number;
    readonly skipCount?: number;
    readonly takeCount?: number;
    readonly enableHtmlBinaryRendering?: boolean;
}

/** @internal */
export const enum EvaluationParamsKind {
    Path = "Path",
    QueryScript = "QueryScript",
}

/** @internal */
export interface EvaluationPath {
    readonly mashupId: string;
    readonly queryName: string;
    readonly stepName?: string;
}

/** @internal */
export const enum EvaluationResultKind {
    table = 0,
    challenge = 1,
    exception = 2,
    errorCode = 3,
    cancellation = 4,
    gatewayNeeded = 5,
    binaryMetadata = 6,
}

/** @internal */
export const enum EvaluationType {
    AutoFilterColumnValues = "AutoFilterColumnValues",
    BinaryMetadata = "BinaryMetadata",
    ConnectorRows = "ConnectorRows",
    ConnectorSchema = "ConnectorSchema",
    CubeAllowedValues = "CubeAllowedValues",
    CubeDisplayFolders = "CubeDisplayFolders",
    CubeParameters = "CubeParameters",
    DataSourceQuery = "DataSourceQuery",
    DetailsPreview = "DetailsPreview",
    DetectTableHeaderAndColumnTypes = "DetectTableHeaderAndColumnTypes",
    DsrDetailsFetching = "DsrDetailsFetching",
    ExcelTableInferenceGetInferredTables = "ExcelTableInferenceGetInferredTables",
    ExpandColumnNames = "ExpandColumnNames",
    GetDestinationSchema = "GetDestinationSchema",
    JsonTableInferenceGetJsonText = "JsonTableInferenceGetJsonText",
    LoadToCdsCreateEntitiesRelationships = "LoadToCdsCreateEntitiesRelationships",
    LoadToCdsRelationships = "LoadToCdsRelationships",
    LoadToCdsSchema = "LoadToCdsSchema",
    MappingEditorQueryProfiling = "MappingEditorQueryProfiling",
    MappingEditorSchemaResult = "MappingEditorSchemaResult",
    MappingEditorTypeMetadata = "MappingEditorTypeMetadata",
    MashupLoad = "MashupLoad",
    MergeMatchAnalysis = "MergeMatchAnalysis",
    MergeQueriesPreview = "MergeQueriesPreview",
    MergeQueriesSuggestionsLeftCandidatePreview = "MergeQueriesSuggestionsLeftCandidatePreview",
    MergeQueriesSuggestionsRightCandidatePreview = "MergeQueriesSuggestionsRightCandidatePreview",
    NaturalLanguageValidation = "NaturalLanguageValidation",
    NavigatorCubeAllowedValues = "NavigatorCubeAllowedValues",
    NavigatorCubeParameters = "NavigatorCubeParameters",
    NavigatorCubePreview = "NavigatorCubePreview",
    NavigatorDiscoverRelatedTables = "NavigatorDiscoverRelatedTables",
    NavigatorPreview = "NavigatorPreview",
    NavigatorTemporaryQueryForIndirectionNode = "NavigatorTemporaryQueryForIndirectionNode",
    Preview = "Preview",
    QueryAnalysis = "QueryAnalysis",
    QueryPlan = "QueryPlan",
    QuerySanitizer = "QuerySanitizer",
    QuerySanitizerAnalysis = "QuerySanitizerAnalysis",
    QuerySanitizerQueryAnalysis = "QuerySanitizerQueryAnalysis",
    QueryScriptPreview = "QueryScriptPreview",
    SplitColumnInferMultiplePositions = "SplitColumnInferMultiplePositions",
    SplitColumnNumberOfColumns = "SplitColumnNumberOfColumns",
    StepAnalysis = "StepAnalysis",
    TableProfiler = "TableProfiler",
    TextByExampleGetText = "TextByExampleGetText",
    WebByExampleExecuteSelectors = "WebByExampleExecuteSelectors",
    WebByExampleGetHtml = "WebByExampleGetHtml",
}

/** @internal */
export interface EvaluationUsage {
    readonly hourlyEvaluationCount: number;
    readonly dailyEvaluationTime: number;
    readonly hourlyEvaluationCountRemaining: number;
    readonly dailyEvaluationTimeRemaining: number;
}

/** @internal */
export interface ExcelTableInferenceGetInferredTablesRequest {
    readonly jsonPattern: string;
    readonly mashupId: string;
    readonly localizedStrings: ExcelTableInferenceLocalizedStrings;
}

/** @internal */
export interface ExcelTableInferenceGetInferredTablesResponse {
    readonly jsonQueryStates: ReadonlyArray<string>;
    readonly success: boolean;
}

/** @internal */
export interface ExcelTableInferenceLocalizedStrings {
    readonly addedColumn: string;
    readonly addedIndex: string;
    readonly allRows: string;
    readonly defaultQueryNameFormatString: string;
    readonly defaultTableTitleFormatString: string;
    readonly filledDown: string;
    readonly filterNullAndWhitespace: string;
    readonly groupedRows: string;
    readonly indexColumn: string;
    readonly mergedColumns: string;
    readonly promotedHeaders: string;
    readonly removedBlankRows: string;
    readonly removedBottomRows: string;
    readonly removedColumns: string;
    readonly removedOtherColumns: string;
    readonly removedTopRows: string;
    readonly selectedGroup: string;
    readonly source: string;
    readonly transposedTable: string;
}

/** @internal */
export interface ExchangeTokenResponse {
    readonly accessToken: string;
    readonly baseUrl: string;
    readonly expiresOn: number;
}

/** @internal */
export interface ExplicitColumnMappings {
    readonly kind: ColumnMappingsKind.Explicit;
    readonly columnMappingPairs: ReadonlyArray<ColumnMappingPair>;
}

/** @internal */
export interface ExportTemplateSettings extends TemplateSettings {
}

/** @internal */
export interface ExtendEditingSessionResponse {
    readonly success: boolean;
    readonly error?: string;
}

/** @internal */
export interface ExtendedQueryDetails extends QueryDetails {
    readonly actionType?: string;
    readonly bindToDefaultDestination?: boolean;
    readonly copyIntent: CopyIntent;
    readonly dataDestinations?: DataDestinations;
    readonly incrementalRefreshSettings?: IncrementalRefreshSettings;
    readonly itemType?: string;
    readonly stagingDefinition?: StagingDefinition;
}

/** @alpha */
export interface ExtensionDataSourceOptionIdentifier {
    readonly kind: DataSourceOptionKind.Extension;
    readonly resourceKind: string;
}

/** @internal */
export interface ExtractPreviousStepsResult {
    readonly modifiedQuery: Query;
    readonly newQuery: Query;
}

/** @public */
export enum FabricArtifactKind {
    CopyJob = "CopyJob",
    Dashboard = "Dashboard",
    DataPipeline = "DataPipeline",
    Eventstream = "Eventstream",
    KQLDataConnection = "KQLDataConnection",
    KQLDatabase = "KQLDatabase",
    KQLQueryset = "KQLQueryset",
    Lakehouse = "Lakehouse",
    MLExperiment = "MLExperiment",
    MLModel = "MLModel",
    MountedWarehouse = "MountedWarehouse",
    Notebook = "Notebook",
    PaginatedReport = "PaginatedReport",
    Report = "Report",
    SQLDatabase = "SQLDatabase",
    SQLEndpoint = "SQLEndpoint",
    SemanticModel = "SemanticModel",
    SparkJobDefinition = "SparkJobDefinition",
    Warehouse = "Warehouse",
}

/** @internal */
export interface FabricLakehouse {
    readonly objectId: string;
    readonly name: string;
}

/** @internal */
export interface FabricLakehousesResult {
    readonly lakehouses: ReadonlyArray<FabricLakehouse>;
}

/** @internal */
export interface FabricWorkspace {
    readonly id: string;
    readonly type: string;
    readonly displayName: string;
    readonly capacityId: string;
}

/** @internal */
export interface FabricWorkspacesResult {
    readonly workspaces: ReadonlyArray<FabricWorkspace>;
}

/** @internal */
export const enum FabricWorkspaceType {
    Personal = "Personal",
    Workspace = "Workspace",
    AdminWorkspace = "AdminWorkspace",
}

/** @internal */
export interface FailedArtifactConnectionInfoResult {
    readonly errorCode: ArtifactConnectionInfoErrorCode;
    readonly success: false;
}

/** @internal */
export interface FailedDataSourceDiscoveryResponse {
    readonly errorCode: DataSourceDiscoveryResponseErrorCode;
    readonly success: false;
}

/** @internal */
export interface FailedDataSourceDiscoveryResultItem {
    readonly errorCode: DataSourceDiscoveryResultItemErrorCode;
    readonly success: false;
}

/** @internal */
export interface FailedGetQueryScriptsResponse {
    readonly errorCode: GetQueryScriptsErrorCode;
    readonly success: false;
}

/** @internal */
export interface FailedRecentItemNodesOperationResponse {
    readonly kind: RecentItemNodesOperationResponseKind.Failed;
}

/** @internal */
export interface FailedTokenResponse {
    /**
     * Gets or sets the reason for the failure to provide user-feedback.
     *
     * It should be a user-friendly localized message that explains why the token acquisition failed
     *  and how the user could resolve the issue.
     */
    readonly reason: string;
    readonly success: false;
}

/** @internal */
export interface FailureOperationResult {
    readonly success: false;
    readonly errorMessage: LocalizedString;
}

/** @internal */
export interface FeatureFlags {
    readonly disableEvaluationProgressInfo?: boolean;
    readonly disableGetDataWizardNextButtonOnGatewayFormError?: boolean;
    readonly disableOAuth?: boolean;
    readonly disableQueryAnalysisOnHiddenQueries?: boolean;
    readonly disableQueryParameterOption?: boolean;
    readonly disableRegularQueryOptionInOutputDestination?: boolean;
    readonly disableWebsiteLink?: boolean;
    readonly enableAadOAuthObo?: boolean;
    readonly enableAddColumnUsingNaturalLanguage?: boolean;
    readonly enableAdvancedDestinationEditing?: boolean;
    readonly enableAgenticEditorInEditQueryFlow?: boolean;
    readonly enableAIInsights?: boolean;
    readonly enableAKVForPipelineConnections?: boolean;
    readonly enableAnalyzeDetailedLogs?: boolean;
    readonly enableAppendDragAndDrop?: boolean;
    readonly enableAuthKindKeyForWeb?: boolean;
    readonly enableAutoAadOAuthOboConnection?: boolean;
    readonly enableAutoBindOnBlankQuery?: boolean;
    readonly enableAutoBindOnFabricAIColumn?: boolean;
    readonly enableAutoBindOnPasteQueries?: boolean;
    readonly enableAzureResourcePickerForDF?: boolean;
    readonly enableAzureResourcePickerMySql?: boolean;
    readonly enableAzureResourcePickerPostgreSQL?: boolean;
    readonly enableAzureResourcePickerSql?: boolean;
    readonly enableBrowseInAzure?: boolean;
    readonly enableBusyPaneMigration?: boolean;
    readonly enableCancelButtonInDataDiscovery?: boolean;
    readonly enableCancellableAutomaticTransformations?: boolean;
    readonly enableCdsAExperimentalDataSources?: boolean;
    readonly enableChooseDataDragAndDrop?: boolean;
    readonly enableCloudConnectionForOracle?: boolean;
    readonly enableConnectionCredentialsFormStaysOpenAfterOAuthSignin?: boolean;
    readonly enableConnectionValidationInConnectionDialog?: boolean;
    readonly enableCopilotContextInGetData?: boolean;
    readonly enableCopilotDebugInfo?: boolean;
    readonly enableCopilotFabricArtifactsInModernGetData?: boolean;
    readonly enableCopilotFileUploadInModernGetData?: boolean;
    readonly enableCopilotGetDataPreviewTableTransformations?: boolean;
    readonly enableCopilotOneDriveInModernGetData?: boolean;
    readonly enableCopilotStatusCheck?: boolean;
    readonly enableCopilotStatusInNaturalLanguageService?: boolean;
    readonly enableCSVOutputConfiguration?: boolean;
    readonly enableCubeCommands?: boolean;
    readonly enableCustomDataSources?: boolean;
    readonly enableDataAddOns?: boolean;
    readonly enableDataflowApisInHost?: boolean;
    readonly enableDataflowsAssistedMappingGA?: boolean;
    readonly enableDataSourceReferenceNavigationSplit?: boolean;
    readonly enableDataSourceSettings?: boolean;
    readonly enableDebugMode?: boolean;
    readonly enableDiagramViewSettings?: boolean;
    readonly enableDmtsObo?: boolean;
    readonly enableDmtsPowerBiVNetGatewaySupport?: boolean;
    readonly enableDownloadDetailedLogs?: boolean;
    readonly enableDownloadDetailedLogsForOPDG?: boolean;
    readonly enableEditorVisualTableView?: boolean;
    readonly enableEditVersionEnforcement?: boolean;
    readonly enableEvaluationQuotaUsage?: boolean;
    readonly enableExcelAdvancedBuilder?: boolean;
    readonly enableExcelFileOutput?: boolean;
    readonly enableExploreDatasourcesApiCallForCdsA?: boolean;
    readonly enableExportAndImportTemplateWithDestinations?: boolean;
    readonly enableExtendedClipboardMashupFormat?: boolean;
    readonly enableExtensionMethodsSyntax?: boolean;
    readonly enableFastCopyByDefault?: boolean;
    readonly enableFeatureFlagsDialog?: boolean;
    readonly enableFilePathInWebPageConnector?: boolean;
    readonly enableFileUpload?: boolean;
    readonly enableFluentV9_Everywhere?: boolean;
    readonly enableFluentV9_Infra?: boolean;
    readonly enableGenericInvokeNativeQuery?: boolean;
    readonly enableGetDataPromptPreview?: boolean;
    readonly enableGridCellConditionalFormatting?: boolean;
    readonly enableHighChartsVisuals?: boolean;
    readonly enableHtmlBinaryRendering?: boolean;
    readonly enableIE11CheckBypass?: boolean;
    readonly enableImportCommitCommandsInDataDiscovery?: boolean;
    readonly enableIncrementalRefreshSettings?: boolean;
    readonly enableInlineEditingMode?: boolean;
    readonly enableInspector?: boolean;
    readonly enableKeyboardShortcutsDialog?: boolean;
    readonly enableKeyPairAuthentication?: boolean;
    readonly enableLocalDocumentEdits?: boolean;
    readonly enableManageConnectionsConnectionsFromOverrides?: boolean;
    readonly enableManageConnectionsDialog?: boolean;
    readonly enableManageConnectionsGlobalPermissions?: boolean;
    readonly enableManageConnectionsMoniker?: boolean;
    readonly enableManageConnectionsRemoveConnection?: boolean;
    readonly enableManageConnectionsStaticAnalysis?: boolean;
    readonly enableMappingAsAStep?: boolean;
    readonly enableMappingAsAStepV2?: boolean;
    readonly enableMappingAsAStepV2SearchInAllCdmSchemas?: boolean;
    readonly enableMappingEditorCompositeKeySupport?: boolean;
    readonly enableMappingEditorUniqueIdFieldSupport?: boolean;
    readonly enableMaxConcurrencySetting?: boolean;
    readonly enableMGDCopilotSplitView?: boolean;
    readonly enableMGDCopilotVisualization?: boolean;
    readonly enableMicrosoftInformationProtection?: boolean;
    readonly enableModernEvaluationEngine?: boolean;
    readonly enableModernGetData?: boolean;
    readonly enableModernRefreshServiceSetting?: boolean;
    readonly enableMyQueriesOnAgenticDataflow?: boolean;
    readonly enableNavigatorDataSourceIcons?: boolean;
    readonly enableNavigatorScratchpadSession?: boolean;
    readonly enableNavigatorSensitivityLabel?: boolean;
    readonly enableNewAutomaticTransformationsExperience?: boolean;
    readonly enableNewBlankQueryNameConvention?: boolean;
    readonly enableNewEmptyGridUI?: boolean;
    readonly enableNewMappingEditorAIExperience?: boolean;
    readonly enableNewRecentsStorage?: boolean;
    readonly enableNewRecentsUI?: boolean;
    readonly enableNewWarehouseInAnotherWorkspaceFiltering?: boolean;
    readonly enableNoSandboxInDatahub?: boolean;
    readonly enableOAuthInAzureBatchConnection?: boolean;
    readonly enableOBOForLakehouse?: boolean;
    readonly enableOboForOneLakeSql?: boolean;
    readonly enableOneLakeCatalogInGetDataHomeTab?: boolean;
    readonly enableOneLakeCatalogInQueryOutput?: boolean;
    readonly enableOneLakeDataSources?: boolean;
    readonly enableOutputDestinationCapabilitiesMetadata?: boolean;
    readonly enableOutputDestinationParameterMetadata?: boolean;
    readonly enableOutputStepMessageBarReferenceLink?: boolean;
    readonly enableOutputToSchemasByDefault?: boolean;
    readonly enableParameterSets?: boolean;
    readonly enableParametricDefaultValueField?: boolean;
    readonly enablePerformanceProfiling?: boolean;
    readonly enablePostgreSqlOutputDestination?: boolean;
    readonly enablePowerPlatform?: boolean;
    readonly enablePrivacyLevelForCdsA?: boolean;
    readonly enableProgressRowCountNotAvailable?: boolean;
    readonly enablePublishToDataflowController?: boolean;
    readonly enablePublishToDataflowDialog?: boolean;
    readonly enableQueryLibrariesV1?: boolean;
    readonly enableQueryLibrariesV2?: boolean;
    readonly enableQueryOutputAnalysis?: boolean;
    readonly enableQueryOutputAzureSQLHierarchicalNavigation?: boolean;
    readonly enableQueryOutputMockConnector?: boolean;
    readonly enableQueryOutputNewColumnNameLengthValidation?: boolean;
    readonly enableQueryOutputNewColumnNameValidation?: boolean;
    readonly enableQueryOutputNewTableNameLengthValidation?: boolean;
    readonly enableQueryOutputNewTableNameValidation?: boolean;
    readonly enableQueryOutputNewTableNameValidationForWarehouse?: boolean;
    readonly enableQueryOutputNewUI?: boolean;
    readonly enableQueryOutputParameterization?: boolean;
    readonly enableQueryOutputTypeChange?: boolean;
    readonly enableQueryOutputValidationUI?: boolean;
    readonly enableQuickAccessConnections?: boolean;
    readonly enableQuickAccessSavedQueries?: boolean;
    readonly enableReadonlyQueries?: boolean;
    readonly enableRecentInDefaultDestination?: boolean;
    readonly enableRecentInQueryOutput?: boolean;
    readonly enableRecentSourcesInModernGetData?: boolean;
    readonly enableRecordResultWidgets?: boolean;
    readonly enableReferencedDataSources?: boolean;
    readonly enableRefreshParametersSection?: boolean;
    readonly enableRenderMonitor?: boolean;
    readonly enableResultRenderingPreferencesInMgdwCopilotPreview?: boolean;
    readonly enableRetriesOnApiRequestError?: boolean;
    readonly enableRibbonCompoundButtons?: boolean;
    readonly enableSecretSourceProvider?: boolean;
    readonly enableSecretSourceProviderForKeyPair?: boolean;
    readonly enableSecretSourceProviderForOnPremGateway?: boolean;
    readonly enableSecretSourceProviderForVNetGateway?: boolean;
    readonly enableServiceMethodInvocationMonitor?: boolean;
    readonly enableServicePrincipalForAllHosts?: boolean;
    readonly enableServicePrincipalForConsentedConnections?: boolean;
    readonly enableServicePrincipalForOnPremGateway?: boolean;
    readonly enableServicePrincipalForVNetGateway?: boolean;
    readonly enableShareCommands?: boolean;
    readonly enableSharePointPickerOBO?: boolean;
    readonly enableSharePointSitePicker?: boolean;
    readonly enableSharePointSitePickerV2?: boolean;
    readonly enableSharePointSiteSelectorDialog?: boolean;
    readonly enableSharePointSiteSelectorDialogV2?: boolean;
    readonly enableSnowflakeOutputDestination?: boolean;
    readonly enableSnowflakeV2Implementation?: boolean;
    readonly enableSQLBreakdownInQuickAccess?: boolean;
    readonly enableStagedDataSettings?: boolean;
    readonly enableStepGroups?: boolean;
    readonly enableStepScriptExplanation?: boolean;
    readonly enableStreamingVNetGateway?: boolean;
    readonly enableSuggestedTransforms?: boolean;
    readonly enableTableResultInlineFilter?: boolean;
    readonly enableTableResultInlineGroupBy?: boolean;
    readonly enableTelemetryMonitor?: boolean;
    readonly enableTemplates?: boolean;
    readonly enableTerseSyntax?: boolean;
    readonly enableTerseSyntaxQueryScriptGeneration?: boolean;
    readonly enableTestInfo?: boolean;
    readonly enableTimeBasedSchedule?: boolean;
    readonly enableTogglingHiddenQueriesVisibility?: boolean;
    readonly enableTrackDataChanges?: boolean;
    readonly enableTridentStyle?: boolean;
    readonly enableUsingSelectedConnectionGatewayinConnectionFormSettings?: boolean;
    readonly enableVisuals?: boolean;
    readonly enableWelcomePane?: boolean;
    readonly enableWorkspaceIdentityAuthenticationMethod?: boolean;
    readonly enableWorkspaceIdentitySupportForConnectorsPhaseThree?: boolean;
    readonly enableWorkspaceIdentitySupportForConnectorsPhaseTwo?: boolean;
    readonly enforceConfidentialClientForOAuthOnPremDataSources?: boolean;
    readonly forceEnableVirtualNetworkGateways?: boolean;
    readonly mockAIInsightsFunctions?: boolean;
    readonly removePreviewFeatureTagFromIncrementalRefresh?: boolean;
    readonly removeUpsertAfterOAuthSignInForConnectionlessOAuth?: boolean;
    readonly removeUxLimitationForLakehouseDestinationForIncrementalRefresh?: boolean;
    readonly startQueryAndStepAnalysesOnStart?: boolean;
    readonly upsertOboWhenOneDriveUploading?: boolean;
    readonly useConfidentialClientForOAuthOnPremDataSources?: boolean;
    readonly useDatahubDailyEndpoint?: boolean;
    readonly useDatahubDebugBundles?: boolean;
    readonly useHostAuthTokenForOneLakeCatalog?: boolean;
    readonly useNoticeBrowserTextOnSignIn?: boolean;
}

/** @alpha */
export const enum FeedbackKind {
    Positive = "Positive",
    Negative = "Negative",
}

/** @alpha */
export interface FeedbackOptionSettings {
    /**
     * Gets or sets the kind of feedback this option represents.
     */
    readonly kind: FeedbackKind;
    /**
     * Gets or sets the display name for this feedback option.
     */
    readonly displayName: string;
    /**
     * Gets or sets the optional tooltip text for this feedback option.
     */
    readonly tooltip?: string;
}

/** @internal */
export interface FeedbackRequest {
    /**
     * Gets or sets the kind of feedback (Positive or Negative).
     */
    readonly kind: FeedbackKind;
}

/** @public */
export interface FieldMetadata {
    readonly sourceColumnName: string | null | undefined;
    readonly destinationFieldType: string;
    readonly autoNumberSettings?: AutoNumberSettings;
}

/** @internal */
export const enum FileFilter {
    None = "None",
    Any = "Any",
    Access = "Access",
    Excel = "Excel",
    Json = "Json",
    TextCsv = "TextCsv",
    Pdf = "Pdf",
    Xml = "Xml",
}

/** @internal */
export interface FileMenuActionableSection {
    readonly type: FileMenuActionableSectionType;
    readonly commands: ReadonlyArray<HostCommand>;
    readonly label?: LocalizedString;
}

/** @internal */
export const enum FileMenuActionableSectionType {
    List = "List",
}

/** @internal */
export type FileMenuNavigationItem =
    | FileMenuNavigationMenuItem
    | FileMenuNavigationSeparator;

/** @internal */
export const enum FileMenuNavigationItemKind {
    MenuItem = "MenuItem",
    Separator = "Separator",
}

/** @internal */
export interface FileMenuNavigationMenuItem {
    readonly enablementConstraint: HostCommandEnablementConstraint;
    readonly iconId: BuiltInIconId;
    readonly id: string;
    readonly label: LocalizedString;
    readonly tooltip?: LocalizedString;
    readonly sections: ReadonlyArray<FileMenuActionableSection>;
    readonly kind: FileMenuNavigationItemKind.MenuItem;
}

/** @internal */
export interface FileMenuNavigationSeparator {
    readonly kind: FileMenuNavigationItemKind.Separator;
}

/** @internal */
export interface FileOutputProperties {
    readonly parametersMetadata: ReadonlyArray<OutputDestinationParameterMetadata>;
    readonly navigationDataColumnName: string;
}

/** @internal */
export const enum FillColumnMethod {
    Max = "Max",
    Mean = "Mean",
    Min = "Min",
    Mode = "Mode",
    RoundedMean = "RoundedMean",
}

/** @internal */
export interface FillColumnTransform {
    readonly columnName: string;
    readonly fillMethod?: FillColumnMethod;
    readonly id: SuggestedTransformId.FillColumn;
    readonly step: Step;
    readonly suggestionScore: number;
}

/** @internal */
export interface FindConnectionsToAutoBindResponse {
    readonly gatewayObjectId?: string;
    readonly evaluationConnectionsToBind: ReadonlyArray<EvaluationConnection>;
    readonly dataSourcesNeedingConnections: ReadonlyArray<MashupDataSource>;
}

/** @internal */
export interface FindMatchingConnectionsForDiscoveredDataSourcesParams {
    readonly kind: FindMatchingConnectionsParamsKind.Discover;
    readonly request: DiscoverDataSourcesRequest;
}

/** @internal */
export interface FindMatchingConnectionsForProvidedDataSourcesParams {
    readonly kind: FindMatchingConnectionsParamsKind.Provided;
    readonly dataSources: ReadonlyArray<MashupDataSource>;
}

/** @internal */
export type FindMatchingConnectionsParams =
    | FindMatchingConnectionsForProvidedDataSourcesParams
    | FindMatchingConnectionsForDiscoveredDataSourcesParams;

/** @internal */
export const enum FindMatchingConnectionsParamsKind {
    Discover = "Discover",
    Provided = "Provided",
}

/** @internal */
export interface FindMatchingConnectionsResponse {
    readonly discoveredConnectionDataSources: ReadonlyArray<DiscoveredConnectionDataSources>;
}

/** @internal */
export interface FindReferencedDataSourcesRequest {
    readonly mashupText: string;
    readonly queryName?: string;
    readonly mashupDiscoveryOptions?: MashupDiscoveryOptions;
    readonly workspaceVariables?: string;
}

/** @internal */
export interface FixedColumnMappings {
    readonly kind: ColumnMappingsKind.Fixed;
    readonly columnMappingPairs: ReadonlyArray<ColumnMappingPair>;
}

/** @public */
export enum FlowStatus {
    ReadyToPaint = "ReadyToPaint",
    Interactive = "Interactive",
}

/** @public */
export interface FlowStatusChange {
    readonly status: FlowStatus;
    /**
     * Gets or sets event timestamp in milliseconds to be used for performance measurements.
     *
     * @remarks
     * - It measured using `performance` API, therefore it is monotonic
     *  see: https://w3c.github.io/hr-time/#dfn-monotonic-clock
     * - `FlowStatusChange` event/callback is fired some time after flow status change has happened
     *   and this value is the exact time when the flow status change has happened.
     */
    readonly perfTimestamp: number;
}

/** @internal */
export interface FolderBuilderSettings {
    readonly disableFolderPathTextField: boolean;
    readonly enableThisDeviceFolderSelection: boolean;
}

/** @internal */
export interface FrontendToolCallInfo {
    readonly id: string;
    readonly name: string;
    readonly arguments: { [key: string]: string };
}

/** @internal */
export interface FrontendToolDefinition {
    readonly name: string;
    readonly description: string;
    readonly parameters: string;
}

/** @internal */
export interface FrontendToolResult {
    readonly toolCallId: string;
    readonly content: string;
    readonly success: boolean;
}

/** @public */
export const enum GatewayKind {
    Personal = "Personal",
    Resource = "Resource",
    TenantCloud = "TenantCloud",
    VirtualNetwork = "VirtualNetwork",
    StreamingVirtualNetwork = "StreamingVirtualNetwork",
}

/** @internal */
export interface GetColumnProfilesRequestAdditionalInfo {
    readonly attributeNames: ReadonlyArray<ColumnProfileAttributeName>;
    readonly columnNames: ReadonlyArray<string>;
    readonly numberOfRowsToProfile?: number;
    readonly distributionKeepCount?: number;
}

/** @internal */
export interface GetConnectionsRequest {
    readonly hostContext: HostContext;
    readonly evaluationConnections: ReadonlyArray<EvaluationConnection>;
}

/** @internal */
export const enum GetConnectorMetadataErrorCode {
    IntrospectionFailure = "IntrospectionFailure",
    InvalidHostContext = "InvalidHostContext",
    Unknown = "Unknown",
}

/** @internal */
export interface GetDatabaseOutputSettingsResult {
    readonly columnMappings: ColumnMappings;
    readonly kind: GetOutputSettingsResultKind.Database;
    readonly mashupDataSourceDiscovery: MashupDataSourceDiscovery;
    readonly tableUpdateSettings: DatabaseTableUpdateSettings;
    readonly targetDefinition: InvocationPatternTargetDefinition;
    readonly targetResourceIdentifier: TargetResourceIdentifier;
}

/** @public */
export interface GetDataSettings {
    /**
     * Gets or sets whether the Data Discovery tab includes a TabList in the Home tab and pass the settings for the TabList.
     */
    readonly dataDiscoveryTabListSettings: DataDiscoveryTabListSettings;
    /**
     * Gets or sets whether the Data Discovery includes a Home Tab and pass the settings for the Home Tab.
     */
    readonly dataDiscoveryHomeModuleSettings: DataDiscoveryModuleSettings<object>;
    /**
     * Gets or sets whether the Blank Query module is enabled in Data Discovery and pass the settings for the module.
     */
    readonly dataDiscoveryBlankQueryModuleSettings: DataDiscoveryModuleSettings<object>;
    /**
     * Gets or sets whether the Blank Table module is enabled in Data Discovery and pass the settings for the module.
     */
    readonly dataDiscoveryBlankTableModuleSettings: DataDiscoveryModuleSettings<object>;
    /**
     * Gets or sets whether the Datahub module is enabled in Data Discovery and pass the settings for the module.
     */
    readonly dataDiscoveryDatahubModuleSettings: DataDiscoveryModuleSettings<DatahubSettings>;
    /**
     * Gets or sets settings for the home tab Datahub section. The section is enabled if the Datahub module is enabled.
     */
    readonly dataDiscoveryDatahubHomeSettings?: AdditionalDatahubSettings;
    /**
     * Gets or sets whether the Templates module is enabled in Data Discovery and pass the settings for the module.
     */
    readonly dataDiscoveryMashupTemplateModuleSettings: DataDiscoveryModuleSettings<object>;
    /**
     * Gets or sets whether the New Source module is enabled in Data Discovery and pass the settings for the module.
     */
    readonly dataDiscoveryNewSourceModuleSettings: DataDiscoveryModuleSettings<object>;
    /**
     * Gets or sets whether the File Upload module is enabled in Data Discovery and pass the settings for the module.
     */
    readonly dataDiscoveryUploadModuleSettings: DataDiscoveryModuleSettings<object>;
    /**
     * Gets or sets whether the Sample Data module is enabled in Data Discovery and pass the settings for the module.
     */
    readonly dataDiscoverySampleDataModuleSettings: DataDiscoveryModuleSettings<SampleDataSettings>;
    /**
     * Gets or sets whether the New Artifact module is enabled in Data Discovery and pass the settings for the module.
     */
    readonly dataDiscoveryNewFabricArtifactModuleSettings: DataDiscoveryModuleSettings<NewFabricArtifactModuleSettings>;
    /**
     * Gets or sets whether the Azure resource module is enabled in Data Discovery and pass the settings for the module.
     * It is allowed by default except for Desktop where this feature is not yet supported.
     */
    readonly dataDiscoveryAzureResourceModuleSettings?: DataDiscoveryModuleSettings<object>;
    /**
     * Gets or sets whether the Recent Items module is enabled in Data Discovery and pass the settings for the module.
     */
    readonly dataDiscoveryRecentItemsModuleSettings: DataDiscoveryModuleSettings<object>;
}

/** @internal */
export const enum GetDataSourceExtensionsErrorCode {
    IntrospectionFailure = "IntrospectionFailure",
    InvalidHostContext = "InvalidHostContext",
    Unknown = "Unknown",
}

/** @internal */
export interface GetDataSourceExtensionsRequest {
    readonly allowedModules?: ReadonlyArray<string>;
    readonly hostContext: HostContext;
}

/** @internal */
export interface GetDataSourceSettings {
    readonly binarySourceBuilderSettings: BinarySourceBuilderSettings;
    readonly folderBuilderSettings: FolderBuilderSettings;
    readonly supportedDataSources: ReadonlyArray<BuiltInDataSourceOptionId>;
    readonly useLargeDataSourceTiles: boolean;
    readonly queryConnectivityModeSettings?: QueryConnectivityModeSettings;
    readonly sharePointPickerSettings: SharePointPickerSettings;
}

/** @internal */
export const enum GetDataWizardStage {
    Cancelled = "Cancelled",
    CombineFiles = "CombineFiles",
    CsvTextSettings = "CsvTextSettings",
    DataDiscovery = "DataDiscovery",
    DataSources = "DataSources",
    DataSourceSettings = "DataSourceSettings",
    FolderPreview = "FolderPreview",
    IncrementalRefreshSettings = "IncrementalRefreshSettings",
    Loading = "Loading",
    MappingEditor = "MappingEditor",
    Navigator = "Navigator",
    QueryEditor = "QueryEditor",
    QuickCreate = "QuickCreate",
    RecentDataPreview = "RecentDataPreview",
    RefreshSettings = "RefreshSettings",
    TemplateCredentials = "TemplateCredentials",
    TemplateParameters = "TemplateParameters",
    TextByExample = "TextByExample",
}

/** @internal */
export interface GetDestinationDetailsFailureResponse {
    readonly success: false;
    readonly error: QueryOutputStatusError;
}

/** @internal */
export interface GetDestinationDetailsRequest {
    readonly queryName: string;
    readonly workspaceVariables?: string;
}

/** @internal */
export type GetDestinationDetailsResponse =
    | GetDestinationDetailsSuccessResponse
    | GetDestinationDetailsFailureResponse;

/** @internal */
export interface GetDestinationDetailsResult {
    readonly destinationDetails: DestinationDetails;
}

/** @internal */
export interface GetDestinationDetailsSuccessResponse {
    readonly success: true;
    readonly destinationDetails: DestinationDetails;
}

/** @internal */
export interface GetDestinationResourceKindFailureResponse {
    readonly success: false;
    readonly error: QueryOutputStatusError;
}

/** @internal */
export interface GetDestinationResourceKindRequest {
    readonly queryName: string;
    readonly workspaceVariables?: string;
}

/** @internal */
export type GetDestinationResourceKindResponse =
    | GetDestinationResourceKindSuccessResponse
    | GetDestinationResourceKindFailureResponse;

/** @internal */
export interface GetDestinationResourceKindSuccessResponse {
    readonly success: true;
    readonly resourceKind: string;
}

/** @internal */
export const enum GetDestinationSchemaErrorCode {
    NoDestinationSet = "GetDestinationSchemaErrorCode_NoDestinationSet",
}

/** @internal */
export interface GetFileOutputSettingsResult {
    readonly kind: GetOutputSettingsResultKind.File;
    readonly mashupDataSourceDiscovery: MashupDataSourceDiscovery;
    readonly outputFileFormatSettings?: OutputFileFormatSettings;
    readonly targetDefinition: InvocationPatternTargetDefinition;
    readonly targetResourceIdentifier: TargetResourceIdentifier;
}

/** @internal */
export interface GetOutputSettingsFailedResult {
    readonly kind: GetOutputSettingsResultKind.Failed;
}

/** @internal */
export interface GetOutputSettingsRequest {
    readonly sourceQueryName: string;
    readonly workspaceVariables?: string;
}

/** @internal */
export type GetOutputSettingsResult =
    | GetDatabaseOutputSettingsResult
    | GetFileOutputSettingsResult
    | GetOutputSettingsFailedResult;

/** @internal */
export const enum GetOutputSettingsResultKind {
    Database = "Database",
    File = "File",
    Failed = "Failed",
}

/** @internal */
export interface GetQueryScriptFromStepList {
    readonly steps: ReadonlyArray<Step>;
    /**
     * Gets or sets the step to be used as the expression step.
     *  If not provided, a reference step is created using the last step in <see cref = "GetQueryScriptFromStepList.Steps"/>
     *   as the expression step.
     */
    readonly expressionStep?: Step;
}

/** @internal */
export const enum GetQueryScriptsErrorCode {
    RequestTooLarge = "RequestTooLarge",
}

/** @internal */
export interface GetQueryScriptsFromStepListsRequest {
    readonly stepLists: ReadonlyArray<GetQueryScriptFromStepList>;
    readonly kind: GetQueryScriptsRequestKind.StepLists;
}

/** @internal */
export type GetQueryScriptsRequest =
    | GetQueryScriptsFromStepListsRequest;

/** @internal */
export const enum GetQueryScriptsRequestKind {
    StepLists = "StepLists",
}

/** @internal */
export type GetQueryScriptsResponse =
    | SuccessfulGetQueryScriptsResponse
    | FailedGetQueryScriptsResponse;

/** @internal */
export interface GetWorkspaceVariablesScriptRequest {
    readonly jsonWorkspaceVariables: string;
}

/** @internal */
export interface GraphTokenResponse {
    readonly accessToken: string;
    readonly baseUrl: string;
}

/** @internal */
export const enum HeaderDetectionPolicy {
    AlwaysWhenInferringAllColumnTypes = "AlwaysWhenInferringAllColumnTypes",
    BasedOnMetadata = "BasedOnMetadata",
    Never = "Never",
}

/** @alpha */
export interface HostCommand {
    readonly enablementConstraint: HostCommandEnablementConstraint;
    readonly iconId: BuiltInIconId;
    readonly id: HostCommandId;
    readonly label: string;
    readonly description: string;
}

/** @alpha */
export interface HostCommandAlwaysEnablementConstraint {
    readonly kind: HostCommandEnablementConstraintKind.Always;
}

/** @alpha */
export type HostCommandEnablementConstraint =
    | HostCommandAlwaysEnablementConstraint
    | HostCommandNeverEnablementConstraint
    | HostCommandHasNewLoadableQueriesEnablementConstraint
    | HostCommandNumberOfQueriesEnablementConstraint;

/** @alpha */
export const enum HostCommandEnablementConstraintKind {
    Always = "Always",
    Never = "Never",
    HasNewLoadableQueries = "HasNewLoadableQueries",
    NumberOfQueries = "NumberOfQueries",
}

/** @alpha */
export const enum HostCommandEnablementConstraintOperator {
    Equals = "Equals",
    GreaterThan = "GreaterThan",
    GreaterThanOrEqual = "GreaterThanOrEqual",
    LessThan = "LessThan",
    LessThanOrEqual = "LessThanOrEqual",
}

/** @alpha */
export interface HostCommandHasNewLoadableQueriesEnablementConstraint {
    readonly kind: HostCommandEnablementConstraintKind.HasNewLoadableQueries;
}

/** @alpha */
export interface HostCommandNeverEnablementConstraint {
    readonly kind: HostCommandEnablementConstraintKind.Never;
}

/** @alpha */
export interface HostCommandNumberOfQueriesEnablementConstraint {
    readonly operator: HostCommandEnablementConstraintOperator;
    readonly value: number;
    readonly kind: HostCommandEnablementConstraintKind.NumberOfQueries;
}

/** @public */
export interface HostContext {
    readonly type: HostContextType;
    readonly details: { [key: string]: string };
}

/** @public */
export interface HostContextAgnosticEditingSessionMashup {
    readonly mashupName: string;
    readonly mashupDocument: string;
    readonly queryGroups: ReadonlyArray<QueryGroupEntity>;
    readonly documentLocale: string;
    readonly gatewayObjectId?: string;
    readonly queriesMetadata: { [key: string]: EditingSessionQueryMetadata };
    readonly connectionOverrides: ReadonlyArray<EvaluationConnection>;
    readonly trustedConnections?: ReadonlyArray<EvaluationConnection>;
    readonly useHostConnectionProvider?: boolean;
    readonly fastCombine?: boolean;
    readonly allowNativeQueries?: boolean;
    readonly allowedModules?: ReadonlyArray<string>;
    readonly skipAutomaticTypeAndHeaderDetection?: boolean;
    /**
     * @deprecated No longer used, evaluation does not allow auto anonymous.
     */
    readonly disableAutoAnonymousConnectionUpsert?: boolean;
    readonly hostProperties?: { [key: string]: string };
    /**
     * Gets or sets the DefaultOutputDestinationSettings.
     * @alpha
     */
    readonly defaultOutputDestinationConfiguration?: DefaultDestinationSettings;
    /**
     * Gets or sets the StagingDefinition.
     * @alpha
     */
    readonly stagingDefinition?: StagingDefinition;
}

/** @public */
export const enum HostContextType {
    Adf = "Adf",
    Athena = "Athena",
    CdsA = "CdsA",
    CdsLite = "CdsLite",
    CdsT = "CdsT",
    CdsV1 = "CdsAdHoc",
    CustomerInsights = "CustomerInsights",
    CustomerInsightsPPDF = "CustomerInsightsPPDF",
    D365TalentInsights = "D365TalentInsights",
    DataIntegration = "DataIntegration",
    DesktopBootstrap = "DesktopBootstrap",
    DesktopUITester = "DesktopUITester",
    DmtsFabricExperimental = "DmtsFabricExperimental",
    DmtsPowerBiExperimental = "DmtsPowerBiExperimental",
    ExcelDataShapingCopilot = "ExcelDataShapingCopilot",
    ExcelDesktopMac = "ExcelDesktopMac",
    ExcelDesktopWindows = "ExcelDesktopWindows",
    ExcelOnline = "ExcelOnline",
    ExcelOnlineDmtsPowerBi = "ExcelOnlineDmtsPowerBi",
    Experimental = "Experimental",
    Flow = "Flow",
    IAPDataflowTemplateReferenceApp = "IAPDataflowTemplateReferenceApp",
    IntelligentOrderManagement = "IntelligentOrderManagement",
    InternalPortal = "InternalPortal",
    KustoViewDesigner = "KustoViewDesigner",
    MashupEditorBootstrap = "MashupEditorBootstrap",
    PowerAppsTeams = "PowerAppsTeams",
    PowerBIDataMart = "PowerBIDataMart",
    PowerBIModeling = "PowerBIModeling",
    PowerBIModelingDmtsPowerBi = "PowerBIModelingDmtsPowerBi",
    PowerBIPaginatedReports = "PowerBIPaginatedReports",
    PowerBIPPDF = "PowerBIPPDF",
    PowerBIReportView = "PowerBIReportView",
    PowerBIDesktop = "PowerBIDesktop",
    PowerPlatformDataflowAdlsGen2 = "PowerPlatformDataflowAdlsGen2",
    PowerPlatformDataflowsBootstrap = "PowerPlatformDataflowsBootstrap",
    PowerPlatformExperimental = "PowerPlatformExperimental",
    PowerQueryDesktop = "PowerQueryDesktop",
    ProcessAdvisor = "ProcessAdvisor",
    SemanticModelAuthoring = "SemanticModelAuthoring",
    SupplyChainVisibility = "SupplyChainVisibility",
    SustainabilityCloud = "SustainabilityCloud",
    Synapse = "Synapse",
    TridentAdminCenterDataverseOnelake = "TridentAdminCenterDataverseOnelake",
    TridentDatabricks = "TridentDatabricks",
    TridentDataflow = "TridentDataflow",
    TridentDataflowNative = "TridentDataflowNative",
    TridentDataPipeline = "TridentDataPipeline",
    TridentDataverseOneLake = "TridentDataverseOneLake",
    TridentDataverseShortcut = "TridentDataverseShortcut",
    TridentDataWarehouse = "TridentDataWarehouse",
    TridentEventStream = "TridentEventStream",
    TridentFunctionSet = "TridentFunctionSet",
    TridentGraphQL = "TridentGraphQL",
    TridentKusto = "TridentKusto",
    TridentLakehouse = "TridentLakehouse",
    TridentLakeWarehouse = "TridentLakeWarehouse",
    TridentMountedDataWarehouse = "TridentMountedDataWarehouse",
    TridentNotebooks = "TridentNotebooks",
    TridentOneLake = "TridentOneLake",
    TridentReplicator = "TridentReplicator",
    TridentShareQuery = "TridentShareQuery",
    TridentSynapseLink = "TridentSynapseLink",
    PpdfConnector = "PpdfConnector",
    DataverseConnector = "DataverseConnector",
}

/** @alpha */
export interface HostQueryPropertiesSettings {
    readonly properties: ReadonlyArray<HostQueryPropertySettings>;
}

/** @alpha */
export interface HostQueryPropertySettings {
    readonly label: string;
    readonly type: HostQueryPropertyType;
    readonly propertyKey: string;
    readonly displayInContextMenu: boolean;
    readonly isReadonly?: boolean;
    readonly defaultValue?: string;
}

/** @alpha */
export const enum HostQueryPropertyType {
    Boolean = "Boolean",
}

/** @internal */
export const enum IdentitySourceKind {
    Process = "Process",
}

/** @internal */
export interface ImportTemplateSettings extends TemplateSettings {
}

/** @internal */
export const enum ImportTemplateWarningType {
    OutputDestinations = "OutputDestinations",
}

/** @internal */
export interface InferColumnTypesRequest {
    readonly hostContext: HostContext;
    readonly documentLocale: string;
    readonly sampleTableColumnNames: ReadonlyArray<string>;
    readonly sampleTableCells: ReadonlyArray<ReadonlyArray<string>>;
    readonly userTier?: string;
}

/** @internal */
export interface InferColumnTypesResult {
    readonly inferredColumnTypes?: ReadonlyArray<string>;
}

/** @internal */
export interface InformationProtectionLabel {
    readonly labelId: Guid;
    readonly name: string;
    readonly tooltip: string;
    readonly children: ReadonlyArray<InformationProtectionLabel>;
    readonly color: string;
    readonly hasProtectionPolicy: boolean;
    readonly sensitivity: number;
}

/** @internal */
export interface InformationProtectionPolicy {
    readonly labels: ReadonlyArray<InformationProtectionLabel>;
    readonly settings: InformationProtectionPolicySettings;
}

/** @internal */
export interface InformationProtectionPolicySettings {
    readonly isLabelingRequired: boolean;
    readonly customMoreInfoUrl: string;
    readonly defaultLabelId?: Guid;
}

/** @internal */
export interface InitializeMashupRequest {
    readonly mashupId: Guid;
    readonly workspaceId: Guid;
    readonly capacityId: string;
}

/** @internal */
export interface InitializeMashupResponse {
    readonly success: boolean;
    readonly errorCode?: string;
    readonly errorDetails?: string;
}

/** @internal */
export interface InitializePqoRequest {
    readonly capacityId: string;
}

/** @internal */
export interface InitializePqoResponse {
    readonly success: boolean;
    readonly errorCode?: string;
    readonly errorMessage?: string;
}

/** @internal */
export interface InitializeWorkspacesRequest {
    readonly mashupId: Guid;
}

/** @internal */
export interface InitializeWorkspacesResponse {
    readonly success: boolean;
    readonly errorCode?: string;
    readonly workspaces?: ReadonlyArray<FabricWorkspace>;
}

/** @internal */
export interface InsertStepExecutionInfo {
    readonly toolName: string;
    readonly editVersion: number;
    readonly queryName: string;
    readonly stepIndex: number;
    readonly queryState: Query;
    readonly queryAndStepDependentGraph: DocumentMemberDependentGraph;
}

/** @internal */
export interface InvocationPatternTargetDefinition {
    readonly kind: TargetDefinitionKind.InvocationPattern;
    readonly targetPattern: InvocationPattern;
    readonly targetNavigationInfos: ReadonlyArray<NavigationInfo>;
}

/** @internal */
export interface JsonActionValue {
    readonly m?: ReadonlyArray<JsonRecordField>;
    readonly t: JsonType;
    readonly v: string;
}

/** @internal */
export interface JsonBinaryValue {
    readonly m?: ReadonlyArray<JsonRecordField>;
    readonly t: JsonType;
    readonly v: string;
}

/** @internal */
export interface JsonBooleanValue extends JsonPrimitiveValueBase<boolean> {
}

/** @internal */
export interface JsonDateTimeValue extends JsonPrimitiveValueBase<string> {
}

/** @internal */
export interface JsonDateTimeZoneValue extends JsonPrimitiveValueBase<string> {
}

/** @internal */
export interface JsonDateValue extends JsonPrimitiveValueBase<string> {
}

/** @internal */
export interface JsonDelayedValue {
    readonly delayed: true;
    readonly m?: ReadonlyArray<JsonRecordField>;
    readonly t?: string;
    readonly v?: string;
}

/** @internal */
export interface JsonDsrDefaultOutputDestinationDefinition {
    readonly kind: DefaultOutputDestinationDefinitionKind.JsonDsr;
    readonly jsonDsr: string;
}

/** @internal */
export interface JsonDurationValue extends JsonPrimitiveValueBase<string> {
}

/** @internal */
export interface JsonEvaluationRequest {
    readonly evaluationId: string;
    readonly evaluationParams: JsonEvaluationParams;
}

/** @internal */
export interface JsonEvaluationRequestWithAdditionalInfo<TAdditionalInfo> extends JsonEvaluationRequest {
    readonly additionalInfo: TAdditionalInfo;
}

/** @internal */
export interface JsonFormattedNumber {
    readonly k: JsonNumericKind.FormattedNumber;
    readonly v: string;
}

/** @internal */
export interface JsonFunctionParameter {
    readonly n: string;
    readonly r: boolean;
    readonly i: JsonFunctionParameterInfo;
}

/** @internal */
export interface JsonFunctionParameterInfo {
    readonly t: string;
    readonly cap?: JsonValue;
    readonly av?: JsonValue;
    readonly avos?: JsonValue;
    readonly sv?: JsonValue;
    readonly eg?: JsonValue;
    readonly dv?: JsonValue;
    readonly fdes?: JsonValue;
}

/** @internal */
export interface JsonFunctionType extends JsonType {
    readonly p?: ReadonlyArray<JsonFunctionParameter>;
    readonly r?: JsonType;
}

/** @internal */
export interface JsonFunctionValue {
    readonly m?: ReadonlyArray<JsonRecordField>;
    readonly t: JsonFunctionType;
    readonly v: string;
}

/** @internal */
export interface JsonListValue {
    readonly m?: ReadonlyArray<JsonRecordField>;
    readonly t: JsonType;
    readonly v: ReadonlyArray<JsonValue>;
}

/** @internal */
export interface JsonNaN {
    readonly k: JsonNumericKind.NotANumber;
}

/** @internal */
export interface JsonNegativeInfinity {
    readonly k: JsonNumericKind.NegativeInfinity;
}

/** @internal */
export interface JsonNoneValue {
    readonly m?: ReadonlyArray<JsonRecordField>;
    readonly t: JsonType;
    readonly v: string;
}

/** @internal */
export interface JsonNullValue extends JsonPrimitiveValueBase<null> {
}

/** @internal */
export interface JsonNumber {
    readonly k: JsonNumericKind.Number;
    readonly v: number;
}

/** @internal */
export type JsonNumeric =
    | JsonNaN
    | JsonNegativeInfinity
    | JsonPositiveInfinity
    | JsonFormattedNumber
    | JsonNumber;

/** @internal */
export const enum JsonNumericKind {
    NotANumber = "NaN",
    NegativeInfinity = "NegativeInfinity",
    PositiveInfinity = "PositiveInfinity",
    FormattedNumber = "FormattedNumber",
    Number = "Number",
}

/** @internal */
export interface JsonNumericValue extends JsonPrimitiveValueBase<JsonNumeric> {
}

/** @internal */
export interface JsonPathEvaluationRequestWithAdditionalInfo<TAdditionalInfo> {
    readonly additionalInfo: TAdditionalInfo;
    readonly evaluationId: string;
    readonly evaluationParams: JsonPathEvaluationParams;
}

/** @internal */
export interface JsonPositiveInfinity {
    readonly k: JsonNumericKind.PositiveInfinity;
}

/** @internal */
export type JsonPrimitiveValue =
    | JsonBooleanValue
    | JsonNullValue
    | JsonNumericValue
    | JsonTextValue;

/** @internal */
export interface JsonPrimitiveValueBase<TValue> {
    readonly m?: ReadonlyArray<JsonRecordField>;
    readonly t: JsonType;
    readonly v: TValue;
}

/** @internal */
export interface JsonRecordField {
    readonly n: string;
    readonly v: JsonValue;
}

/** @internal */
export interface JsonRecordValue {
    readonly m?: ReadonlyArray<JsonRecordField>;
    readonly t: JsonType;
    readonly v: ReadonlyArray<JsonRecordField>;
}

/** @internal */
export interface JsonTableColumn {
    readonly n: string;
    readonly t: JsonType;
    readonly w?: boolean;
    readonly delayed?: boolean;
}

/** @internal */
export interface JsonTableInferenceGetInferredTableRequest {
    readonly existingStepNames: ReadonlyArray<string>;
    readonly localizedStrings: JsonTableInferenceLocalizedStrings;
    readonly mashupId: string;
    readonly queryName: string;
    readonly jsonSourceStep: string;
    readonly sessionId: string;
}

/** @internal */
export interface JsonTableInferenceGetInferredTableResponse {
    readonly success: boolean;
    readonly jsonNewSteps: ReadonlyArray<string>;
}

/** @internal */
export interface JsonTableInferenceLocalizedStrings {
    readonly convertedToList: string;
    readonly convertedToTable: string;
    readonly expandedFormatString: string;
    readonly source: string;
    readonly splitColumn: string;
    readonly transformColumns: string;
}

/** @internal */
export interface JsonTableKey {
    readonly c: ReadonlyArray<string>;
    readonly primary: boolean;
}

/** @internal */
export interface JsonTableType extends JsonType {
    readonly c?: ReadonlyArray<JsonTableColumn>;
    readonly k?: ReadonlyArray<JsonTableKey>;
    readonly cc: number;
}

/** @internal */
export interface JsonTableValue {
    readonly m?: ReadonlyArray<JsonRecordField>;
    readonly t: JsonTableType;
    readonly v: ReadonlyArray<ReadonlyArray<JsonValue>>;
}

/** @internal */
export interface JsonTextValue extends JsonPrimitiveValueBase<string> {
}

/** @internal */
export interface JsonTimeValue extends JsonPrimitiveValueBase<string> {
}

/** @internal */
export interface JsonType {
    readonly m?: {};
    readonly n: string;
}

/** @internal */
export interface JsonTypeValue {
    readonly t: JsonType;
    readonly v: string;
}

/** @internal */
export type JsonValue =
    | JsonActionValue
    | JsonBinaryValue
    | JsonDateValue
    | JsonDateTimeValue
    | JsonDateTimeZoneValue
    | JsonDelayedValue
    | JsonDurationValue
    | JsonFunctionValue
    | JsonListValue
    | JsonNoneValue
    | JsonPrimitiveValue
    | JsonRecordValue
    | JsonTableValue
    | JsonTimeValue
    | JsonTypeValue;

/** @internal */
export interface KeyCredential {
    readonly authKind: AuthenticationKind.Key;
    readonly key?: AuthCredentialValue;
}

/** @internal */
export interface KeyPairCredential {
    readonly authKind: AuthenticationKind.KeyPair;
    readonly privateKey?: AuthCredentialValue;
    readonly passphrase?: AuthCredentialValue;
}

/** @internal */
export interface LibrarySymbol {
    readonly name: string;
    readonly documentation?: LibrarySymbolDocumentation;
    readonly functionParameters?: ReadonlyArray<LibrarySymbolFunctionParameter>;
    readonly completionItemKind: number;
    readonly isDataSource: boolean;
    readonly type: string;
}

/** @internal */
export interface LibrarySymbolDocumentation {
    readonly description: string;
    readonly longDescription?: string;
}

/** @internal */
export interface LibrarySymbolError {
    readonly code: LibrarySymbolErrorCode;
    readonly failedColumns?: ReadonlyArray<LibrarySymbolErrorFailedColumn>;
    readonly index?: number;
}

/** @internal */
export enum LibrarySymbolErrorCode {
    FailedAfterMaxRetry = "FailedAfterMaxRetry",
    InvalidRowCount = "InvalidRowCount",
    JsonDeserializeIsNull = "JsonDeserializeIsNull",
    JsonSerializationException = "JsonSerializationException",
    RecordIsIncorrectType = "RecordIsIncorrectType",
    UnhandledException = "UnhandledException",
    UnexpectedEvaluationResultType = "UnexpectedEvaluationResultType",
}

/** @internal */
export interface LibrarySymbolErrorFailedColumn {
    readonly expectedType?: string;
    readonly columnName: string;
}

/** @internal */
export interface LibrarySymbolFunctionParameter {
    readonly name: string;
    readonly type: string;
    readonly isRequired: boolean;
    readonly isNullable: boolean;
    readonly caption?: string;
    readonly description?: string;
    readonly sampleValues: ReadonlyArray<number | string>;
    readonly allowedValues: ReadonlyArray<number | string>;
    readonly defaultValue: number | string;
    readonly fields?: ReadonlyArray<LibrarySymbolRecordField>;
    readonly enumNames?: ReadonlyArray<string>;
    readonly enumCaptions?: ReadonlyArray<string>;
}

/** @internal */
export interface LibrarySymbolRecordField {
    readonly name: string;
    readonly type: string;
    readonly isRequired: boolean;
    readonly caption?: string;
    readonly description?: string;
}

/** @internal */
export interface LibrarySymbolResponse {
    readonly librarySymbols: ReadonlyArray<LibrarySymbol>;
    readonly errors: ReadonlyArray<LibrarySymbolError>;
    readonly version: string;
}

/** @internal */
export interface LocaleInfo {
    readonly displayName: string;
    readonly name: string;
}

/** @internal */
export interface LocalizedStringDescriptor {
    readonly kind: StringDescriptorKind.LocalizedString;
    readonly value: string;
}

/** @public */
export interface ManageDataSourcesDialogSettings {
    /**
     * Gets or sets the ability to disable clear permissions for folder data source in the Manage Data Sources dialog.
     *
     * @defaultValue false.
     */
    readonly disableClearPermissionsForFolder?: boolean;
}

/** @internal */
export interface Mashup {
    readonly allowedModules: ReadonlyArray<string>;
    readonly allowNativeQueries: boolean;
    readonly connectionOverrides: ReadonlyArray<EvaluationConnection>;
    /**
     * @deprecated No longer used, evaluation does not allow auto anonymous.
     */
    readonly disableAutoAnonymousConnectionUpsert?: boolean;
    readonly documentLocale: string;
    readonly fastCombine: boolean;
    readonly gatewayObjectId: string | null | undefined;
    readonly hostContext: HostContext;
    readonly hostProperties: { [key: string]: string } | null | undefined;
    readonly id: string;
    readonly modifiedDateString: string;
    readonly m: string;
    readonly name: string;
    readonly parameterSetSettings?: ParameterSetSettings;
    readonly queryGroups: ReadonlyArray<QueryGroupEntity> | null | undefined;
    readonly skipAutomaticTypeAndHeaderDetection: boolean;
    readonly trustedConnections: ReadonlyArray<EvaluationConnection> | null | undefined;
    readonly useHostConnectionProvider: boolean;
    readonly defaultOutputDestinationSettings?: DefaultDestinationSettings;
    readonly stagingDefinition?: StagingDefinition;
}

/** @internal */
export interface MashupDataSource {
    readonly kind: MashupDataSourceKind;
    readonly normalizedPath: string;
    readonly path: string;
}

/** @internal */
export interface MashupDataSourceDiscovery {
    readonly discoveredDataSources: ReadonlyArray<MashupDataSource>;
    readonly unknownDataSourcesExist: boolean;
}

/** @internal */
export const enum MashupDataSourceKind {
    Access = "Access",
    ActiveDirectory = "ActiveDirectory",
    AdoDotNet = "AdoDotNet",
    AdobeAnalytics = "AdobeAnalytics",
    AnalysisServices = "AnalysisServices",
    AzureBlobs = "AzureBlobs",
    AzureDataLakeStorage = "AzureDataLakeStorage",
    AzureTables = "AzureTables",
    Cdpa = "CDPA",
    CurrentWorkbook = "CurrentWorkbook",
    DB2 = "DB2",
    Environment = "Environment",
    Essbase = "Essbase",
    Exchange = "Exchange",
    File = "File",
    Folder = "Folder",
    Ftp = "Ftp",
    GoogleAnalytics = "GoogleAnalytics",
    HDInsight = "HDInsight",
    Hdfs = "Hdfs",
    Informix = "Informix",
    MQ = "MQ",
    MicrosoftInformationProtection = "MicrosoftInformationProtection",
    MySql = "MySql",
    OData = "OData",
    Odbc = "Odbc",
    OleDb = "OleDb",
    Oracle = "Oracle",
    PostgreSQL = "PostgreSQL",
    PowerBIDataflow = "PowerBI",
    Python = "Python",
    R = "R",
    Salesforce = "Salesforce",
    SapBusinessWarehouse = "SapBusinessWarehouse",
    SapHana = "SapHana",
    SharePoint = "SharePoint",
    Spark = "Spark",
    Sql = "SQL",
    Staging = "Staging",
    Sybase = "Sybase",
    Teradata = "Teradata",
    Web = "Web",
}

/** @internal */
export interface MashupDataSourceReference {
    readonly dataSource: MashupDataSource;
    readonly dsrJson: string;
    /**
     * Gets or sets serialized DSR options record.
     *
     *  WARN: It might be empty "{}" when there are no options available or supported
     *   and It is `null` when the DSR is discovered without `shouldReportOptions` flag.
     */
    readonly options: string | null | undefined;
    readonly address: ReadonlyArray<MashupDataSourceReferenceAddressPart>;
    readonly coordinate: MashupDataSourceReferenceCoordinate | null | undefined;
}

/** @internal */
export interface MashupDataSourceReferenceAddressPart {
    readonly label: string;
    readonly name: string;
    readonly value: string;
}

/** @internal */
export interface MashupDataSourceReferenceCoordinate {
    readonly memberName: string;
}

/** @public */
export const enum MashupEditorAuthKind {
    Anonymous = "Anonymous",
    Windows = "Windows",
    OAuth2 = "OAuth2",
    Key = "Key",
    KeyPair = "KeyPair",
    Sas = "Sas",
    Basic = "Basic",
    ServicePrincipal = "ServicePrincipal",
    WorkspaceIdentity = "WorkspaceIdentity",
}

/** @internal */
export interface MashupEditResult<TResult> {
    readonly value: TResult;
    readonly editVersion: number;
}

/** @internal */
export interface MashupPatch {
    readonly name?: string;
    readonly gatewayObjectId?: string | null | undefined;
    readonly queryGroups?: ReadonlyArray<QueryGroupEntity>;
    readonly fastCombine?: boolean;
    readonly allowNativeQueries?: boolean;
    readonly documentLocale?: string;
    readonly queryMetadataPatches?: { [key: string]: QueryMetadataPatch };
    readonly skipAutomaticTypeAndHeaderDetection?: boolean;
}

/** @internal */
export const enum MashupTemplatePackagePartUri {
    TemplateMetadata = "Metadata.json",
    MashupDocument = "MashupDocument.pq",
    MashupMetadata = "MashupMetadata.json",
}

/** @internal */
export const enum MashupXmlVisitorErrorCode {
    GroupNestingLimitReached = "GroupNestingLimitReached",
    NullMashupXml = "NullMashupXml",
    Unknown = "Unknown",
}

/** @internal */
export interface MdmMetric {
    readonly name: string;
    readonly value: number;
    readonly dimensions: { [key: string]: string };
}

/** @internal */
export interface MergeQueriesMatchAnalysisRequest {
    readonly leftTableQueryName: string;
    readonly leftTableName: string;
    readonly mashupId: string;
    readonly leftTableColumnNames: ReadonlyArray<string>;
    readonly rightTableName: string;
    readonly rightTableColumnNames: ReadonlyArray<string>;
    readonly joinKind: string;
    readonly leftTableUsesStep: boolean;
    readonly useFuzzyMerge: boolean;
    readonly ignoreCase?: boolean;
    readonly ignoreSpace?: boolean;
    readonly numberOfMatches?: number;
    readonly threshold?: number;
    readonly transformationTable?: string;
}

/** @internal */
export interface MergeQueriesSuggestion {
    readonly leftTableColumnNames: ReadonlyArray<string>;
    readonly rightTableColumnNames: ReadonlyArray<string>;
    readonly suggestionScore: number;
}

/** @internal */
export interface MergeQueriesSuggestionRequest {
    readonly leftCandidateTableResult: SuggestionTableResult;
    readonly rightCandidateTableResult: SuggestionTableResult;
    readonly uiLocale: string;
}

/** @internal */
export interface MergeQueriesSuggestionResult {
    readonly mergeQueriesSuggestions: ReadonlyArray<MergeQueriesSuggestion>;
    readonly suggestionsComputationElapsedTimeInMs: number;
    readonly suggestionsVersion: string;
}

/** @internal */
export interface MessageTrace {
    readonly details: string;
    readonly message: string;
    readonly topic?: string;
    readonly tracingLevel: MessageTracingLevel;
}

/** @internal */
export const enum MessageTracingLevel {
    Critical = "Critical",
    Error = "Error",
    Warning = "Warning",
    Informational = "Informational",
    Verbose = "Verbose",
}

/** @internal */
export interface MetricsEventTrace {
    readonly name: string;
    readonly hostContextType: string;
    readonly details?: string;
    readonly correlationId?: string;
}

/** @internal */
export const enum MetricsOperationStatus {
    CompletedSuccessfully = "CompletedSuccessfully",
    CompletedWithExpectedError = "CompletedWithExpectedError",
    CompletedWithUnexpectedError = "CompletedWithUnexpectedError",
    Started = "Started",
}

/** @internal */
export interface MetricsOperationTrace {
    readonly name: string;
    readonly hostContextType: string;
    readonly status: MetricsOperationStatus;
    readonly durationMs?: number;
    readonly details?: string;
    readonly correlationId: string;
}

/** @internal */
export interface MigrateDataSourceResult {
    readonly modifiedQueries: ReadonlyArray<Query>;
}

/** @internal */
export interface MigrateFileToWebContentsResponse {
    readonly modifiedQueries: ReadonlyArray<Query>;
}

/** @public */
export interface ModernExperienceSwitchSettings {
    readonly initalSwitchValue: boolean;
    readonly learnMoreUrl: string;
}

/** @internal */
export const enum MoveQueriesRelativePosition {
    Above = "Above",
    Below = "Below",
}

/** @internal */
export interface MoveQueriesToGroupPosition {
    readonly relativePosition: MoveQueriesRelativePosition;
    readonly targetQueryName: string;
}

/** @internal */
export interface MScriptDefaultOutputDestinationDefinition {
    readonly kind: DefaultOutputDestinationDefinitionKind.MScript;
    readonly mScript: string;
}

/** @internal */
export interface NaturalLanguageCopilotFailureResponse {
    readonly success: false;
    readonly errorMessage: string;
    readonly requestId?: string;
    readonly jsonDebugInfo?: string;
}

/** @internal */
export interface NaturalLanguageCopilotRequest {
    readonly naturalLanguageInput: string;
    readonly currentQueryName?: string;
    readonly currentStepName?: string;
    readonly editingSessionId: string;
    readonly queriesInDocument: ReadonlyArray<CopilotSerializedQuery>;
    readonly includeDebugInfo?: boolean;
    readonly hostContext: HostContext;
    readonly uiLocale: string;
    readonly externalUserToken?: string;
}

/** @internal */
export type NaturalLanguageCopilotResponse =
    | NaturalLanguageCopilotSuccessResponse
    | NaturalLanguageCopilotFailureResponse;

/** @internal */
export interface NaturalLanguageCopilotStatusRequest {
    readonly hostContext: HostContext;
    readonly externalUserToken?: string;
}

/** @internal */
export interface NaturalLanguageCopilotSuccessResponse {
    readonly success: true;
    readonly copilotResponse: CopilotResponse;
    readonly requestId?: string;
    readonly jsonDebugInfo?: string;
}

/** @internal */
export interface NaturalLanguageStepScriptGenerationFailureResponse {
    readonly success: false;
}

/** @internal */
export type NaturalLanguageStepScriptGenerationResponse =
    | NaturalLanguageStepScriptGenerationSuccessResponse
    | NaturalLanguageStepScriptGenerationFailureResponse;

/** @internal */
export interface NaturalLanguageStepScriptGenerationSuccessResponse {
    readonly success: true;
    readonly stepScript: string;
    readonly stepPattern: MashupScriptPattern;
    readonly stepExplanation?: string;
}

/** @internal */
export interface NavigationPreviewRequestAdditionalInfo {
    readonly jsonNavigationInfoArray: string;
    readonly isCubeMode: boolean;
    readonly useCubeDisplayFoldersTransform: boolean | null | undefined;
    readonly useFileSystemNavigationTableTransform?: boolean;
}

/** @internal */
export interface NavigationRecentItemNode {
    /**
     * Gets or sets the unique identifier for the recent item node.
     */
    readonly id: RecentItemNodeId;
    /**
     * Gets or sets the contract version number.
     */
    readonly version: number;
    /**
     * Gets or sets the query script associated with this node.
     */
    readonly queryScript: string;
    /**
     * Gets or sets the link kind for this node.
     */
    readonly linkKind: RecentItemNodeLinkKind | null | undefined;
    /**
     * Gets or sets the last accessed timestamp as an ISO string.
     */
    readonly lastAccessedAt: string;
    /**
     * Gets or sets the array of data access modes used with this node.
     * Defaults to [Read] if not specified (for backward compatibility).
     */
    readonly dataAccessModes?: ReadonlyArray<DataAccessMode>;
    /**
     * Gets or sets whether this item is marked as a favorite.
     * Null or false means the item is not favorited.
     */
    readonly isFavorite?: boolean | null | undefined;
    /**
     * Gets the kind of this node, which is always Navigation.
     */
    readonly kind: RecentItemNodeKind.Navigation;
    /**
     * Gets or sets the display label for this navigation node.
     */
    readonly label: string;
    /**
     * Gets or sets the parent node identifier.
     */
    readonly parentId: RecentItemNodeId;
}

/** @public */
export interface NavigatorSettings {
    readonly cancelButtonLabel?: string;
    readonly commitButtonLabel?: string;
    /**
     * Disables the Add Web Tables using Example functionality in the Navigator.
     *
     * @defaultValue false
     */
    readonly disableAddWebTableUsingExamples?: boolean;
    /**
     * Disables multi-selection in the Navigator.
     */
    readonly disableMultiSelection?: boolean;
    /**
     * Shows the Commit button even when the Next button is visible.
     */
    readonly enableCommitWhenNextIsEnabled?: boolean;
    readonly nextButtonLabel?: string;
}

/** @public */
export interface NewFabricArtifactModuleSettings {
    /**
     * Gets or sets the array of allowed external artifact get data setting items.
     * For the default empty array, it would support all fabric artifact kinds.
     */
    readonly allowedFabricArtifactKinds: ReadonlyArray<FabricArtifactKind>;
    /**
     * Gets or sets a value indicating whether NewFabricArtifactModule will show the workspace selection.
     * defaultValue false.
     */
    readonly hideWorkspaceSelection?: boolean;
    /**
     * Gets or sets a value indicating whether NewFabricArtifactModule will avoid creating connection for artifacts created.
     * defaultValue false.
     *     When it's disabled, it would invoke DataDiscoveryCallbacks.onFabricItemCreated(newFabricItem).
     *     When it's enabled, it would create a new connection as well and commit the flow.
     */
    readonly disableConnectionCreation?: boolean;
}

/** @internal */
export interface NormalizedPathResponse {
    readonly resourceKind: string;
    readonly normalizedPath?: string;
}

/** @internal */
export type OperationResult =
    | SuccessOperationResult
    | FailureOperationResult;

/** @internal */
export interface OptionalValue<T> {
    readonly hasValue: boolean;
    readonly value?: T;
}

/** @alpha */
export interface OptionsDialogSettings {
    /**
     * Gets or sets a value indicating whether usage quota diagnostics can be seen in the options dialog.
     *
     * @defaultValue true.
     * @alpha .
     */
    readonly enableUsageQuotaDiagnostics?: boolean;
    /**
     * Gets or sets a value indicating whether export diagnostics button can be seen in the options dialog.
     *
     * @defaultValue false.
     */
    readonly disableExportDiagnostics?: boolean;
}

/** @internal */
export type OutputDestinationAutoFixer =
    | OutputDestinationRegexAutoFixer;

/** @internal */
export const enum OutputDestinationAutoFixerKind {
    Regex = "Regex",
}

/** @internal */
export interface OutputDestinationConfiguration {
    readonly updateMethod: UpdateMethod;
    readonly columnMappings: ColumnMappings;
    readonly destination: DestinationDefinition;
    readonly destinationTransformQueryName?: string;
    readonly partitioningColumnNames?: ReadonlyArray<string>;
}

/** @internal */
export interface OutputDestinationParameterMetadata {
    readonly name: string;
    readonly labelDescriptor: StringDescriptor;
    readonly typeName: TypeName;
    readonly isTargetIdentifier: boolean;
    readonly isCaseSensitive: boolean;
    readonly isHidden: boolean;
    readonly autoFixer?: OutputDestinationAutoFixer;
    readonly defaultValue?: string;
    readonly validationRule?: OutputDestinationValidationRule;
    readonly maxLength?: number;
}

/** @internal */
export interface OutputDestinationProperties {
    readonly isPreview: boolean;
    readonly tableOutput?: TableOutputProperties;
    readonly fileOutput?: FileOutputProperties;
    readonly canBeUsedAsDefaultDestination: boolean;
    readonly supportsIncrementalRefresh: boolean;
    readonly requiresStaging: boolean;
}

/** @internal */
export interface OutputDestinationRegexAutoFixer {
    readonly kind: OutputDestinationAutoFixerKind.Regex;
    readonly pattern: string;
    readonly replacementString: string;
}

/** @internal */
export interface OutputDestinationRegexValidationRule {
    readonly kind: OutputDestinationValidationRuleKind.Regex;
    readonly pattern: string;
}

/** @internal */
export interface OutputDestinations {
    readonly destinations: ReadonlyArray<OutputDestinationConfiguration>;
}

/** @internal */
export type OutputDestinationValidationRule = (
    | OutputDestinationRegexValidationRule
)
& {
    readonly errorMessageDescriptor: StringDescriptor;
}

/** @internal */
export const enum OutputDestinationValidationRuleKind {
    Regex = "Regex",
}

/** @internal */
export interface OutputFileFormatSettings {
    readonly pattern: InvocationPattern;
    readonly navigationInfos?: ReadonlyArray<NavigationInfo>;
}

/** @alpha */
export interface PaneSettings {
    /**
     * If true, the pane will be hidden and inaccessible.
     *
     * @defaultValue false.
     */
    readonly hidePane?: boolean;
    /**
     * Shows the pane collapsed.
     *
     * @defaultValue false.
     */
    readonly showCollapsed?: boolean;
}

/** @internal */
export interface ParameterQueryPatchRequest {
    readonly currentQueryName: string;
    readonly newQueryName?: string;
    readonly newDescription?: string;
    readonly newJsonPattern?: string;
}

/** @internal */
export const enum ParityDistributionValue {
    Even = "Even",
    Odd = "Odd",
}

/** @internal */
export interface PasteQueriesRequest {
    readonly clipboardXmlContent: string;
    readonly mashupId: string;
    readonly queryLoadEnabledOverride?: boolean;
    readonly targetQueryGroupId?: string | null | undefined;
}

/** @internal */
export interface PasteQueriesResult {
    readonly addedQueryGroups: ReadonlyArray<QueryGroupEntity>;
    readonly addedQueries: ReadonlyArray<Query>;
}

/** @internal */
export interface PotentiallyIncompleteListResult<T> {
    readonly list: ReadonlyArray<T>;
    readonly isComplete: boolean;
}

/** @internal */
export interface PreventableDefaultBehaviourHandlerResult {
    readonly preventDefault: boolean;
}

/** @internal */
export interface PreviewQueryScriptRequest {
    readonly hostContext: HostContext;
    readonly sessionId: string;
    readonly mashupId: string;
    readonly queryScript: string;
    readonly previewSamplePolicy: PreviewSamplePolicy;
    readonly numberOfRows: number;
    readonly queryName: string;
    readonly evaluationId?: string;
    readonly locale?: string;
    readonly forceRefresh: boolean;
    readonly allowModernEvaluationEngine: boolean;
    readonly workspaceVariables?: string;
}

/** @internal */
export interface PreviewQueryScriptWithConnectionsRequest {
    readonly hostContext: HostContext;
    readonly sessionId: string;
    readonly queryScript: string;
    readonly previewSamplePolicy: PreviewSamplePolicy;
    readonly numberOfRows: number;
    readonly queryName: string;
    readonly connections: ReadonlyArray<EvaluationConnection>;
    readonly evaluationId?: string;
    readonly locale?: string;
    readonly forceRefresh: boolean;
    readonly allowModernEvaluationEngine: boolean;
    readonly workspaceVariables?: string;
    readonly mashupDocument?: string;
}

/** @internal */
export const enum PreviewSamplePolicy {
    TopRows = "TopRows",
}

/** @internal */
export interface PreviousAgenticSessionMessage {
    readonly content: string;
    /**
     * The chat role of this message. Allowed values are "user" and "assistant".
     */
    readonly role: string;
}

/** @public */
export const enum PrivacyGroup {
    None = "None",
    Private = "Private",
    Organizational = "Organizational",
    Public = "Public",
}

/** @internal */
export interface PublishDataflowRequest {
    readonly dataflowId: Guid;
    readonly displayName: string;
    readonly workspaceObjectId: Guid;
    readonly mashupId: Guid;
}

/** @internal */
export interface PublishDataflowResult {
    readonly success: boolean;
    readonly dataflowObjectId?: Guid;
    readonly errorMessage?: string;
}

/** @internal */
export interface PublishToDataflowCreateRequest {
    readonly capacityObjectId: string;
    readonly dataflowDisplayName: string;
    readonly dataflowDescription: string;
    readonly lakehouseObjectId: string;
    readonly workspaceObjectId: string;
    readonly mashupDocument: string;
    readonly tableNames: ReadonlyArray<string>;
}

/** @internal */
export interface PublishToDataflowCreateResponse {
    readonly dataflowObjectId: string;
}

/** @internal */
export interface PublishToDataflowDeleteRequest {
    readonly workspaceObjectId: string;
    readonly dataflowObjectId: string;
}

/** @internal */
export enum PublishToDataflowErrorCode {
    BadRequest = "BadRequest",
    EntityNotFound = "EntityNotFound",
    ItemDisplayNameAlreadyInUse = "ItemDisplayNameAlreadyInUse",
    NonSuccessStatusCode = "NonSuccessStatusCode",
    NullJson = "NullJson",
    NullMashup = "NullMashup",
    UnknownBackendTarget = "UnknownBackendTarget",
    UnknownError = "UnknownError",
}

/** @internal */
export interface PublishToDataflowPublishRequest {
    readonly dataflowObjectId: string;
    readonly workspaceObjectId: string;
}

/** @alpha */
export interface QueryConnectivityMode {
    readonly id: string;
    readonly label: string;
    readonly requirements: ReadonlyArray<QueryConnectivityModeRequirement>;
}

/** @alpha */
export interface QueryConnectivityModeDataSourceSupport {
    readonly dataSource: DataSourceOptionIdentifier;
    readonly supportedModeIds: ReadonlyArray<string>;
    readonly defaultModeId?: string;
}

/** @alpha */
export interface QueryConnectivityModeRequirement {
    readonly validationRule: QueryConnectivityModeValidationRule;
    readonly errorMessage: string;
    readonly fix?: QueryConnectivityModeValidationErrorFix;
}

/** @alpha */
export interface QueryConnectivityModeSettings {
    readonly allowedModes: ReadonlyArray<QueryConnectivityMode>;
    readonly requireAllQueriesUseSameMode: boolean;
    /**
     * Gets or sets definition of which data sources supports which of the allowed query connectivity modes.
     */
    readonly dataSourceSupportList: ReadonlyArray<QueryConnectivityModeDataSourceSupport>;
    /**
     * Gets or sets the information to be displayed in the tooltip for the connectivity mode picker.
     */
    readonly infoTooltip?: string;
}

/** @alpha */
export type QueryConnectivityModeValidationErrorFix =
    | ChangeQueryConnectivityModes;

/** @alpha */
export const enum QueryConnectivityModeValidationErrorFixKind {
    ChangeModes = "ChangeModes",
}

/** @alpha */
export const enum QueryConnectivityModeValidationRule {
    QueryNeedsToFold = "QueryNeedsToFold",
}

/** @internal */
export interface QueryDatabaseOutputSettings {
    readonly kind: QueryOutputDestinationKind.Database;
    readonly columnMappings: ColumnMappings;
    readonly targetDefinition: TargetDefinition;
    readonly targetResourceIdentifier: TargetResourceIdentifier;
    readonly tableUpdateSettings: DatabaseTableUpdateSettings;
}

/** @internal */
export interface QueryDetails {
    readonly queryDescription?: string;
    readonly queryName: string;
    readonly queryScript: string;
}

/** @public */
export interface QueryEditorHelpButton {
    readonly description: string;
    readonly label: string;
    readonly url: string;
}

/** @public */
export interface QueryEditorHelpSettings {
    /**
     * URL to the help page for add custom column.
     */
    readonly addCustomColumnUrl?: string;
    /**
     * URL to the help page for add index column.
     */
    readonly addIndexColumnUrl?: string;
    /**
     * URL to the help page for append queries.
     */
    readonly appendUrl?: string;
    /**
     * URL to the help page for change type with locale.
     */
    readonly changeTypeWithLocaleUrl?: string;
    /**
     * URL to the relevant PQ community.
     */
    readonly communityUrl?: string;
    /**
     * URL to the help page for conditional column.
     */
    readonly conditionalColumnUrl?: string;
    /**
     * URL to the help page for group by.
     */
    readonly groupByUrl?: string;
    /**
     * Host-specific Blog button.
     */
    readonly hostBlogButton?: QueryEditorHelpButton;
    /**
     * Host-specific Community button.
     */
    readonly hostCommunityButton?: QueryEditorHelpButton;
    /**
     * Host-specific Documentation button.
     */
    readonly hostDocumentationButton?: QueryEditorHelpButton;
    /**
     * URL to the help page for manage parameters.
     */
    readonly manageParametersUrl?: string;
    /**
     * URL to the help page for merge queries.
     */
    readonly mergeQueriesUrl?: string;
    /**
     * URL to the help page for pivot column.
     */
    readonly pivotColumnUrl?: string;
    /**
     * URL to the help page for replace values.
     */
    readonly replaceValuesUrl?: string;
    /**
     * URL to the help page for split column.
     */
    readonly splitColumnUrl?: string;
    /**
     * URL to submit an idea for the host.
     */
    readonly submitAnIdeaUrl?: string;
    /**
     * URL for support for the host.
     */
    readonly supportUrl?: string;
}

/** @public */
export interface QueryEditorMaxNumberOfQueriesSettings {
    /**
     * Gets or sets the maximum number of queries that the Query Editor supports.
     */
    readonly maxNumberOfQueries: number;
    /**
     * Gets or sets the warning message to display when the number of queries exceeds maxNumberOfQueries.
     */
    readonly warningMessage: string;
}

/** @public */
export interface QueryEditorSettingsBase {
    /**
     * The label for the Cancel button in the Query Editor.
     */
    readonly cancelButtonLabel?: string;
    /**
     * Upon clicking on the Close button in the Query Editor, the behavior will be the following:
     * - if showConfirmationPrompt is true:
     *    - if actionOnConfirm is Ask:
     *       - a message dialog will show up, asking "Do you want to save your changes?"
     *       - in this message dialog:
     *          - clicking on the Save button will commit the Mashup Editor flow
     *          - clicking on the Discard button will cancel the Mashup Editor flow
     *          - clicking on the Cancel button will just dismiss the message dialog
     *    - if actionOnConfirm is AlwaysCommit:
     *       - a message dialog will show up, asking "Are you sure you want to exit?"
     *       - in this message dialog:
     *          - clicking on the Yes button will commit the Mashup Editor flow
     *          - clicking on the No button will just dismiss the message dialog
     *    - if actionOnConfirm is AlwaysCancel:
     *       - a message dialog will show up, asking "Are you sure you want to exit?"
     *       - in this message dialog:
     *          - clicking on the Yes button will cancel the Mashup Editor flow
     *          - clicking on the No button will just dismiss the message dialog
     * - if showConfirmationPrompt is false:
     *    - if actionOnClick is Commit:
     *       - the Mashup Editor flow will be committed
     *    - if actionOnClick is Cancel:
     *       - the Mashup Editor flow will be cancelled
     */
    readonly closeButtonSettings?: CloseButtonSettings;
    /**
     * The label for the Commit button in the Query Editor.
     */
    readonly commitButtonLabel?: string;
    /**
     * Settings for default output destination in the Query Editor.
     *
     * This property can be used by hosts to customize the behavior of the default destination feature.
     * For instance, hosts can now specify a target to use as default destination by providing a DSR for it.
     *
     * @alpha
     */
    readonly defaultOutputDestinationSettings?: DefaultOutputDestinationSettings;
    /**
     * Disables the Add Cell as New Query command in the Query Editor.
     */
    readonly disableAddCellAsNewQuery?: boolean;
    /**
     * Disables the Add Column as New Query command in the Query Editor.
     */
    readonly disableAddColumnAsNewQuery?: boolean;
    /**
     * Disables the Add Column by Example command in the Query Editor.
     *
     * @defaultValue false
     */
    readonly disableAddColumnByExample?: boolean;
    /**
     * Disables the Append Queries as New command in the Query Editor.
     *
     * @defaultValue false
     */
    readonly disableAppendQueriesAsNew?: boolean;
    /**
     * Disables the ability to copy/paste queries in the Query Editor.
     *
     * @defaultValue false
     */
    readonly disableCopyPasteQueries?: boolean;
    /**
     * Disables the ability to create a function from a query in the Query Editor.
     *
     * @defaultValue false
     */
    readonly disableCreateFunction?: boolean;
    /**
     * Disables the Diagram View in the Query Editor.
     *
     * @defaultValue false
     */
    readonly disableDiagramView?: boolean;
    /**
     * Disables the Duplicate Query command in the Query Editor.
     *
     * @defaultValue false
     */
    readonly disableDuplicateQuery?: boolean;
    /**
     * Disables the Enter Data command in the Query Editor.
     *
     * @defaultValue false
     */
    readonly disableEnterData?: boolean;
    /**
     * Disables Excel Table Inference when connecting to an Excel data source.
     *
     * @defaultValue false
     */
    readonly disableExcelTableInference?: boolean;
    /**
     * Disables the Export Diagnostics command in the Query Editor.
     */
    readonly disableExportDiagnostics?: boolean;
    /**
     * Disables the Export Template Ribbon button.
     */
    readonly disableExportTemplate?: boolean;
    /**
     * Disables the Extract Previous Steps command in the Query Editor.
     */
    readonly disableExtractPreviousSteps?: boolean;
    /**
     * Disables the footer in the Query Editor stage only.
     *
     * @defaultValue false
     */
    readonly disableFooter?: boolean;
    /**
     * Disables Fuzzy Operations in the Query Editor.
     *
     * @defaultValue false
     */
    readonly disableFuzzyOperations?: boolean;
    /**
     * Disables Get Data commands in the Query Editor.
     *
     * @defaultValue false
     */
    readonly disableGetData?: boolean;
    /**
     * Disables the Import Template link.
     */
    readonly disableImportTemplate?: boolean;
    /**
     * Disables the Invoke Custom Function command in the Query Editor.
     */
    readonly disableInvokeCustomFunction?: boolean;
    /**
     * Disables Json Table Inference when connecting to a Json data source.
     *
     * @defaultValue false
     */
    readonly disableJsonTableInference?: boolean;
    /**
     * Disables the Merge Queries as New command in the Query Editor.
     *
     * @defaultValue false
     */
    readonly disableMergeQueriesAsNew?: boolean;
    /**
     * Disables the Manage Parameters, Edit Parameters and New Parameter entry points in the UI.
     */
    readonly disableParameterManagement?: boolean;
    /**
     * Disables the UI that allows to select a parameter query as an argument.
     */
    readonly disableParameterQueryArguments?: boolean;
    /**
     * Disables the ability to paste queries in the Query Editor.
     *
     * @defaultValue false
     */
    readonly disablePasteQueries?: boolean;
    /**
     * Disables query analysis.
     *
     * @defaultValue false
     */
    readonly disableQueryAnalysis?: boolean;
    /**
     * Disables the UI that allows to edit query properties and query group properties.
     *
     * @defaultValue false
     */
    readonly disableQueryAndQueryGroupProperties?: boolean;
    /**
     * Disables the ability to create query groups, and to to move queries and query groups into query groups.
     *
     * @defaultValue false
     */
    readonly disableQueryGroupManagement?: boolean;
    /**
     * Disables the option to Enable/Disable Load in the Query Editor.
     *
     * @defaultValue false
     */
    readonly disableQueryLoad?: boolean;
    /**
     * Disables the query plan graph inspector UI.
     *
     * @defaultValue false
     */
    readonly disableQueryPlanGraph?: boolean;
    /**
     * Hides from the Status Bar the buttons that allow to switch between query visualization modes (Data View and
     * Schema View).
     *
     * @defaultValue false
     */
    readonly disableQueryVisualizationModeButtonsInStatusBar?: boolean;
    /**
     * Disables the Quick Access command in the Query Editor.
     *
     * @defaultValue false
     */
    readonly disableQuickAccess?: boolean;
    /**
     * Disables the Reference Query command in the Query Editor.
     *
     * @defaultValue false
     */
    readonly disableReferenceQuery?: boolean;
    /**
     * Disables the UI that allows to select a regular query (a non-parameter query) as an argument.
     */
    readonly disableRegularQueryArguments?: boolean;
    /**
     * Disables the relative Date/Time filters in the Filter dropdown menu and in the Filter Rows dialog's Operator
     * dropdown:
     *  - Year
     *    - Next year
     *    - This year
     *    - Last year
     *    - Year to date
     *  - Quarter
     *    - Next quarter
     *    - This quarter
     *    - Last quarter
     *  - Month
     *    - Next month
     *    - This month
     *    - Last month
     *  - Week
     *    - Next week
     *    - This week
     *    - Last week
     *  - Day
     *     - Tomorrow
     *     - Today
     *     - Yesterday
     *  - Hour
     *    - Next hour
     *    - This hour
     *    - Last hour
     *  - Minute
     *    - Next minute
     *    - This minute
     *    - Last minute
     *  - Second
     *    - Next second
     *    - This second
     *    - Last second
     */
    readonly disableRelativeDateTimeFilters?: boolean;
    /**
     * Disables all script editors in the Query Editor (Advanced Editor, Formula Bar, Query Script Pane, Add Custom
     * Column).
     */
    readonly disableScriptEditors?: boolean;
    /**
     * Disables step analysis. Hosts that use SPARK-based compute (like Synapse) may have incorrect step analysis
     * results. So, such hosts must disable step analysis.
     *
     * @defaultValue false
     */
    readonly disableStepAnalysis?: boolean;
    /**
     * Disables the Text/CSV By Example experience when connecting to a Text/CSV data source.
     *
     * @defaultValue false
     */
    readonly disableTextCsvByExample?: boolean;
    /**
     * Disables the ability to toggle the visibility of the Diagram View.
     *
     * @defaultValue false
     */
    readonly disableToggleDiagramViewVisibility?: boolean;
    /**
     * Enables the concept of calculated entities in the Power Query experience.
     *
     * @defaultValue false
     */
    readonly enableCalculatedEntities?: boolean;
    /**
     * Enables the Command Bar in the Query Editor.
     *
     * @defaultValue false
     */
    readonly enableCommandBar?: boolean;
    /**
     * Enables the popup button in the Command Bar in the Query Editor.
     *
     * @defaultValue false
     */
    readonly enableCommandBarPopupButton?: boolean;
    /**
     * Enables the Command Bar in the Diagram View.
     *
     * @defaultValue false
     */
    readonly enableDiagramViewCommandBar?: boolean;
    /**
     * Enables the popup button in the Command Bar in the Diagram View.
     *
     * @defaultValue false
     */
    readonly enableDiagramViewCommandBarPopupButton?: boolean;
    /**
     * Enables the Fabric AI Prompt Column Builder.
     *
     * @defaultValue false
     */
    readonly enableFabricAIPromptColumnBuilder?: boolean;
    /**
     * Enables the try grant access dialog in configure connection.
     *
     * @defaultValue false
     */
    readonly enableGrantAccessDialog?: boolean;
    /**
     * Enables the concept of linked entities in the Power Query experience.
     *
     * @defaultValue false
     */
    readonly enableLinkedEntities?: boolean;
    /**
     * Enables the Open in Excel button in the Query Editor.
     *
     * @defaultValue false
     */
    readonly enableOpenInExcelButton?: boolean;
    /**
     * Enables the preview-only steps in the Query Editor.
     *
     * @defaultValue false
     */
    readonly enablePreviewOnlySteps?: boolean;
    /**
     * Enables writing data to output destinations in the Query Editor.
     *
     * @defaultValue false
     */
    readonly enableQueryOutputDestinations?: boolean;
    /**
     * When true, the editor will enable specific UIs (context menu entry, query property control) that allow users to set queries as staged and to have visibility over staged queries in the document.
     *
     * @defaultValue false
     *
     * @alpha
     */
    readonly enableQueryStaging?: boolean;
    /**
     * Enables the query sanitization in the Query Editor.
     *
     * @defaultValue false
     */
    readonly enableQuerySanitization?: boolean;
    /**
     * Enables the Quick Query sharing options in the UI.
     *
     * @defaultValue false
     */
    readonly enableQuickQuerySharing?: boolean;
    /**
     * Enables the Share as URL functionality of the Query Editor.
     * If set to true, the host should also provide a generateShareUrl callback in QueryEditorCallbacks.
     *
     * @defaultValue false
     */
    readonly enableShareAsUrl?: boolean;
    /**
     * Enables the Welcome Pane experience when there are no queries in the mashup.
     *
     * @defaultValue false
     */
    readonly enableWelcomePane?: boolean;
    /**
     * Enables the Query Library experience in the Query Editor.
     *
     * @defaultValue false
     */
    readonly enableQueryLibrary?: boolean;
    /**
     * Allows the host to customize the URLs of the buttons in the Help tab in the Ribbon.
     */
    readonly helpSettings?: QueryEditorHelpSettings;
    /**
     * Hides the Options in the Query Editor.
     */
    readonly hideOptions?: boolean;
    /**
     * Hides the Ribbon in the Query Editor.
     *
     * @deprecated Use `ribbonSettings.hide` instead.
     */
    readonly hideRibbon?: boolean;
    /**
     * Hides the Status Bar in the Query Editor.
     */
    readonly hideStatusBar?: boolean;
    /**
     * Prevents committing a mashup with no load-enabled queries.
     *
     * @defaultValue false
     */
    readonly preventCommittingMashupWithNoLoadEnabledQueries?: boolean;
    /**
     * Shows "Refresh all" command in the ribbon.
     */
    readonly showRefreshAllQueriesCommand?: boolean;
    /**
     * Shows the send feedback button in the Get Data Wizard and Query Editor headers.
     *
     * @deprecated Use <see cref = "SendFeedbackSettings"/> instead. When <see cref = "SendFeedbackSettings"/> is provided, this setting is ignored.
     * @defaultValue false
     */
    readonly showSendFeedbackButtonInHeaders?: boolean;
    /**
     * Configuration settings for the send feedback feature.
     * Controls which feedback options are available.
     *
     * @alpha
     */
    readonly sendFeedbackSettings?: SendFeedbackSettings;
    /**
     * Settings for query output destinations in the Query Editor.
     *
     * This property can be used by hosts to customize the behavior of the query output destination feature.
     * For instance, hosts can now control what connectors can be used as output connectors.
     *
     * @alpha
     */
    readonly queryOutputDestinationSettings?: QueryOutputDestinationSettings;
    /**
     * Contains readonly info for the queries.
     * @alpha
     */
    readonly queryReadonlyInfoByQueryId?: { [key: string]: QueryReadonlyInfo };
    /**
     * Buttons that will appear in the Ribbon used to close the Query Editor.
     * @alpha
     */
    readonly ribbonCloseCommands?: ReadonlyArray<HostCommand>;
    /**
     * Settings for the validation UI in the Query Editor.
     */
    readonly validationSettings?: QueryEditorValidationSettings;
    /**
     * Settings for the host defined query properties UI.
     *
     * This can be used to display UI to control host-specific properties which will be persisted
     *  under QueryMetadata.hostProperties
     *
     * @alpha
     */
    readonly hostQueryPropertiesSettings?: HostQueryPropertiesSettings;
    /**
     * @alpha
     */
    readonly optionsDialogSettings?: OptionsDialogSettings;
    /**
     * Settings for the validation of number of queries in Query Editor.
     */
    readonly maxNumberOfQueriesSettings?: QueryEditorMaxNumberOfQueriesSettings;
    /**
     * Optional Settings for the Queries Tree Pane.
     *
     * @alpha
     */
    readonly queriesPaneSettings?: PaneSettings;
    /**
     * Optional Settings for the ribbon pane.
     *
     * @alpha
     */
    readonly ribbonSettings?: RibbonSettings;
    /**
     * Controls the visibility of the Cancel button in the Query Editor.
     */
    readonly showCancelButton?: boolean;
    /**
     * Optional Settings for the Query Settings Pane.
     *
     * @alpha
     */
    readonly stepsPaneSettings?: StepsPaneSettings;
}

/** @public */
export interface QueryEditorValidationRules {
    readonly disallowAnyTypeColumnsInLoadEnabledQueries?: boolean;
    readonly disallowColumnNamesWithLeadingOrTrailingSpacesInLoadEnabledQueries?: boolean;
    readonly disallowDuplicateColumnNamesInLoadEnabledQueries?: boolean;
    readonly disallowEmptyColumnNamesInLoadEnabledQueries?: boolean;
    readonly disallowNonFoldingLoadEnabledQueries?: boolean;
    readonly disallowNonPrimitiveColumnsInLoadEnabledQueries?: boolean;
    readonly disallowNonTableLoadEnabledQueries?: boolean;
    readonly requireExactlyOneLoadEnabledQuery?: boolean;
    readonly requireMappingStepInLoadEnabledQueries?: boolean;
}

/** @public */
export interface QueryEditorValidationSettings {
    /**
     * Enables a confirmation dialog on commit if any validation messages are present.
     *
     * @defaultValue false
     */
    readonly enableConfirmationDialog?: boolean;
    readonly rules?: QueryEditorValidationRules;
    readonly strings?: QueryEditorValidationStrings;
}

/** @public */
export interface QueryEditorValidationStrings {
    /**
     * The string must contain a "\{queryName\}" placeholder.
     * Example:
     *
     *    The query "\{queryName\}" is not supported as a Data Mart SQL view, since it cannot be fully translated to SQL.
     */
    readonly nonFoldingLoadEnabledQuery?: string;
}

/** @internal */
export interface QueryFileOutputSettings {
    readonly kind: QueryOutputDestinationKind.File;
    readonly columnMappings: ColumnMappings;
    readonly outputFileFormatSettings: OutputFileFormatSettings;
    readonly targetDefinition: TargetDefinition;
    readonly targetResourceIdentifier: TargetResourceIdentifier;
}

/** @public */
export interface QueryGroupEntity {
    readonly id: string;
    readonly name: string;
    readonly description: string | null | undefined;
    readonly parentId: string | null | undefined;
    readonly order: number;
}

/** @internal */
export interface QueryInspection {
    readonly allDependentQueryNames: ReadonlyArray<string>;
    readonly allReferencedQueryNames: ReadonlyArray<string>;
    readonly directDependentQueryNames: ReadonlyArray<string>;
    readonly directReferencedQueryNames: ReadonlyArray<string>;
    readonly queryName: string;
    readonly queryScript: string;
}

/** @internal */
export interface QueryLibrary {
    readonly queryLibraryId: string;
    readonly queryLibraryName: string;
    readonly mashup: HostContextAgnosticEditingSessionMashup;
}

/** @internal */
export enum QueryLibraryErrorCode {
    FailedToCreateQueryLibrary = "FailedToCreateQueryLibrary",
    FailedToGetDataflowsInWorkspace = "FailedToGetDataflowsInWorkspace",
    FailedToGetDocumentState = "FailedToGetDocumentState",
    FailedToGetFoldersInWorkspace = "FailedToGetFoldersInWorkspace",
    FailedToGetWorkspaces = "FailedToGetWorkspaces",
    FailedToTransferQueries = "FailedToTransferQueries",
    FailedToUpdateQueryLibrary = "FailedToUpdateQueryLibrary",
    WorkspaceIdNotFound = "WorkspaceIdNotFound",
}

/** @internal */
export interface QueryLibraryQuery {
    readonly artifactId: string;
    readonly artifactName: string;
    readonly artifactType: string;
    readonly lastUsedTime?: string | null | undefined;
    readonly queryLibraryQueryId: string;
    readonly queryLibraryQueryLocation: QueryLibraryQueryLocation;
    readonly queryName: string;
    readonly queryScript: string;
    readonly userId: string;
}

/** @internal */
export interface QueryLibraryQueryLocation {
    readonly dataflowDisplayName: string;
    readonly dataflowId: string;
    readonly queryName: string;
    readonly workspaceDisplayName: string;
    readonly workspaceId: string;
}

/** @internal */
export interface QueryLibraryQueryWithReferences {
    readonly mainQuery: QueryLibraryQuery;
    readonly referencedQueries: ReadonlyArray<QueryLibraryQuery>;
}

/** @internal */
export interface QueryMetadata {
    readonly loadEnabled: boolean;
    readonly queryGroupId: string | null | undefined;
    readonly queryId: Guid;
    readonly queryName: string;
    readonly hostProperties: { [key: string]: string };
    readonly entityName?: string;
    readonly lastKnownIsCalculatedEntity?: boolean;
    readonly lastKnownIsLinkedEntity?: boolean;
    readonly lastKnownIsParameter?: boolean;
    readonly isHidden?: boolean;
    readonly lastKnownResultTypeName: TypeName | null | undefined;
    readonly fieldsMetadata?: { [key: string]: FieldMetadata };
    readonly deleteExistingDataOnLoad: boolean;
    readonly outputDestinations?: OutputDestinations;
    readonly bindToDefaultOutputDestination?: boolean;
    /**
     * @deprecated Use IncrementalRefreshSettingsActions to get the settings.
     */
    readonly incrementalRefreshSettings?: IncrementalRefreshSettings;
    /**
     * @deprecated Use StagingDefinitionActions to get the settings.
     */
    readonly stagingDefinition?: StagingDefinition;
}

/** @internal */
export interface QueryMetadataPatch {
    readonly entityName?: string;
    readonly fieldsMetadata?: { [key: string]: FieldMetadata };
    readonly loadEnabled?: boolean;
    readonly deleteExistingDataOnLoad?: boolean;
    readonly hostProperties?: { [key: string]: string };
    readonly queryGroupId?: string;
}

/** @internal */
export interface QueryModelDefaultOutputDestinationDefinition {
    readonly kind: DefaultOutputDestinationDefinitionKind.QueryModel;
    readonly jsonQueryModel: string;
}

/** @internal */
export interface QueryNameGenerationSettings {
    readonly disambiguator?: NameDisambiguatorType;
}

/** @alpha */
export interface QueryOutputDestination {
    readonly dataSourceOptionIdentifier: DataSourceOptionIdentifier;
    readonly showAsShortcutMenuItem: boolean;
}

/** @internal */
export const enum QueryOutputDestinationKind {
    Database = "Database",
    File = "File",
}

/** @alpha */
export type QueryOutputDestinationSettings =
    | DisabledQueryOutputDestinationSettings
    | EnabledQueryOutputDestinationSettings;

/** @internal */
export interface QueryOutputInfo {
    readonly sourceQueryName: string;
}

/** @internal */
export type QueryOutputSettings =
    | QueryDatabaseOutputSettings
    | QueryFileOutputSettings;

/** @internal */
export interface QueryOutputStatusChallengeError {
    readonly kind: QueryOutputStatusErrorKind.DestinationQueryChallenge;
    readonly challengeKind: string;
    readonly challengePath: string;
}

/** @internal */
export interface QueryOutputStatusDestinationNotFoundError {
    readonly kind: QueryOutputStatusErrorKind.DestinationQueryNotFound;
    readonly destinationQueryName: string;
}

/** @internal */
export interface QueryOutputStatusDestinationTransformNotFoundError {
    readonly kind: QueryOutputStatusErrorKind.DestinationTransformQueryNotFound;
    readonly destinationTransformQueryName: string;
}

/** @internal */
export type QueryOutputStatusError =
    | QueryOutputStatusDestinationNotFoundError
    | QueryOutputStatusDestinationTransformNotFoundError
    | QueryOutputStatusExceptionError
    | QueryOutputStatusUnsupportedScriptError
    | QueryOutputStatusChallengeError
    | QueryOutputStatusUnknownError
    | QueryOutputStatusMultipleDestinationsError;

/** @internal */
export const enum QueryOutputStatusErrorKind {
    DestinationQueryNotFound = "DestinationQueryNotFound",
    DestinationTransformQueryNotFound = "DestinationTransformQueryNotFound",
    DestinationQueryChallenge = "DestinationQueryChallenge",
    DestinationQueryException = "DestinationQueryException",
    DestinationQueryHasUnsupportedScript = "DestinationQueryHasUnsupportedScript",
    MultipleDestinationQueries = "MultipleDestinationQueries",
    UnknownError = "UnknownError",
}

/** @internal */
export interface QueryOutputStatusExceptionError {
    readonly kind: QueryOutputStatusErrorKind.DestinationQueryException;
    readonly exceptionMessage?: LocalizedString;
    readonly piiExceptionMessage?: LocalizedString;
}

/** @internal */
export interface QueryOutputStatusMultipleDestinationsError {
    readonly kind: QueryOutputStatusErrorKind.MultipleDestinationQueries;
}

/** @internal */
export interface QueryOutputStatusUnknownError {
    readonly kind: QueryOutputStatusErrorKind.UnknownError;
}

/** @internal */
export interface QueryOutputStatusUnsupportedScriptError {
    readonly kind: QueryOutputStatusErrorKind.DestinationQueryHasUnsupportedScript;
    readonly destinationQueryName: string;
}

/** @internal */
export const enum QueryPlanType {
    DynamicPlanFolded = "DynamicPlanFolded",
    DynamicPlanMightFold = "DynamicPlanMightFold",
    DynamicPlanNotFolded = "DynamicPlanNotFolded",
    DynamicPlanOpaque = "DynamicPlanOpaque",
    ErrorRetrievingPlan = "ErrorRetrievingPlan",
    FoldedQueryNotImplemented = "FoldedQueryNotImplemented",
    FoldedQueryNull = "FoldedQueryNull",
    FoldedWithQuery = "FoldedWithQuery",
    NotFolded = "NotFolded",
    NotFoldedPrivacyBuffering = "NotFoldedPrivacyBuffering",
    NotTable = "NotTable",
    OpaqueConstant = "OpaqueConstant",
    UnexpectedPlanKind = "UnexpectedPlanKind",
}

/** @alpha */
export type QueryPreviewCell =
    | QueryPreviewPrimitiveCell
    | QueryPreviewNonPrimitiveCell;

/** @alpha */
export interface QueryPreviewChallengeErrorResult {
    readonly kind: QueryPreviewResultKind.Challenge;
    readonly challengeType: string;
    readonly dataSources: ReadonlyArray<string>;
    readonly challengeMessage?: string;
    readonly reason?: string;
}

/** @alpha */
export interface QueryPreviewColumn {
    readonly name: string;
    readonly typeName: TypeName;
}

/** @alpha */
export const enum QueryPreviewErrorKind {
    Cancellation = "Cancellation",
    Challenge = "Challenge",
    EvaluationError = "EvaluationError",
    GatewayNeeded = "GatewayNeeded",
    NotAvailable = "NotAvailable",
}

/** @alpha */
export interface QueryPreviewErrorResult {
    readonly kind: QueryPreviewResultKind.Error;
    readonly errorKind: QueryPreviewErrorKind;
    readonly message?: string;
}

/** @alpha */
export interface QueryPreviewNonPrimitiveCell {
    readonly isPrimitive: false;
    readonly isDelayed: boolean;
    readonly value: string;
}

/** @alpha */
export interface QueryPreviewNotSupportedResult {
    readonly kind: QueryPreviewResultKind.NotSupported;
    readonly resultKind: QueryPreviewNotSupportedResultKind;
}

/** @alpha */
export const enum QueryPreviewNotSupportedResultKind {
    Binary = "Binary",
    Function = "Function",
    Primitive = "Primitive",
    List = "List",
    Record = "Record",
    Type = "Type",
}

/** @alpha */
export interface QueryPreviewPrimitiveCell {
    readonly isPrimitive: true;
    readonly value: string | number | boolean | Date | null | undefined;
}

/** @alpha */
export type QueryPreviewResult = (
    | QueryPreviewTableResult
    | QueryPreviewErrorResult
    | QueryPreviewChallengeErrorResult
    | QueryPreviewNotSupportedResult
)
& {
    readonly queryName: string;
    readonly stepName?: string;
}

/** @alpha */
export const enum QueryPreviewResultKind {
    Table = "Table",
    Error = "Error",
    Challenge = "Challenge",
    NotSupported = "NotSupported",
}

/** @alpha */
export interface QueryPreviewTableResult {
    readonly kind: QueryPreviewResultKind.Table;
    readonly columns: ReadonlyArray<QueryPreviewColumn>;
    readonly rows: ReadonlyArray<ReadonlyArray<QueryPreviewCell>>;
}

/** @alpha */
export interface QueryReadonlyInfo {
    readonly localizedReasonMessages: ReadonlyArray<string>;
}

/** @internal */
export interface QueryValidationRequest {
    readonly mashupId: Guid;
    readonly queryName: string;
    readonly destinationResourceKind?: string;
}

/** @internal */
export interface QueryValidationResult {
    readonly isValid: boolean;
    readonly errorMessage?: string;
    /**
     * The evaluation result type (e.g. "Challenge", "Exception", "ErrorCode", "Table").
     * Allows the UI to differentiate between credential errors and general evaluation errors.
     */
    readonly resultType?: string;
    readonly dataSourceKind?: string;
    readonly dataSourcePath?: string;
}

/** @internal */
export interface QuickAccessConnection {
    readonly createdTime: string | null | undefined;
    readonly lastUsedTime: string | null | undefined;
    readonly connection: QuickAccessConnectionConnectionProperties;
    readonly id: string;
    readonly queryScript: string;
    readonly dataAccessMode?: DataAccessMode;
}

/** @internal */
export interface QuickAccessConnectionConnectionProperties {
    readonly displayName: string;
    readonly gatewayObjectId: string | null | undefined;
    readonly id: string;
    readonly kind: string;
    readonly path: string;
}

/** @internal */
export interface QuickAccessData {
    readonly connections: ReadonlyArray<QuickAccessConnection>;
    readonly queries: ReadonlyArray<QuickAccessQuery>;
    readonly queryLibraries: ReadonlyArray<QueryLibrary>;
    readonly queryLibraryQueries: ReadonlyArray<QuickAccessQueryLibraryQuery>;
}

/** @internal */
export interface QuickAccessQuery {
    readonly queryName: string;
    readonly queryScript: string;
    readonly storageId: string;
    readonly createdTime: string | null | undefined;
    readonly lastUsedTime: string | null | undefined;
    readonly referencedQueries?: ReadonlyArray<QuickAccessReferencedQuery>;
    readonly resultTypeName?: TypeName;
    readonly schema?: TableResultSchema;
}

/** @internal */
export interface QuickAccessQueryLibraryQuery {
    readonly artifactName: string;
    readonly queryLibraryQueryId: string;
    readonly queryLibraryQueryLocation?: QueryLibraryQueryLocation | null | undefined;
    readonly queryName: string;
    readonly lastUsedTime: string | null | undefined;
}

/** @internal */
export interface QuickAccessReferencedQuery {
    readonly queryName: string;
    readonly queryScript: string;
}

/** @internal */
export type RecentItemNode =
    | SourceRecentItemNode
    | NavigationRecentItemNode;

/** @internal */
export const enum RecentItemNodeKind {
    Source = "Source",
    Navigation = "Navigation",
}

/** @internal */
export const enum RecentItemNodeLinkKind {
    Binary = "Binary",
    Connection = "Connection",
    Database = "Database",
    DatabaseServer = "DatabaseServer",
    Schema = "Schema",
    Table = "Table",
    Folder = "Folder",
    View = "View",
    Sheet = "Sheet",
    Function = "Function",
}

/** @internal */
export type RecentItemNodesOperationResponse =
    | SuccessfulRecentItemNodesOperationResponse
    | FailedRecentItemNodesOperationResponse;

/** @internal */
export const enum RecentItemNodesOperationResponseKind {
    Successful = "Successful",
    Failed = "Failed",
}

/** @alpha */
export interface ReferenceDestinationDefinition {
    readonly kind: DestinationDefinitionKind.Reference;
    readonly queryName: string;
    readonly isNewTarget: boolean;
}

/** @internal */
export interface RefreshDataflowRequest {
    readonly dataflowDisplayName: string;
    readonly workspaceObjectId: Guid;
    readonly dataflowObjectId: Guid;
}

/** @internal */
export interface RefreshDataflowResult {
    readonly success: boolean;
    readonly dataflowObjectId?: string;
    readonly errorMessage?: string;
}

/** @internal */
export interface RefreshScheduleConfiguration {
    /**
     * The type of schedule configuration: "Cron", "Daily", or "Weekly".
     */
    readonly type: RefreshScheduleType;
    /**
     * The start time for this schedule in UTC, using the YYYY-MM-DDTHH:mm:ssZ format.
     */
    readonly startDateTime: string;
    /**
     * The end time for this schedule in UTC, using the YYYY-MM-DDTHH:mm:ssZ format.
     */
    readonly endDateTime: string;
    /**
     * The time zone identifier (e.g., "Central Standard Time").
     */
    readonly localTimeZoneId: string;
    /**
     * The time interval in minutes for Cron schedules. A number between 1 and 5270400 (10 years).
     */
    readonly interval?: number;
    /**
     * A list of time slots in the hh:mm format for Daily/Weekly schedules. Maximum 100 time slots.
     */
    readonly times?: ReadonlyArray<string>;
    /**
     * A list of weekdays for Weekly schedules (Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, Sunday).
     */
    readonly weekdays?: ReadonlyArray<string>;
}

/** @internal */
export const enum RefreshScheduleType {
    Cron = "Cron",
    Daily = "Daily",
    Weekly = "Weekly",
}

/** @internal */
export interface RemoveIncrementalRefreshSettingsFailureResult {
    readonly success: false;
}

/** @internal */
export interface RemoveIncrementalRefreshSettingsRequest {
    readonly queryName: string;
}

/** @internal */
export type RemoveIncrementalRefreshSettingsResult =
    | RemoveIncrementalRefreshSettingsFailureResult
    | RemoveIncrementalRefreshSettingsSuccessResult;

/** @internal */
export interface RemoveIncrementalRefreshSettingsSuccessResult {
    readonly success: true;
    readonly newQueryMetadata?: QueryMetadata;
    readonly newQuery?: Query;
}

/** @internal */
export interface RemoveQueriesFromMashupRequest {
    readonly mashupId: Guid;
    readonly queryNamesToRemove: ReadonlyArray<string>;
}

/** @internal */
export interface RemoveQueriesFromMashupResponse {
    readonly success: boolean;
    readonly errorMessage?: string;
}

/** @internal */
export interface RenameQueryExecutionInfo {
    readonly toolName: string;
    readonly editVersion: number;
    readonly previousName: string;
    readonly newName: string;
}

/** @internal */
export interface ResultWithErrorCode<TSuccess> extends ResultWithErrorCodeBase<TSuccess, string> {
}

/** @internal */
export interface ResultWithErrorCodeBase<TSuccess, TErrorCode> {
    readonly success: boolean;
    readonly successValue?: TSuccess;
    readonly errorCode?: TErrorCode;
    readonly displayErrorDetails?: string;
    readonly errorMetadata?: { [key: string]: string | null | undefined };
}

/** @internal */
export interface ResultWithStrongErrorCode<TSuccess, TErrorCode> extends ResultWithErrorCodeBase<TSuccess, TErrorCode> {
}

/** @internal */
export interface RibbonCommandCallback {
    readonly preventDefault: boolean;
}

/** @internal */
export interface RibbonCommandCallbacks {
    readonly onOptionsClick?: RibbonCommandCallback;
    readonly onManageConnectionsClick?: RibbonCommandCallback;
}

/** @alpha */
export interface RibbonSettings {
    /**
     * If true, the ribbon will be hidden and inaccessible.
     *
     * @defaultValue false.
     */
    readonly hide?: boolean;
    /**
     * Shows the pane collapsed.
     *
     * @defaultValue false.
     */
    readonly showCollapsed?: boolean;
}

/** @public */
export interface SampleDataItem {
    /**
     * Gets or sets the id of the sample data item.
     * It will be returned to the client when the user selects the item.
     */
    readonly id: string;
    /**
     * Gets or sets the icon of the sample data item.
     */
    readonly iconBase64: string;
    /**
     * Gets or sets the title of the sample data item.
     */
    readonly title: string;
    /**
     * Gets or sets the subtitle of the sample data item.
     */
    readonly subtitle: string;
    /**
     * Gets or sets the description of the sample data item.
     */
    readonly description: string;
    /**
     * Gets or sets the tooltip text of the sample data item.
     */
    readonly tooltip: string;
}

/** @public */
export interface SampleDataSettings {
    /**
     * Gets or sets each of the sample data items to show in the Data Discovery tab.
     *
     * defaultValue [].
     */
    readonly sampleDataItems: ReadonlyArray<SampleDataItem>;
}

/** @internal */
export enum SapHanaCryptoProvider {
    CommonCrypto = "CommonCrypto",
    MsCrypto = "MsCrypto",
    SapCrypto = "SapCrypto",
}

/** @internal */
export interface SasCredential {
    readonly authKind: AuthenticationKind.SAS;
    readonly sasToken?: AuthCredentialValue;
}

/** @internal */
export interface ScheduleRefreshRequest {
    readonly workspaceObjectId: Guid;
    readonly dataflowObjectId: Guid;
    readonly configuration: RefreshScheduleConfiguration;
}

/** @internal */
export interface ScheduleRefreshResult {
    readonly success: boolean;
    readonly scheduleId?: Guid;
    readonly errorMessage?: string;
}

/** @alpha */
export interface ScriptDestinationDefinition {
    readonly kind: DestinationDefinitionKind.Script;
    readonly script: string;
    readonly isNewTarget: boolean;
}

/** @internal */
export interface ScriptTargetDefinition {
    readonly kind: TargetDefinitionKind.Script;
    readonly script: string;
}

/** @internal */
export interface SendFeedbackRequest {
    readonly details: string;
    readonly feedback: string;
    readonly hostContextType: string;
    readonly rating: string;
}

/** @alpha */
export interface SendFeedbackSettings {
    /**
     * Gets or sets the feedback options to display in the dropdown.
     */
    readonly options: ReadonlyArray<FeedbackOptionSettings>;
}

/** @internal */
export interface ServicePrincipalCredential {
    readonly authKind: AuthenticationKind.ServicePrincipal;
    readonly servicePrincipalSecret?: AuthCredentialValue;
}

/** @internal */
export interface SetIncrementalRefreshSettingsFailureResult {
    readonly success: false;
}

/** @internal */
export interface SetIncrementalRefreshSettingsRequest {
    readonly queryName: string;
    readonly incrementalRefreshSettings: IncrementalRefreshSettings;
}

/** @internal */
export type SetIncrementalRefreshSettingsResult =
    | SetIncrementalRefreshSettingsFailureResult
    | SetIncrementalRefreshSettingsSuccessResult;

/** @internal */
export interface SetIncrementalRefreshSettingsSuccessResult {
    readonly success: true;
    readonly newQueryMetadata?: QueryMetadata;
    readonly newQuery?: Query;
}

/** @internal */
export interface SetMashupDocumentCompilationErrorResult {
    readonly kind: SetMashupDocumentResultKind.CompilationError;
    readonly sourceErrors: ReadonlyArray<SourceError>;
}

/** @internal */
export interface SetMashupDocumentInvalidQueryNameResult {
    readonly invalidQueryNames: ReadonlyArray<string>;
    readonly kind: SetMashupDocumentResultKind.InvalidQueryName;
}

/** @internal */
export interface SetMashupDocumentParsingErrorResult {
    readonly kind: SetMashupDocumentResultKind.ParsingError;
    readonly sourceErrors: ReadonlyArray<SourceError>;
}

/** @internal */
export type SetMashupDocumentResult =
    | SetMashupDocumentSuccessResult
    | SetMashupDocumentCompilationErrorResult
    | SetMashupDocumentInvalidQueryNameResult
    | SetMashupDocumentParsingErrorResult
    | SetMashupDocumentUnexpectedDocumentKindResult;

/** @internal */
export const enum SetMashupDocumentResultKind {
    CompilationError = "CompilationError",
    InvalidQueryName = "InvalidQueryName",
    ParsingError = "ParsingError",
    Success = "Success",
    UnexpectedDocumentKind = "UnexpectedDocumentKind",
}

/** @internal */
export interface SetMashupDocumentSuccessResult {
    readonly kind: SetMashupDocumentResultKind.Success;
}

/** @internal */
export interface SetMashupDocumentUnexpectedDocumentKindResult {
    readonly kind: SetMashupDocumentResultKind.UnexpectedDocumentKind;
}

/** @internal */
export interface SetMashupFromTemplateFailureResult {
    readonly kind: SetMashupFromTemplateResultKind.Failure;
}

/** @internal */
export type SetMashupFromTemplateResult =
    | SetMashupFromTemplateSuccessResult
    | SetMashupFromTemplateFailureResult;

/** @internal */
export const enum SetMashupFromTemplateResultKind {
    Failure = "Failure",
    Success = "Success",
}

/** @internal */
export interface SetMashupFromTemplateSuccessResult {
    readonly kind: SetMashupFromTemplateResultKind.Success;
    readonly mashup: Mashup;
    readonly queryMetadata: ReadonlyArray<QueryMetadata>;
    readonly importTemplateWarnings: ReadonlyArray<ImportTemplateWarningType>;
}

/** @internal */
export enum SetMashupPropertiesErrorCode {
    GatewayBindingMultipleMatchingGateways = "GatewayBindingMultipleMatchingGateways",
    GatewayBindingNoMatchingDataSourceInGateway = "GatewayBindingNoMatchingDataSourceInGateway",
    GatewayBindingNoMatchingGateways = "GatewayBindingNoMatchingGateways",
    Unknown = "Unknown",
}

/** @internal */
export interface SetMashupPropertiesRequest {
    readonly allowNativeQueries: OptionalValue<boolean>;
    readonly documentLocale: OptionalValue<string>;
    readonly fastCombine: OptionalValue<boolean>;
    readonly gatewayObjectId: OptionalValue<string | null | undefined>;
    readonly skipAutomaticTypeAndHeaderDetection: OptionalValue<boolean>;
}

/** @internal */
export interface SetOutputDestinationExecutionInfo {
    readonly toolName: string;
    readonly editVersion: number;
    /**
     * The source query with DataDestinations configured.
     */
    readonly sourceQuery: Query;
    /**
     * The newly created destination query.
     */
    readonly destinationQuery: Query;
    /**
     * The newly created transformation query (for file destinations), if applicable.
     */
    readonly transformQuery?: Query;
    /**
     * Metadata for newly created queries.
     */
    readonly newQueriesMetadata: ReadonlyArray<QueryMetadata>;
    readonly queryAndStepDependentGraph: DocumentMemberDependentGraph;
}

/** @internal */
export interface SetQueriesIsSharedRequest {
    readonly requests: ReadonlyArray<SetQueryIsSharedRequest>;
}

/** @internal */
export interface SetQueriesIsSharedResult {
    readonly queryStates: ReadonlyArray<Query>;
}

/** @internal */
export interface SetQueriesLoadEnabledRequest {
    readonly requests: ReadonlyArray<SetQueryLoadEnabledRequest>;
}

/** @internal */
export interface SetQueriesLoadEnabledResult {
    readonly queryStates: ReadonlyArray<Query>;
    readonly queriesMetadata: ReadonlyArray<QueryMetadata>;
}

/** @internal */
export interface SetQueryIsSharedExecutionInfo {
    readonly toolName: string;
    readonly editVersion: number;
    readonly queryName: string;
    readonly isShared: boolean;
}

/** @internal */
export interface SetQueryIsSharedRequest {
    readonly queryName: string;
    readonly isShared: boolean;
}

/** @internal */
export interface SetQueryLoadEnabledRequest {
    readonly queryName: string;
    readonly loadEnabled: boolean;
}

/** @internal */
export interface SetQueryMetadataExecutionInfo {
    readonly toolName: string;
    readonly queryMetadata: QueryMetadata;
}

/** @internal */
export interface SetQueryScriptConfirmation {
    readonly toolType: ToolConfirmationType.SetQueryScript;
    readonly queryName: string;
    readonly script: string;
}

/** @internal */
export interface SetQueryScriptExecutionInfo {
    readonly toolName: string;
    readonly editVersion: number;
    readonly queryName: string;
    readonly queryState: Query;
    readonly queryAndStepDependentGraph: DocumentMemberDependentGraph;
}

/** @internal */
export interface SetQueryStagingDefinitionFailureResult {
    readonly success: false;
}

/** @internal */
export interface SetQueryStagingDefinitionRequest {
    readonly queryName: string;
    readonly stagingDefinition: StagingDefinition | null | undefined;
}

/** @internal */
export type SetQueryStagingDefinitionResult =
    | SetQueryStagingDefinitionFailureResult
    | SetQueryStagingDefinitionSuccessResult;

/** @internal */
export interface SetQueryStagingDefinitionSuccessResult {
    readonly success: true;
    /**
     * Gets or sets the updated query metadata. Only available if the NewQuery property is null.
     */
    readonly newQueryMetadata?: QueryMetadata;
    /**
     * Gets or sets the updated query state. If null, NewQueryMetadata will be available.
     */
    readonly newQuery?: Query;
}

/** @internal */
export interface SetStepScriptErrorResult {
    readonly success: false;
    readonly jsonQueryState: string;
    readonly sourceErrors: ReadonlyArray<SourceError>;
}

/** @internal */
export type SetStepScriptResult2 =
    | SetStepScriptErrorResult
    | SetStepScriptSuccessResult;

/** @internal */
export interface SetStepScriptSuccessResult {
    readonly success: true;
    readonly actualScript: string;
    readonly jsonQueryState: string;
}

/** @internal */
export interface SharePointPickerSettings {
    readonly enableDialog: boolean;
}

/** @internal */
export interface SharePointSite {
    readonly dateModified: string;
    readonly description: string;
    readonly displayName: string;
    readonly iconData: string;
    readonly name: string;
    readonly siteType: SharePointSiteType;
    readonly webUrl: string;
}

/** @internal */
export interface SharePointSiteGraphResponse {
    readonly siteList: ReadonlyArray<SharePointSite>;
    readonly nextLink?: string;
}

/** @public */
export enum SharePointSiteType {
    FrequentlyUsed = "frequent",
    Followed = "followed",
    SearchResult = "searched",
}

/** @internal */
export interface ShorthandParseAsMLiteralResult {
    readonly success: boolean;
    readonly mLiteral?: string;
}

/** @internal */
export interface ShorthandParseResult {
    readonly success: boolean;
    readonly jsonFragment: string;
}

/** @internal */
export interface ShorthandValuesParseAsMLiteralsResult {
    readonly shorthandParseResults: ReadonlyArray<ShorthandParseAsMLiteralResult>;
}

/** @internal */
export interface ShorthandValuesParseResult {
    readonly shorthandParseResults: ReadonlyArray<ShorthandParseResult>;
}

/** @internal */
export interface ShorthandValuesToArgumentFragmentsRequest {
    readonly allowEscapedText: boolean;
    readonly allowNull: boolean;
    readonly allowSpecialCharacters: boolean;
    readonly enforceTypeCompatibility: boolean;
    readonly expectedType: TypeName;
    readonly locale: string;
    readonly shorthandValues: ReadonlyArray<string>;
}

/** @internal */
export const enum SimsError {
    endpointNotAvailableInRegion = 0,
    httpError = 1,
    invalidInput = 2,
    maxRetriesReached = 3,
    modelError = 4,
}

/** @internal */
export interface SimsErrorResponse {
    readonly error: SimsError;
    readonly responseStatus: SimsResponseStatus.Error;
}

/** @internal */
export interface SimsOkResponse {
    readonly payloadRaw: string;
    readonly responseStatus: SimsResponseStatus.Ok;
}

/** @internal */
export interface SimsPreviewColumn {
    readonly columnData: ReadonlyArray<string>;
    readonly columnName: string;
    readonly columnTypeName: string;
}

/** @internal */
export interface SimsRequest {
    readonly columns: ReadonlyArray<SimsPreviewColumn>;
    readonly enableAIBuilder?: boolean;
    readonly endpointName: string;
    readonly endpointVersion?: string;
    readonly entityName: string;
}

/** @internal */
export type SimsResponse =
    | SimsOkResponse
    | SimsErrorResponse;

/** @internal */
export const enum SimsResponseStatus {
    Error = "Error",
    Ok = "Ok",
}

/** @internal */
export interface SourceRecentItemNode {
    /**
     * Gets or sets the unique identifier for the recent item node.
     */
    readonly id: RecentItemNodeId;
    /**
     * Gets or sets the contract version number.
     */
    readonly version: number;
    /**
     * Gets or sets the query script associated with this node.
     */
    readonly queryScript: string;
    /**
     * Gets or sets the link kind for this node.
     */
    readonly linkKind: RecentItemNodeLinkKind | null | undefined;
    /**
     * Gets or sets the last accessed timestamp as an ISO string.
     */
    readonly lastAccessedAt: string;
    /**
     * Gets or sets the array of data access modes used with this node.
     * Defaults to [Read] if not specified (for backward compatibility).
     */
    readonly dataAccessModes?: ReadonlyArray<DataAccessMode>;
    /**
     * Gets or sets whether this item is marked as a favorite.
     * Null or false means the item is not favorited.
     */
    readonly isFavorite?: boolean | null | undefined;
    /**
     * Gets the kind of this node, which is always Source.
     */
    readonly kind: RecentItemNodeKind.Source;
    /**
     * Gets or sets the resource kind identifier.
     */
    readonly resourceKind?: string | null | undefined;
    /**
     * Gets or sets the resource path.
     */
    readonly resourcePath?: string | null | undefined;
    /**
     * Gets or sets the connection identity associated with this source.
     */
    readonly connectionIdentity: ConnectionIdentity;
}

/** @internal */
export interface SourceValue {
    readonly kind: ValueKind.Source;
}

/** @internal */
export interface SpecialCharactersInfo {
    readonly containsSpecialCharacters: boolean;
    readonly resolvedShorthand: string;
}

/** @internal */
export interface SplitColumnTransform {
    readonly columnName: string;
    readonly delimiter?: string;
    readonly id: SuggestedTransformId.SplitColumn;
    readonly newColumnNames: ReadonlyArray<string>;
    readonly step: Step;
    readonly suggestionScore: number;
}

/** @internal */
export interface StartEditingSessionError {
    readonly errorKind: StartEditingSessionErrorKind;
    readonly errorDetails: string;
    readonly mashupDocument: string;
}

/** @internal */
export const enum StartEditingSessionErrorKind {
    ConnectionsAuthorizationFailed = "ConnectionsAuthorizationFailed",
    DuplicateQueryId = "DuplicateQueryId",
    DuplicateQueryName = "DuplicateQueryName",
    DuplicateStepName = "DuplicateStepName",
    InvalidQueryName = "InvalidQueryName",
    MashupTooLong = "MashupTooLong",
    MashupDocumentNotASectionDocument = "MashupDocumentNotASectionDocument",
    ParsingError = "ParsingError",
    UnexpectedQueryIdFormat = "UnexpectedQueryIdFormat",
}

/** @internal */
export interface StartEditingSessionRequest {
    readonly mashup: EditingSessionMashup;
    readonly disableMashupNameDisambiguation?: boolean;
    readonly editingSessionId?: string;
    readonly lifeSpanInMinutes?: number;
    readonly includeQueriesMetadata?: boolean;
    readonly numberOfQueriesLimit?: number;
    readonly includeMashup?: boolean;
}

/** @internal */
export interface StartEditingSessionResponse {
    readonly success: boolean;
    readonly sessionId: string;
    readonly queries?: Query[];
    readonly queriesMetadata?: ReadonlyArray<QueryMetadata>;
    readonly mashup?: Mashup;
    readonly editVersion?: number;
    readonly error: StartEditingSessionError;
}

/** @internal */
export interface StartupDataSourceQuery {
    readonly resourceKind: string;
    readonly resourcePath: string;
    /**
     * Gets or sets the query script that is used to connect to the data source.
     *
     *  WARN: this should be query script should align with the given <see cref = "ResourceKind"/>
     *  and <see cref = "ResourcePath"/>.
     */
    readonly queryScript: string;
    readonly kind: StartupQueriesDefinitionKind.DataSourceQuery;
}

/** @internal */
export type StartupQueriesDefinition =
    | StartupDataSourceQuery
    | StartupQueriesDetails;

/** @internal */
export const enum StartupQueriesDefinitionKind {
    DataSourceQuery = "DataSourceQuery",
    QueriesDetails = "QueriesDetails",
}

/** @internal */
export interface StartupQueriesDetails {
    readonly queries: ReadonlyArray<QueryDetails>;
    readonly kind: StartupQueriesDefinitionKind.QueriesDetails;
}

/** @internal */
export interface StepDescription {
    readonly pointer: StepPointer;
    readonly description: string;
}

/** @alpha */
export interface StepsPaneSettings extends PaneSettings {
    /**
     * Only show the applied steps in the pane and not other query settings.
     * This is useful for scenarios where we are editing a single query.
     * When this option is set, then the title of the pane will be "Applied Steps"
     * instead of "Query Settings".
     *
     * @defaultValue false.
     */
    readonly onlyShowAppliedSteps?: boolean;
}

/** @internal */
export type StringDescriptor =
    | LocalizedStringDescriptor
    | StringIdentifierDescriptor;

/** @internal */
export const enum StringDescriptorKind {
    LocalizedString = "LocalizedString",
    StringIdentifier = "StringIdentifier",
}

/** @internal */
export interface StringIdentifierDescriptor {
    readonly kind: StringDescriptorKind.StringIdentifier;
    readonly identifier: string;
}

/** @internal */
export interface SuccessfulArtifactConnectionInfoResult {
    readonly connectionString: string;
    readonly success: true;
}

/** @internal */
export interface SuccessfulDataSourceDiscoveryResponse {
    readonly results: ReadonlyArray<DataSourceDiscoveryResult>;
    readonly success: true;
}

/** @internal */
export interface SuccessfulDataSourceDiscoveryResultItem {
    readonly dataSource: MashupDataSource;
    readonly connectionDetails?: DataSourceConnectionDetails;
    readonly success: true;
}

/** @internal */
export interface SuccessfulGetQueryScriptsResponse {
    readonly queryScripts: ReadonlyArray<string>;
    readonly success: true;
}

/** @internal */
export interface SuccessfulRecentItemNodesOperationResponse {
    readonly kind: RecentItemNodesOperationResponseKind.Successful;
    /**
     * Updated list of recent item nodes after the operation was completed successfully.
     */
    readonly recentItemNodes: ReadonlyArray<RecentItemNode>;
}

/** @internal */
export interface SuccessfulTokenResponse {
    readonly token: string;
    readonly success: true;
}

/** @internal */
export interface SuccessOperationResult {
    readonly success: true;
}

/** @internal */
export type SuggestedTransform =
    | CustomTransform
    | DropBlankRowsTransform
    | DropColumnTransform
    | DropDuplicatesTransform
    | FillColumnTransform
    | SplitColumnTransform
    | UnpivotColumnsTransform
    | UnpivotOtherColumnsTransform;

/** @internal */
export const enum SuggestedTransformId {
    Custom = "Custom",
    DropColumn = "DropColumn",
    DropBlankRows = "DropBlankRows",
    DropDuplicates = "DropDuplicates",
    FillColumn = "FillColumn",
    SplitColumn = "SplitColumn",
    UnpivotColumns = "UnpivotColumns",
    UnpivotOtherColumns = "UnpivotOtherColumns",
}

/** @internal */
export interface SuggestedTransformsLocalizedStrings {
    readonly addedColumn: string;
    readonly addedIndex: string;
    readonly attribute: string;
    readonly average: string;
    readonly extractedFirstCharacters: string;
    readonly extractedLastCharacters: string;
    readonly extractedTextAfterDelimiter: string;
    readonly extractedTextBeforeDelimiter: string;
    readonly extractedTextBetweenDelimiter: string;
    readonly extractedTextRange: string;
    readonly filledDown: string;
    readonly filledMissingValues: string;
    readonly groupedRows: string;
    readonly mode: string;
    readonly removedBlankRows: string;
    readonly removedColumn: string;
    readonly removedColumns: string;
    readonly removedDuplicateRows: string;
    readonly removedTopRows: string;
    readonly renameColumns: string;
    readonly replacedMissingValues: string;
    readonly roundedAverage: string;
    readonly selectedRows: string;
    readonly source: string;
    readonly splitColumn: string;
    readonly splitColumnByDelimiter: string;
    readonly splitColumnByDelimiters: string;
    readonly splitColumnByPositions: string;
    readonly trimmedText: string;
    readonly unpivotedOnlySelectedColumns: string;
    readonly unpivotedOtherColumns: string;
    readonly value: string;
}

/** @internal */
export interface SuggestedTransformsRequest {
    readonly excludedColumnNames: ReadonlyArray<string>;
    readonly evaluationParams: JsonPathEvaluationParams;
    readonly localizedStrings: SuggestedTransformsLocalizedStrings;
    readonly tableResult: SuggestionTableResult;
}

/** @internal */
export interface SuggestedTransformsResult {
    readonly suggestedTransforms: ReadonlyArray<SuggestedTransform>;
    readonly msrSuggestionsComputeTimeInMs: number;
    readonly proseSuggestionsComputeTimeInMs: number;
}

/** @internal */
export interface SuggestionTableResult {
    readonly columns: ReadonlyArray<SuggestionTableResultColumn>;
    readonly rows: ReadonlyArray<SuggestionTableResultRow>;
}

/** @internal */
export type SuggestionTableResultCell =
    | SuggestionTableResultDelayedCell
    | SuggestionTableResultNonDelayedCell;

/** @internal */
export interface SuggestionTableResultColumn {
    readonly name: string;
    readonly typeName: string;
}

/** @internal */
export interface SuggestionTableResultDelayedCell {
    readonly isDelayed: true;
}

/** @internal */
export interface SuggestionTableResultNonDelayedCell {
    readonly isDelayed: false;
    readonly typeName: string;
    readonly value?: string;
}

/** @internal */
export interface SuggestionTableResultRow {
    readonly cells: ReadonlyArray<SuggestionTableResultCell>;
}

/** @internal */
export interface TableOutputProperties {
    readonly parametersMetadata: ReadonlyArray<OutputDestinationParameterMetadata>;
    readonly navigationDataColumnName: string;
}

/** @internal */
export const enum TableProfileColumnName {
    Column = "Column",
    ColumnType = "ColumnType",
    Count = "Count",
    ErrorCount = "ErrorCount",
    NullCount = "NullCount",
    DistinctCount = "DistinctCount",
    UniqueCount = "UniqueCount",
    Min = "Min",
    Max = "Max",
    ValueDistribution = "ValueDistribution",
    Average = "Average",
    EmptyStringCount = "EmptyStringCount",
    TextLengthDistribution = "TextLengthDistribution",
    NaNCount = "NaNCount",
    ZeroCount = "ZeroCount",
    EvenCount = "EvenCount",
    OddCount = "OddCount",
    StandardDeviation = "StandardDeviation",
    SignDistribution = "SignDistribution",
    ParityDistribution = "ParityDistribution",
    TrueCount = "TrueCount",
    FalseCount = "FalseCount",
    YearDistribution = "YearDistribution",
    MonthDistribution = "MonthDistribution",
    DayDistribution = "DayDistribution",
    WeekOfYearDistribution = "WeekOfYearDistribution",
    DayOfWeekDistribution = "DayOfWeekDistribution",
    AmPmDistribution = "AmPmDistribution",
    HourDistribution = "HourDistribution",
}

/** @public */
export interface TableResultSchema {
    readonly columns: ReadonlyArray<TableResultSchemaColumn>;
}

/** @public */
export interface TableResultSchemaColumn {
    readonly name: string;
    readonly type: MashupTypeName;
    readonly isPrimaryKey: boolean;
    readonly isNullable: boolean;
}

/** @internal */
export interface TableUpdateAddColumnStep extends TableUpdateWithDataProviderStep {
    readonly kind: TableUpdateStepKind.AddColumn;
}

/** @internal */
export interface TableUpdateAddRowsStep extends TableUpdateWithRowIndexAndDataProviderStep {
    readonly kind: TableUpdateStepKind.AddRows;
}

/** @internal */
export interface TableUpdateColumnRename {
    readonly oldColumnName: string;
    readonly newColumnName: string;
}

/** @internal */
export const enum TableUpdateMethodKind {
    AppendRows = "AppendRows",
    ReplaceRows = "ReplaceRows",
}

/** @internal */
export interface TableUpdatePlanFailureResult {
    readonly success: false;
    readonly errorReason: string;
}

/** @internal */
export interface TableUpdatePlanFromNaturalLanguageRequest {
    readonly hostContext: HostContext;
    readonly documentLocale: string;
    readonly dataSourceScript: string;
    readonly naturalLanguageInput: string;
    readonly sampleTableColumnNames: ReadonlyArray<string>;
    readonly sampleTableCells: ReadonlyArray<ReadonlyArray<string>>;
    readonly sampleTableColumnTypes?: ReadonlyArray<string>;
    readonly llmProviderKind: string;
    readonly explainStep: boolean;
    readonly userTier?: string;
}

/** @internal */
export interface TableUpdatePlanFromQueryScriptRequest {
    readonly hostContext: HostContext;
    readonly documentLocale: string;
    readonly queryScript: string;
    readonly sampleTableColumnNames: ReadonlyArray<string>;
    readonly sampleTableCells: ReadonlyArray<ReadonlyArray<string>>;
    readonly sampleTableColumnTypes?: ReadonlyArray<string>;
    readonly userTier?: string;
}

/** @internal */
export interface TableUpdatePlanFromStepScriptRequest {
    readonly hostContext: HostContext;
    readonly documentLocale: string;
    readonly dataSourceScript: string;
    readonly transformationStepScript: string;
    readonly transformationStepExplanation?: string;
    readonly sampleTableColumnNames: ReadonlyArray<string>;
    readonly sampleTableCells: ReadonlyArray<ReadonlyArray<string>>;
    readonly sampleTableColumnTypes?: ReadonlyArray<string>;
    readonly userTier?: string;
}

/** @internal */
export type TableUpdatePlanResult =
    | TableUpdatePlanSuccessResult
    | TableUpdatePlanFailureResult;

/** @internal */
export interface TableUpdatePlanSuccessResult {
    readonly success: true;
    readonly queryName: string;
    readonly planMScript: string;
    readonly transformationStepScript?: string;
    readonly transformationStepExplanation?: string;
    readonly steps: ReadonlyArray<TableUpdateStep>;
}

/** @internal */
export interface TableUpdatePlanValidateScriptFailureResult {
    readonly success: false;
    readonly errorReason: string;
}

/** @internal */
export interface TableUpdatePlanValidateScriptRequest {
    readonly hostContext: HostContext;
    readonly documentLocale: string;
    readonly queryScript: string;
    readonly userTier?: string;
}

/** @internal */
export type TableUpdatePlanValidateScriptResult =
    | TableUpdatePlanValidateScriptSuccessResult
    | TableUpdatePlanValidateScriptFailureResult;

/** @internal */
export interface TableUpdatePlanValidateScriptSuccessResult {
    readonly success: true;
    readonly validationResult: ValidateExpressionResult;
}

/** @internal */
export interface TableUpdateRenameColumnsStep {
    readonly renames: ReadonlyArray<TableUpdateColumnRename>;
    readonly kind: TableUpdateStepKind.RenameColumns;
}

/** @internal */
export interface TableUpdateReplaceColumnsStep extends TableUpdateWithDataProviderStep {
    readonly kind: TableUpdateStepKind.ReplaceColumns;
}

/** @internal */
export interface TableUpdateReplaceRowsStep extends TableUpdateWithRowIndexAndDataProviderStep {
    readonly kind: TableUpdateStepKind.ReplaceRows;
}

/** @internal */
export interface TableUpdateReplaceTableStep extends TableUpdateWithDataProviderStep {
    readonly kind: TableUpdateStepKind.ReplaceTable;
}

/** @internal */
export interface TableUpdateSelectColumnsStep {
    readonly columnNames: ReadonlyArray<string>;
    readonly kind: TableUpdateStepKind.SelectColumns;
}

/** @internal */
export interface TableUpdateSelectRowsStep extends TableUpdateWithRowIndexProviderStep {
    readonly kind: TableUpdateStepKind.SelectRows;
}

/** @internal */
export interface TableUpdateSortStep extends TableUpdateWithRowIndexProviderStep {
    readonly kind: TableUpdateStepKind.Sort;
}

/** @internal */
export interface TableUpdateSplitColumnStep extends TableUpdateWithDataProviderStep {
    readonly kind: TableUpdateStepKind.SplitColumn;
}

/** @internal */
export type TableUpdateStep =
    | TableUpdateAddColumnStep
    | TableUpdateAddRowsStep
    | TableUpdateRenameColumnsStep
    | TableUpdateReplaceColumnsStep
    | TableUpdateReplaceRowsStep
    | TableUpdateReplaceTableStep
    | TableUpdateSelectColumnsStep
    | TableUpdateSelectRowsStep
    | TableUpdateSortStep
    | TableUpdateSplitColumnStep;

/** @internal */
export const enum TableUpdateStepKind {
    SelectRows = "SelectRows",
    Sort = "Sort",
    SelectColumns = "SelectColumns",
    RenameColumns = "RenameColumns",
    AddColumn = "AddColumn",
    ReplaceColumns = "ReplaceColumns",
    ReplaceRows = "ReplaceRows",
    AddRows = "AddRows",
    SplitColumn = "SplitColumn",
    ReplaceTable = "ReplaceTable",
}

/** @internal */
export interface TableUpdateWithDataProviderStep {
    readonly dataProvider: string;
}

/** @internal */
export interface TableUpdateWithRowIndexAndDataProviderStep {
    readonly rowIndexAndDataProvider: string;
}

/** @internal */
export interface TableUpdateWithRowIndexProviderStep {
    readonly rowIndexProvider: string;
}

/** @internal */
export type TargetDefinition =
    | InvocationPatternTargetDefinition
    | ScriptTargetDefinition;

/** @internal */
export const enum TargetDefinitionKind {
    InvocationPattern = "InvocationPattern",
    Script = "Script",
}

/** @internal */
export interface TargetResourceIdentifier {
    readonly isNewResource: boolean;
    readonly navigationInfo: NavigationInfo;
}

/** @internal */
export interface TemplateMashupMetadata {
    readonly documentLocale: string;
    readonly engineVersion: string;
    readonly queriesMetadata: ReadonlyArray<TemplateQueryMetadata>;
    readonly queryGroups: ReadonlyArray<TemplateQueryGroup>;
}

/** @internal */
export interface TemplateMetadata {
    readonly name: string;
    readonly description: string;
    readonly version: string;
}

/** @internal */
export interface TemplateQueryGroup {
    readonly id: string;
    readonly name: string;
    readonly description: string;
    readonly parentId?: string;
    readonly order: number;
}

/** @internal */
export interface TemplateQueryMetadata {
    readonly queryName: string;
    readonly queryGroupId?: string;
    readonly lastKnownIsParameter?: boolean;
    readonly lastKnownResultTypeName: string;
    readonly loadEnabled: boolean;
    readonly isHidden?: boolean;
}

/** @internal */
export interface TemplateSettings {
    readonly includeDataDestinationSettings: boolean;
}

/** @internal */
export interface TemporaryQueryForIndirectionNodeAdditionalInfo {
    readonly jsonNavigationInfoArray: string;
}

/** @internal */
export interface TerseSyntaxQueryScriptGenerationDataItem {
    readonly name: string;
    readonly mScript: string;
    readonly schema: TableResultSchema;
}

/** @internal */
export interface TerseSyntaxQueryScriptGenerationRequest {
    readonly input: string;
    readonly dataItems: ReadonlyArray<TerseSyntaxQueryScriptGenerationDataItem>;
}

/** @internal */
export type TerseSyntaxQueryScriptGenerationResponse =
    | TerseSyntaxQueryScriptGenerationResponseSuccess
    | TerseSyntaxQueryScriptGenerationResponseFailure;

/** @internal */
export interface TerseSyntaxQueryScriptGenerationResponseFailure {
    readonly success: false;
    readonly error: string;
}

/** @internal */
export interface TerseSyntaxQueryScriptGenerationResponseSuccess {
    readonly success: true;
    readonly queryScript: string;
}

/** @internal */
export interface TextByExampleError {
    readonly kind: TextByExampleErrorKind;
    readonly rowIndex?: number;
    readonly columnIndex?: number;
}

/** @internal */
export const enum TextByExampleErrorKind {
    NoTextExampleFound = "NoTextExampleFound",
    NoTextProgramFound = "NoTextProgramFound",
    NoTextProgramFoundBeforeCancellation = "NoTextProgramFoundBeforeCancellation",
    NoTextProgramFoundBeforeTimeout = "NoTextProgramFoundBeforeTimeout",
}

/** @internal */
export interface TextByExampleInitializeSessionRequest {
    readonly encoding: number;
    readonly localizedStrings: TextByExampleLocalizedStrings;
    readonly mashupId: string;
    readonly numberOfSampleLines?: number;
    readonly queryName: string;
    readonly sessionId: string;
    readonly textByExampleSessionKey: string;
}

/** @internal */
export interface TextByExampleInitializeSessionResult {
    readonly inputText: string;
    readonly inputTextIsTruncated: boolean;
    readonly success: boolean;
}

/** @internal */
export interface TextByExampleLearnExamplesRequest {
    readonly columnNames: ReadonlyArray<string>;
    readonly examples: ReadonlyArray<ReadonlyArray<ByExampleCell>>;
    readonly numberOfSampleLines?: number;
    readonly sessionKey: string;
    readonly encoding: number;
}

/** @internal */
export interface TextByExampleLearnExamplesResult {
    readonly kind: TextByExampleLearnExamplesResultKind;
}

/** @internal */
export interface TextByExampleLearnExamplesResultFailed extends TextByExampleLearnExamplesResult {
    readonly error: TextByExampleError;
}

/** @internal */
export const enum TextByExampleLearnExamplesResultKind {
    Successful = "Successful",
    Failed = "Failed",
    SessionExpired = "SessionExpired",
}

/** @internal */
export interface TextByExampleLearnExamplesResultSessionExpired extends TextByExampleLearnExamplesResult {
}

/** @internal */
export interface TextByExampleLearnExamplesResultSuccess extends TextByExampleLearnExamplesResult {
    readonly calculatedGrid: ReadonlyArray<ReadonlyArray<ByExampleCell>>;
    readonly jsonQuery: string;
    readonly shouldGridUpdate: boolean;
}

/** @internal */
export interface TextByExampleLocalizedStrings {
    readonly addedColumn: string;
    readonly addedIndex: string;
    readonly extractedFirstCharacters: string;
    readonly extractedLastCharacters: string;
    readonly extractedTextAfterDelimiter: string;
    readonly extractedTextBeforeDelimiter: string;
    readonly extractedTextBetweenDelimiter: string;
    readonly extractedTextRange: string;
    readonly filledDown: string;
    readonly groupedRows: string;
    readonly removedColumns: string;
    readonly removedTopRows: string;
    readonly renameColumns: string;
    readonly selectedRows: string;
    readonly splitColumnByDelimiters: string;
    readonly splitColumnByPositions: string;
    readonly trimmedText: string;
    readonly source: string;
}

/** @internal */
export interface TokenRequest {
    readonly resource: string;
}

/** @internal */
export type TokenResponse =
    | FailedTokenResponse
    | SuccessfulTokenResponse;

/** @internal */
export type ToolConfirmation =
    | DeleteQueryConfirmation
    | DeleteStepConfirmation
    | SetQueryScriptConfirmation;

/** @internal */
export const enum ToolConfirmationType {
    DeleteQuery = "deleteQuery",
    DeleteStep = "deleteStep",
    SetQueryScript = "setQueryScript",
    Unknown = "unknown",
}

/** @internal */
export type ToolExecutionInfo = (
    | AddQueryExecutionInfo
    | DeleteQueryExecutionInfo
    | RenameQueryExecutionInfo
    | SetQueryIsSharedExecutionInfo
    | SetQueryScriptExecutionInfo
    | EditStepExecutionInfo
    | InsertStepExecutionInfo
    | DeleteStepExecutionInfo
    | SetQueryMetadataExecutionInfo
    | SetOutputDestinationExecutionInfo
    | EvaluationExecutionInfo
)
& {
    readonly evaluationResultKind?: EvaluationResultKind;
    readonly errorCode?: string;
}

/** @internal */
export interface ToolResultPayload {
    readonly type: string;
    readonly data: string;
}

/** @internal */
export interface TraceMessagesRequest {
    readonly messages: ReadonlyArray<MessageTrace>;
}

/** @internal */
export interface TriggerPublishResponse {
    readonly publishId: string;
}

/** @internal */
export interface UngroupQueryGroupResponse {
    readonly mashup: Mashup;
    readonly queriesMetadata: ReadonlyArray<QueryMetadata>;
}

/** @internal */
export interface UnknownToolConfirmation {
    readonly toolType: ToolConfirmationType.Unknown;
}

/** @internal */
export enum UnlinkConnectionErrorCode {
    InvalidUnlinkConnection = "InvalidUnlinkConnection",
    InvalidPath = "InvalidPath",
}

/** @internal */
export interface UnpivotColumnsTransform {
    readonly columnNames: ReadonlyArray<string>;
    readonly id: SuggestedTransformId.UnpivotColumns;
    readonly step: Step;
    readonly suggestionScore: number;
}

/** @internal */
export interface UnpivotOtherColumnsTransform {
    readonly id: SuggestedTransformId.UnpivotOtherColumns;
    readonly otherColumnNames: ReadonlyArray<string>;
    readonly step: Step;
    readonly suggestionScore: number;
    readonly unpivotColumnNames: ReadonlyArray<string>;
}

/** @internal */
export const enum UntrustedUriSource {
    DataSourceTag = "DataSourceTag",
    QueryEditorMessageBar = "QueryEditorMessageBar",
    QuickCreateForm = "QuickCreateForm",
    WebByExampleForm = "WebByExampleForm",
    LoadToDestinationFlow = "LoadToDestinationFlow",
    CreateDataflowFlow = "CreateDataflowFlow",
}

/** @alpha */
export const enum UpdateMethodKind {
    Append = "Append",
    Replace = "Replace",
}

/** @internal */
export interface UpdatePreviewOnlyStepsRequest {
    readonly queryName: string;
    readonly stepNamesToMark?: ReadonlyArray<string>;
    readonly stepNamesToUnmark?: ReadonlyArray<string>;
}

/** @internal */
export interface UpdateSensitivityLabelRequest {
    readonly labelId: string;
    readonly artifactObjectId: string;
}

/** @internal */
export interface UpsertDefaultOutputDestinationRequest {
    readonly defaultDestinationDefinition: DefaultOutputDestinationDefinition;
    readonly bindQueriesToDefaultOutputDestinationRequest?: BindQueriesToDefaultOutputDestinationRequest;
    readonly outputFileFormatSettings?: OutputFileFormatSettings;
}

/** @internal */
export interface UpsertDefaultOutputDestinationResult {
    readonly defaultDestinationQuery: Query;
    readonly defaultDestinationQueryMetadata: QueryMetadata;
    readonly defaultDestinationDataSourceDiscovery: MashupDataSourceDiscovery;
    readonly defaultDestinationUpdateMethod: UpdateMethod;
    readonly defaultDestinationSettings: DefaultDestinationSettings;
    readonly bindQueriesToDefaultOutputDestinationResult?: BindQueriesToDefaultOutputDestinationResult;
    readonly destinationTransformQuery?: Query;
    readonly destinationTransformQueryMetadata?: QueryMetadata;
}

/** @internal */
export interface UpsertQueryOutputFailureResult {
    readonly success: false;
}

/** @internal */
export type UpsertQueryOutputResult =
    | UpsertQueryOutputFailureResult
    | UpsertQueryOutputSuccessResult;

/** @internal */
export interface UpsertQueryOutputSuccessResult {
    readonly success: true;
    readonly newQueriesMetadata: ReadonlyArray<QueryMetadata>;
    readonly destinationQuery: Query;
    readonly destinationTransformQuery?: Query;
    readonly sourceQuery: Query;
    readonly deletedQueryNames?: ReadonlyArray<string>;
}

/** @internal */
export interface UpsertRecentItemNodesRequest {
    readonly recentItemNodes: ReadonlyArray<RecentItemNode>;
}

/** @internal */
export interface ValidateExpressionCompilationErrorResult {
    readonly kind: ValidateExpressionResultKind.CompilationError;
    readonly sourceError: SourceError;
}

/** @internal */
export interface ValidateExpressionParsingErrorResult {
    readonly kind: ValidateExpressionResultKind.ParsingError;
    readonly sourceError: SourceError;
}

/** @internal */
export interface ValidateExpressionRequest2 {
    readonly script: string;
}

/** @internal */
export type ValidateExpressionResult =
    | ValidateExpressionSuccessResult
    | ValidateExpressionCompilationErrorResult
    | ValidateExpressionParsingErrorResult
    | ValidateExpressionUnexpectedDocumentKindResult;

/** @internal */
export const enum ValidateExpressionResultKind {
    CompilationError = "CompilationError",
    ParsingError = "ParsingError",
    Success = "Success",
    UnexpectedDocumentKind = "UnexpectedDocumentKind",
}

/** @internal */
export interface ValidateExpressionSuccessResult {
    readonly kind: ValidateExpressionResultKind.Success;
    readonly actualNewScript: string;
}

/** @internal */
export interface ValidateExpressionUnexpectedDocumentKindResult {
    readonly kind: ValidateExpressionResultKind.UnexpectedDocumentKind;
}

/** @internal */
export const enum ValueKind {
    Source = "Source",
}

/** @internal */
export interface WebByExampleGetSuggestionsRequest {
    readonly mashupId: string;
    readonly sessionId: string;
    readonly url: string;
    readonly webByExampleSessionKey: string;
}

/** @internal */
export interface WebByExampleGetSuggestionsResult {
    readonly success: boolean;
    readonly errorMessage: string | null | undefined;
    readonly suggestions: ReadonlyArray<WebByExampleSuggestion>;
}

/** @internal */
export const enum WebByExampleLearnedResultKind {
    RawInput = "RawInput",
    BasicTable = "BasicTable",
    SuggestedTable = "SuggestedTable",
    ByExampleTable = "ByExampleTable",
}

/** @internal */
export interface WebByExampleLearnedSelectors {
    readonly title: string | null | undefined;
    readonly columnSelectors: ReadonlyArray<string | null | undefined>;
    readonly columnAttributes: ReadonlyArray<string | null | undefined> | null | undefined;
    readonly rowSelector: string | null | undefined;
    readonly kind?: WebByExampleLearnedResultKind | null | undefined;
}

/** @internal */
export interface WebByExampleLearnExamplesFailureResult {
    readonly success: false;
}

/** @internal */
export interface WebByExampleLearnExamplesRequest {
    readonly examples: ReadonlyArray<ReadonlyArray<ByExampleCell>>;
    readonly mashupId: string;
    readonly sessionId: string;
    readonly url: string;
    readonly webByExampleSessionKey: string;
}

/** @internal */
export type WebByExampleLearnExamplesResult =
    | WebByExampleLearnExamplesSuccessResult
    | WebByExampleLearnExamplesFailureResult;

/** @internal */
export interface WebByExampleLearnExamplesSuccessResult {
    readonly calculatedGrid: ReadonlyArray<ReadonlyArray<ByExampleCell>>;
    readonly learnedSelectors: WebByExampleLearnedSelectors;
    readonly shouldGridUpdate: boolean;
    readonly success: true;
}

/** @internal */
export interface WebByExamplePredictWebTablesRequest {
    readonly url: string;
    readonly mashupId: string;
    readonly sessionId: string;
}

/** @internal */
export interface WebByExamplePredictWebTablesResult {
    readonly errorMessage?: string | null | undefined;
    readonly results: ReadonlyArray<WebByExampleLearnedSelectors>;
}

/** @internal */
export interface WebByExampleSuggestion {
    readonly key: string;
    readonly kind: WebByExampleSuggestionKind;
    readonly text: string;
}

/** @internal */
export const enum WebByExampleSuggestionKind {
    Link = "Link",
    Text = "Text",
}

/** @public */
export interface WorkspaceVariable {
    readonly name: string;
    readonly type: string;
    readonly value: string | number | boolean;
}

